/**
 * Backoffice dictionaries.
 *
 * Bundled as TypeScript rather than fetched as JSON on purpose. The set is
 * small, the menu is built in ngOnInit, and an HTTP loader would let the first
 * paint show raw keys before the file lands. Keeping it synchronous removes
 * that window entirely, and a missing key fails at compile time instead of
 * silently rendering its own name.
 *
 * English is the reference: every key added here must exist in both objects.
 */
export const EN = {
  statusLabels: {
    inEscrow: 'In escrow',
    available: 'Available',
    noParticipants: 'No participants',
    paid: 'Paid',
    refunded: 'Refunded',
    failed: 'Failed',
    ineligible: 'Ineligible',
    eligibleForPayout: 'Eligible for payout',
    pendingEligibility: 'Pending eligibility',
    revoked: 'Revoked',
    completed: 'Completed',
    pending: 'Pending',
    expired: 'Expired',
    declined: 'Declined',
    awaitingTransfer: 'Awaiting transfer',
    approved: 'Approved',
    inTransit: 'In transit',
    canceled: 'Canceled',
    cancelled: 'Cancelled',
    rejected: 'Rejected',
    resubmitted: 'Resubmitted',
    transferred: 'Transferred',
    incoming: 'Incoming',
    waitingApproval: 'Waiting approval',
    connected: 'Connected',
    notStarted: 'Not started',
    accessRevoked: 'Access revoked',
    infoRequested: 'Info requested',
    insuranceExpired: 'Insurance expired',
    active: 'Active',
  },
  auditLogs: {
    relativeTime: {
      justNow: 'just now',
      inAMoment: 'in a moment',
      minutesAgo: '{{n}}m ago',
      inMinutes: 'in {{n}}m',
      hoursAgo: '{{n}}h ago',
      inHours: 'in {{n}}h',
      daysAgo: '{{n}}d ago',
      inDays: 'in {{n}}d',
      monthsAgo: '{{n}}mo ago',
      inMonths: 'in {{n}}mo',
      yearsAgo: '{{n}}y ago',
      inYears: 'in {{n}}y',
    },
    scopeAll: 'All scopes',
    transaction: 'Transaction',
    wallet: 'Wallet',
    platform: 'Platform',
    adminAccess: 'Admin access',
    organizerAccess: 'Organizer access (pro space)',
    system: 'System',
    actionAll: 'All actions',
    depositCharged: 'Deposit charged',
    depositRefunded: 'Deposit refunded',
    depositReleased: 'Deposit released',
    depositCreated: 'Deposit created',
    depositFailed: 'Deposit failed',
    escrowHold: 'Escrow hold',
    escrowRelease: 'Escrow release',
    payoutInitiated: 'Payout initiated',
    payoutCompleted: 'Payout completed',
    payoutPaid: 'Payout paid',
    payoutFailed: 'Payout failed',
    payoutCanceled: 'Payout cancelled',
    connectAccountCreated: 'Connect account created',
    bankVerificationChanged: 'Bank verification changed',
    transferConfirmed: 'Transfer confirmed',
    earningsRecorded: 'Earnings recorded',
    balanceSync: 'Balance sync',
    balanceAdjustment: 'Balance adjustment',
    adminRead: 'Admin read',
    organizerRead: 'Organizer read (people of an event)',
    organizerMessage: 'Organizer message (to the people of an event)',
    adminWrite: 'Admin write',
    adminDelete: 'Admin delete',
    systemCron: 'System cron',
    systemReconciliation: 'System reconciliation',
    actorAll: 'All actor types',
    user: 'User',
    admin: 'Admin',
    webhook: 'Webhook',
    colTimestamp: 'Timestamp',
    colScope: 'Scope',
    colAction: 'Action',
    colActorType: 'Actor type',
    colActor: 'Actor',
    colEntity: 'Entity',
    colAmount: 'Amount',
  },
  menu: {
    steering: 'STEERING',
    users: 'USERS',
    activities: 'ACTIVITIES',
    payments: 'PAYMENTS',
    compliance: 'COMPLIANCE',
    administration: 'ADMINISTRATION',
    gamification: 'GAMIFICATION',
    finals: 'Competitions',
    gamificationSettings: 'Settings',
    brands: 'Partner brands',
    partnerEvent: 'New partner event',
    partnerEvents: 'Partner events',

    synthesis: 'Summary',
    studio: 'Marketing studio',
    todo: 'To handle',
    userActivity: 'User activity',
    userAnalytics: 'User analytics',
    segments: 'User segments',
    fillRate: 'Activity fill rate',
    acquisition: 'Acquisition',
    activation: 'Profile quality',
    matching: 'Matching',
    journeyFunnel: 'Parcours',
    prizeDraw: 'Prize draw',
    needingHelp: 'Activities in trouble',
    crossAnalysis: 'Cross analysis',
    citiesToActivate: 'Cities to activate',
    duplicateCities: 'Duplicate cities',
    stores: 'Stores',
    heatmap: 'Heat map',
    eventRevenue: 'Event revenue',
    monetisation: 'Monetization',
    userLists: 'User lists',
    usersManagement: 'Users',
    groups: 'Groups',
    futureActivities: 'Lists of future activities',
    pastActivities: 'Lists of past activities',
    activitiesManagement: 'Activities',
    paidEventAccess: 'Paid event access',
    transactions: 'Payment transactions',
    withdrawalRequest: 'Withdrawal request',
    organizerBalances: 'Organizer’s balances',
    clientWallets: 'Client wallets',
    blockedPayouts: 'Money stuck',
    memberReports: 'Moderation',
    collusionCases: 'Pairs set aside',
    groupReview: 'Group review',
    earningsStatement: 'Earnings statement',
    commissionsRate: 'Commissions rate',
    termsContent: 'Terms & conditions content',
    termsAcceptances: 'Terms & conditions acceptances',
    auditLogs: 'Audit logs',
    deletedItems: 'Deleted items',
    accessManagement: 'Access management',
  },
  accessMgmt: {
    title: 'Back-office access',
    subtitle: 'Who can reach the back office, and at what level.',
    grantTitle: 'Grant or change access',
    emailPlaceholder: 'Account email',
    levelAdmin: 'Full access',
    levelReadonly: 'Read-only',
    grantCta: 'Grant',
    granting: 'Granting…',
    grantHint:
      'The account must already exist in the app. Full access can do everything; read-only can see every screen but change nothing.',
    granted: 'Access updated.',
    listTitle: 'Current access',
    loadFailed: 'Could not load the access list.',
    colUser: 'Account',
    colLevel: 'Level',
    colActions: 'Actions',
    noName: 'No name',
    level_admin: 'Full access',
    level_readonly: 'Read-only',
    level_superadmin: 'Superadmin',
    you: '(you)',
    managedByEnv: 'Managed by the environment',
    makeAdmin: 'Full access',
    makeReadonly: 'Read-only',
    revoke: 'Revoke',
    empty: 'No back-office account yet.',
    errorNotFound: 'No account found for that email.',
    errorSuperadmin: 'A superadmin is managed by the environment and cannot be changed here.',
    errorSelf: 'You cannot revoke your own access.',
    errorGeneric: 'The change could not be applied.',
  },
  login: {
    title: 'Welcome!',
    subtitle: 'Sign in to continue',
    email: 'Email',
    emailPlaceholder: 'Email address',
    password: 'Password',
    passwordPlaceholder: 'Password',
    submit: 'Sign in',
    googleAlt: 'Sign in with Google',
    logoAlt: 'Smatchy logo',
  },
  topbar: {
    calendar: 'Calendar',
    profile: 'Profile',
    language: 'Language',
    switchToFrench: 'Switch to French',
    synthese: 'Synthesis',
    todo: 'To handle',
    studio: 'Studio',
    switchToEnglish: 'Switch to English',
  },
  profileMenu: {
    open: 'Open the profile menu',
    admin: 'Administrator',
    noEmail: 'Email unknown',
    changePassword: 'Change my password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm the new password',
    pwdTooShort: 'At least 6 characters.',
    pwdMismatch: 'The two passwords do not match.',
    cancel: 'Cancel',
    save: 'Update',
    saving: 'Updating…',
    providerNote: 'You are signed in with an external account (Google or Facebook); manage your password there.',
    pwdSuccess: 'Password updated.',
    pwdErrorWrong: 'The current password is incorrect.',
    pwdErrorWeak: 'The new password is too weak.',
    pwdErrorTooMany: 'Too many attempts, please try again later.',
    pwdErrorGeneric: 'The password could not be changed.',
    logout: 'Sign out',
  },
  todo: {
    attentionGo: 'Handle',
    vatStatusMissing: {
      title: '{{count}} pro(s) without a VAT status',
      hint: 'Their withdrawals wait until they declare it in the app (Profile, My wallet). Amount waiting for each:',
      asked: 'asked in the app',
      reminded: 'reminded',
    },
    attention: {
      pro_requests: '{{count}} paid-activity access request(s) waiting for your decision',
      payouts: '{{count}} withdrawal request(s) to approve or decline',
      member_reports: '{{count}} member(s) reported, waiting to be looked at',
      groups_to_review: '{{count}} group(s) waiting for brand-safety review',
    },

    liveBadge: 'live figure',
    live: {
      paymentAlerts: {
        title: 'Handle the waiting money',
        finding:
          '{{pending}} withdrawal request(s) waiting and {{withoutBank}} positive balance(s) without a verified bank account, right now.',
        action:
          'Open the balances screen and treat each line; money waiting erodes organiser trust faster than anything.',
      },
      sportMissing: {
        title: 'Reach the {{count}} profiles missing only their sport',
        finding:
          '{{count}} profiles filled everything but the sport, today. One field separates them from being matchable.',
        action: 'Run a "pick your sport" push campaign. Potential gain: +{{gain}}% matchable profiles.',
      },
      needingHelp: {
        title: 'Rescue the {{count}} activities where the organiser is alone',
        finding: '{{count}} upcoming activities have no participant yet, as of this load.',
        action: 'Open the struggling activities screen and treat the diagnosis of each one.',
      },
      organiserCandidates: {
        title: 'Talk to the {{count}} group leaders who never organised',
        finding:
          '{{count}} people run a group today and never created a single activity; running a group multiplies the odds of organising by 19.7.',
        action: 'Open the segments screen and contact the candidates list, strongest first.',
      },
      noOfferCities: {
        title: 'Create offer in the {{count}} cities without any',
        finding:
          '{{count}} cities hold members and nearly no activity; the biggest is {{city}} with {{profiles}} profiles.',
        action: 'Open cities to activate and recruit or seed organisers there.',
      },
      duplicateCities: {
        title: 'Clean the duplicate city spellings',
        finding: 'An exact search currently misses {{missed}} profiles because their city is spelled differently.',
        action: 'Open duplicate cities and run the merge suggestions.',
      },
    },
    title: 'To handle',
    subtitle: 'Recommended actions, measured against production data',
    openCount: 'open',
    finding: 'What the numbers say',
    action: 'What to do',
    done: 'Done',
    openInAsana: 'Open in Asana',
    archivedNote: 'action(s) ticked earlier have been archived',
    empty: 'Nothing left to handle here',
    loadFailed: 'Could not load the handled actions',
    category: {
      security: 'Security',
      marketing: 'Marketing',
      product: 'Product',
      finance: 'Finance',
      data: 'Data',
      compliance: 'Compliance',
    },
    priority: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
    },
  },
  heatmap: {
    title: 'Heat map',
    subtitle: 'Where the members are, where the activities are, and where the two never meet',
    profilesWithoutActivity: 'Members with nothing held nearby',
    activitiesWithoutProfile: 'Activities held with no member nearby',
    profilesPlaced: 'Members placed',
    activitiesPlaced: 'Activities placed',
    layer: 'Layer',
    layerGap: 'The gap',
    layerProfiles: 'Members',
    layerActivities: 'Activities',
    members: 'Members',
    activities: 'Activities',
    cell: 'Cell',
    city: 'Town',
    country: 'Country',
    recommendSports: 'Sports to recommend',
    noDeclaredSports: 'no declared sport there yet',
    cityPending: 'Looking up…',
    worstGapsTitle: 'Where the most members have nothing to join',
    svgLabel: 'Density of members and activities, one dot per cell',
    computedAt: 'Computed on {{date}}. Recomputed once a night, so these figures can be up to a day old.',
    note:
      'One dot per cell of about {{km}} km. Area carries the count, not width. The gap layer shows only the ' +
      'cells where members live and no activity has ever been held, which is the case worth acting on. ' +
      'Positions never leave the database: the map is drawn from counts per cell.',
    outside: '{{count}} active cells sit outside the initial view: they are on the map, pan to reach them.',
    loadFailed: 'Could not load the map',
  },
  stores: {
    title: 'Stores',
    subtitle: 'What the App Store and Google Play report about the app',
    downloadsTitle: 'Installs',
    reviewsTitle: 'Reviews and ratings',
    iosInstalls: 'iOS installs',
    androidInstalls: 'Android installs',
    activeDevices: 'Android devices carrying the app',
    daysReported: 'Days reported',
    average: 'Average rating',
    reviewCount: 'Reviews',
    negative: 'Three stars or less',
    territories: 'Countries',
    androidPending:
      'Google Play is not connected on this environment: the PLAY_* variables are not set. Once they are, ' +
      'Android installs, uninstalls and active devices appear here.',
    appleNotConfigured: 'The App Store credentials are not set on this environment, so there is nothing to read.',
    downloadsNote:
      'First installs only, so the figure means acquisition rather than activity: updates and reinstalls are ' +
      'excluded. Apple publishes with a day or two of delay, and a missing day means no report yet rather ' +
      'than nobody installing.',
    averageNote:
      'The average is computed from the reviews listed here, not from the public star rating, which also ' +
      'counts ratings left without a review. The two figures do not have to agree.',
    androidRating: 'Google Play rating',
    androidRatingCount: 'Google Play ratings',
    androidRatingManual:
      'Entered by hand, read from the Play listing on {{asOf}}. The bulk export’s figure contradicts the ' +
      'live listing and the Play API is not reachable yet, so this one is kept current manually.',
    androidRatingManualNoDate:
      'Entered by hand from the Play listing. The bulk export’s figure contradicts the live listing and the ' +
      'Play API is not reachable yet, so this one is kept current manually.',
    noDays: 'No day in the range has a published report yet',
    noReviews: 'Nobody has reviewed the app yet',
    acquisitionTitle: 'Where installs come from',
    acquisitionPending:
      'The detailed reports exist but have not produced data yet: they only started on 14 August 2026. This is ' +
      'not zero installs, it is Apple not having published yet.',
    newUsers: 'New users',
    redownloads: 'Redownloads and updates',
    deletions: 'Uninstalls',
    netInstalls: 'Net',
    bySource: 'By source',
    byVersion: 'By version',
    byDevice: 'By device',
    byTerritory: 'By country',
    acquisitionNote:
      'New users counts first-time downloads only: counting redownloads and updates would turn every release ' +
      'into a fake acquisition spike. Uninstalls are shown apart rather than netted away, because gaining ten ' +
      'and losing six is not the same product as gaining four.',
    acquisitionFailed: 'Could not load the acquisition detail',
    listingTitle: 'Play Store listing',
    listingVisitors: 'Listing visitors',
    listingAcquisitions: 'Installed from it',
    listingConversion: 'Conversion',
    listingNote:
      'Over the {{days}} days Play has published. The conversion is recomputed from visitors and installs, ' +
      'never averaged from per-day rates. Apple has no equivalent until its engagement reports start producing.',
    crashNote: '{{count}} crash(es) in the last six months of published files, the latest on {{day}}.',
    noCrashNote: 'No crash in the last six months of published files.',
    listingFailed: 'Could not load the listing figures',
    downloadsFailed: 'Could not load the installs',
    reviewsFailed: 'Could not load the reviews',
    minimumTitle: 'Minimum app version',
    minimumIntro:
      'An older version shows a screen that requires the update before the app opens. At 0.0.0, nobody is blocked. If our server does not answer, the app opens as usual.',
    minimumCurrent: { ios: 'iOS, current minimum', android: 'Android, current minimum' },
    minimumPlaceholder: 'e.g. 2.2.8',
    minimumSave: 'Save',
    minimumWarning:
      'Raise a value only once that version is live on the store concerned: otherwise the screen sends people to an update that does not exist yet.',
    minimumInvalid: 'Write the version as three numbers, for example 2.2.8.',
    minimumFailed: 'The minimum version could not be read.',
    minimumSaveFailed: 'Saving failed, nothing changed.',
    platform: { ios: 'iOS', android: 'Android' },
    minimumConfirmTitle: 'Block {{platform}} versions older than {{version}}?',
    minimumConfirmBody: {
      ios: 'People on an older version will no longer be able to open the app without updating it from the App Store.',
      android:
        'People on an older version will no longer be able to open the app without updating it from Google Play.',
    },
    minimumUnblockTitle: 'Stop blocking {{platform}} versions?',
    minimumUnblockBody: 'Every version will be able to open the app again.',
    minimumCancel: 'Cancel',
    minimumConfirm: 'Confirm',
  },
  crossAnalysis: {
    title: 'Cross analysis',
    subtitle: 'Which sport works in which city at which duration, instead of one national average',
    groupBy: 'Group by',
    all: 'All',
    dimension: {
      sport: 'Sport',
      city: 'City',
      duration: 'Duration',
    },
    duration: {
      upTo45: '45 min or less',
      h1: '1 h',
      h1x5: '1 h 30',
      h2: '2 h',
      h3: '3 h',
      over3: 'More than 3 h',
      unknown: 'Not stated',
    },
    activities: 'Activities',
    joined: 'Joined',
    joinedShare: 'Share joined',
    other: 'Other, groups too small to read alone',
    counted: '{{filtered}} activities in this slice, of {{total}} in total',
    note:
      'The rate is recomputed for the grouping on screen, never averaged from stored rates. Groups under five ' +
      'activities fold into one line rather than vanishing, so the visible total always matches the header. ' +
      'Green joins at 40% or more, orange from 20%, red below.',
    loadFailed: 'Could not load the cross analysis',
  },
  citiesToActivate: {
    title: 'Cities to activate',
    subtitle: 'Every city worth acting on, and what kind of action it calls for',
    status: {
      replicate: 'Replicate',
      dormant: 'Dormant',
      noOffer: 'No offer',
      watch: 'Watch',
    },
    hint: {
      replicate: 'It works here: copy the recipe elsewhere',
      dormant: 'People are there, activity is not: needs organisers',
      noOffer: 'Members with nearly nothing to join: needs activities',
      watch: 'Nothing calls for action yet',
    },
    city: 'City',
    profiles: 'Members',
    withSport: 'With a sport',
    activities: 'Activities',
    joined: 'Joined',
    upcoming: 'Upcoming',
    statusColumn: 'Status',
    note:
      'Spellings are folded with the same rule as the city filter, and an activity counts for the city its ' +
      'address names. Cities under {{floor}} members are left out, {{folded}} of them: below that every rate ' +
      'is noise. Click a card to narrow the table to that status.',
    loadFailed: 'Could not load the cities',
  },
  duplicateCities: {
    title: 'Duplicate cities',
    subtitle: 'The same place saved under several spellings',
    distinctSpellings: 'Values written',
    distinctPlaces: 'Actual places',
    profilesMissed: 'Profiles an exact search misses',
    missedShare: 'Of everyone with a city',
    place: 'Place',
    spellings: 'Spellings',
    profiles: 'Profiles',
    missed: 'Missed',
    showSpellings: 'See the spellings',
    hideSpellings: 'Hide',
    showAll: 'Show the {{count}} others',
    partOf: 'part of {{city}}?',
    partOfHint:
      'Looks like an arrondissement of a city that also exists here. Offered, not applied: whether the two are ' +
      'the same place is a decision about what a city means here.',
    note:
      'City is typed by hand and never checked, so one place arrives many ways. The kept spelling is simply the ' +
      'most common one. Missed counts the profiles an exact search for their own city would not return, which ' +
      'is what says whether cleaning up is worth it. Nothing on this screen changes a profile.',
    none: 'Every place is written one way',
    loadFailed: 'Could not load the cities',
  },
  acquisition: {
    title: 'Acquisition',
    subtitle: 'Sign-ups by month, and what each cohort became',
    totalSignups: 'Signed up, all time',
    totalActivated: 'Ever acted',
    totalShare: 'Of the whole base',
    totalWithin7dShare: 'Acted within 7 days',
    note:
      'The grey bar is the cohort, the coloured part is who ever created or joined an activity, and the ' +
      'percentage is their ratio. Activation is measured to date, so recent months look worse than they will ' +
      'end up: a cohort keeps maturing. On production the two lines disagree loudly: sign-ups halved over the ' +
      'year while activation doubled, so fewer but better people are arriving.',
    within7dNote:
      'The "within 7 days" share counts members who created or joined a first activity in their first week, the ' +
      'window the onboarding nudge uses. Unlike the lifetime share it does not keep maturing, so cohorts compare ' +
      'on equal footing. Joining keeps no timestamp, so a join is dated by the activity it is read against, the ' +
      'same approximation the nudge accepts; hover a month for its figure.',
    sourcesNote: 'Where installs come from, store by store, lives on the',
    sourcesLink: 'Stores screen',
    loadFailed: 'Could not load the acquisition',
  },
  activation: {
    title: 'Profile quality',
    subtitle: 'What the profiles actually contain, and who is one step from matchable',
    onlySportMissing: 'One sport away from matchable',
    matchable: 'Matchable today',
    onlyPositionMissing: 'Position missing',
    bothMissing: 'Sport and position missing',
    blockersNote:
      'Matching needs a declared sport and a position. The first card is the shortest win in the whole ' +
      'funnel: these people already shared where they are, one question in the app makes them matchable.',
    fieldsTitle: 'Field by field',
    field: {
      firstName: 'First name',
      avatar: 'Photo',
      city: 'City',
      position: 'Position',
      dateOfBirth: 'Date of birth',
      sex: 'Gender',
      sport: 'Declared sport',
      push: 'Push token',
    },
    dead: 'never filled by anyone',
    fieldsNote:
      'Counted across all {{total}} living profiles. A field nobody has ever filled is a different statement ' +
      'than a badly filled one: it says the product asks a question nobody answers, or never really asks it.',
    loadFailed: 'Could not load the profile quality',
  },
  prizeDraw: {
    title: 'Prize draw',
    subtitle: 'Draw a winner among 100%-complete profiles, reproducibly and on the record.',
    eligibleLabel: 'Eligible profiles (100% complete)',
    cityLabel: 'Restrict to a city (optional)',
    cityPlaceholder: 'e.g. Annecy',
    refresh: 'Refresh count',
    formTitle: 'Run a draw',
    seedLabel: 'Seed',
    seedHelp: 'Recorded with the draw so it can be replayed. Generate one or type your own.',
    generateSeed: 'Generate',
    winnerCountLabel: 'Winners to draw',
    labelLabel: 'Draw label (optional)',
    labelPlaceholder: 'e.g. October profile campaign',
    run: 'Run the draw',
    running: 'Drawing…',
    noEligible: 'No profile is eligible for the draw yet.',
    resultTitle: 'Winner',
    resultTitlePlural: 'Winners',
    main: 'Main winner',
    suppleant: 'Backup',
    pool: 'Pool',
    listHash: 'List fingerprint',
    drawnAt: 'Drawn on',
    historyTitle: 'Past draws',
    historyEmpty: 'No draw yet.',
    loadFailed: 'Could not load. Please try again.',
  },
  activitiesManagement: {
    cancellationsTitle: 'Why these activities were cancelled',
    cancellationsEmpty: 'No cancellation over this period.',
    cancellationsWithAWord: 'Cancellations carrying a word for the people registered',
    cancellationsTotal: 'Cancellations',
    reasonColumn: 'Reason',
    reasonWeather: 'Weather',
    reasonNotEnoughRegistrations: 'Not enough registrations',
    reasonOrganiserUnavailable: 'Organiser unavailable',
    reasonVenueUnavailable: 'Venue unavailable',
    reasonOther: 'Other',
    reasonNotAsked: 'No reason asked',
    reasonNotAskedHelp: 'Cancelled before the question existed, or with nobody registered.',
    title: 'Activities management',
    subtitle: 'Create, fix, cancel: the intervention desk for activities.',
    searchPlaceholder: 'Search an activity or a group',
    filters: 'Filters',
    exportCsv: 'Export CSV',
    exporting: 'Exporting',
    clearFilter: 'Clear filters',
    dateRange: 'Date range',
    statusLabel: 'Activity status',
    sportLabel: 'Sport',
    addressContains: 'Address contains',
    addressPlaceholder: 'City, venue, country',
    allStatuses: 'All statuses',
    allSports: 'All sports',
    chipUpcoming: 'Upcoming',
    chipThisWeek: 'This week',
    chipWithoutPartners: 'Without partner',
    chipFull: 'Full',
    chipWeekly: 'Weekly',
    partnerSeats: 'Partner seats taken',
    uniqueParticipants: 'unique participants',
    upcomingDesc: 'Scheduled or in progress',
    withoutPartnersTitle: 'Upcoming without partner',
    seeThem: 'See these activities',
    thisMonth: 'this month',
    fullMonths: 'full month vs previous',
    vsLastMonth: 'vs last month',
    colActivity: 'ACTIVITIES',
    colGroup: 'GROUP',
    colDate: 'DATE & TIME',
    colStatus: 'STATUS',
    colFill: 'FILL',
    colActions: 'ACTIONS',
    by: 'by',
    weekly: 'Weekly',
    empty: 'No activities match your search or filters',
    editTooltip: 'Edit activity',
    pageReport: 'Showing {first} to {last} of {totalRecords} results',
    fillNote: 'The fill counts partner seats: a 4-seat activity whose organiser holds one offers 3.',
  },
  paidEvents: {
    pageTitle: 'Paid event access',
    filters: 'Filters',
    clearFilter: 'Clear filter',
    searchOrganizer: 'Search organizer',
    searchOrganizerName: 'Search organizer name',
    searchParticipantName: 'Search participant name',
    submittedDateRange: 'Submitted date range',
    grantDateRange: 'Grant date range',
    dateRange: 'Date range',
    roleType: 'Role type',
    allRoleTypes: 'All role types',
    status: 'Status',
    allStatuses: 'All statuses',
    statusChip: {
      PENDING: 'Pending',
      RESUBMITTED: 'Resubmitted',
      REQUESTED_INFO: 'Requested info',
      INSURANCE_EXPIRED: 'Insurance expired',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
      APPROVED: 'Active',
      REVOKED: 'Revoked',
    },
    roleLabels: {
      PERSONAL_TRAINER_COACH: 'Personal trainer / coach',
      PROFESSIONAL_ATHLETE: 'Professional athlete',
      OTHER: 'Other',
      ASSOCIATION_CLUB: 'Association / club',
      USER: 'User',
    },
    pageReport: 'Showing {first} to {last} of {totalRecords} results',
    noResults: 'No results found',
    colOrganizer: 'Organizer',
    colSubmittedOn: 'Submitted On',
    colRoleType: 'Role Type',
    colRequestNote: 'Request Note',
    colSubmissionCount: 'Submission count',
    colStatus: 'Status',
    approveTitle: 'Organizers with paid event permission',
    popupRejectTitle: 'Reject request?',
    popupRejectIntro:
      'You are about to reject this request.<br />Please provide a reason for this action. The user will be notified.',
    reasonRejection: 'Reason for rejection',
    rejectPlaceholder: 'Enter rejection reason here...',
    reject: 'Reject',
    popupApproveTitle: 'Approve request?',
    approveOtherWarning:
      'This user submitted the request with the role set to “Other”. You must define an official role tag before approving.',
    currentRate: 'Current rate',
    globalDefault: 'Global default',
    assignRoleTag: 'Assign official role tag',
    roleTagHint: "Admin-defined role tag used in the user's profile",
    popupApproveIntro:
      'You are about to approve this request.<br />The user will become a <span class="text-[#FCA13B]">Pro Organizer</span> and gain permission to create paid activities.',
    approve: 'Approve',
    legalSetupTitle: 'Legal setup required before approval?',
    legalSetupBody:
      'You cannot approve this organizer because the required Legal Policies have not been published.<br />To approve Pro Organizers and enable paid activities, you must first publish:<ul><li>Pro Organizer Terms & Conditions</li><li>Participant Terms / GCS Policy</li></ul>Without these policies, organizer approval would result in invalid legal consent.',
    goToLegal: 'Go to Legal setup',
    additionalProofTitle: 'Request additional proof',
    messageToOrganizer: 'Message to organizer',
    enterText: 'Enter Text Here',
    sendRequest: 'Send request',
    popupRevokeTitle: 'Revoke access?',
    revokeWarning:
      'This user will lose the right to create paid activities. Existing paid activities will remain unaffected, but no new paid activities can be created.',
    popupRevokeIntro:
      'You are about to revoke this request.<br />Please provide a reason for this action. The user will be notified.',
    reasonRevoked: 'Reason for revoked access',
    nameLabel: 'Name',
    statusApproved: 'Submission approved by Smatchy',
    statusRevoked: 'Submission revoked by Smatchy',
    statusRequestedInfo: 'Re-submission requested by Smatchy',
    statusRejected: 'Submission Rejected by Smatchy',
    statusCancelled: 'Submission Cancelled by user',
    statusRequested: 'Submission requested by user',
    statusInsuranceExpired: "User's insurance has expired",
    colGrantedOn: 'Granted On',
    colGrantedBy: 'Granted By',
    colActions: 'Actions',
    revoke: 'Revoke',
    viewHistory: 'View history',
  },
  cgvAcceptance: {
    colProOrganizer: 'Pro Organizer',
    colUsers: 'Users',
    colCommissionRate: 'Commission rate',
    colCgvVersion: 'Cgv version',
    colGcsVersion: 'GCS policy version',
    colAcceptanceStatus: 'Acceptance status',
    colLastUpdated: 'Last updated',
    colActions: 'Actions',
    pendingAcceptance: 'Pending acceptance',
    accepted: 'Accepted',
    acceptedOn: 'Accepted on',
    changedOn: 'Changed on',
    by: 'by',
    customRate: 'Custom rate',
    viewHistory: 'View history',
    noData: 'No Data',
    kpiOrganizersTitle: 'Pro Organizers (Active)',
    kpiOrganizersSub: 'Organizers eligible for paid events',
    kpiAcceptedSub: 'Accepted latest organizer terms',
    kpiPendingSub: 'Paid event creation is blocked',
    kpiTotalUsers: 'Total users',
    kpiTotalUsersSub: 'All users in system',
    pageTitle: 'Terms & conditions acceptances',
    tabOrganizers: 'Pro Organizers',
    tabUsers: 'All Smatchy Users',
    historyTitle: 'Acceptance History',
    acceptedTermsCommission: 'Accepted Terms & Commission',
    acceptedTerms: 'Accepted Terms & Conditions',
    dateTime: 'Date & Time',
    organizerTermsDoc: 'Pro Organizer Terms & Conditions',
    gcsDoc: 'General Conditions of Sale',
    close: 'Close',
    kpiAcceptedRefundSub: 'Accepted latest refund policy',
    kpiPendingCheckoutSub: 'Required before next checkout',
  },
  walletPages: {
    title: 'Client wallet',
    subtitle: "See a member's in-app balance and adjust it, with a reason.",
    userIdLabel: 'Member ID',
    userIdPlaceholder: "Paste the member's id",
    load: 'Load',
    balance: 'Balance',
    totalCredited: 'Total credited',
    totalSpent: 'Total spent',
    movements: 'Movements',
    noMovements: 'No movement yet.',
    colDate: 'Date',
    colDirection: 'Direction',
    colAmount: 'Amount',
    colBalanceAfter: 'Balance after',
    colReason: 'Reason',
    credit: 'Credit',
    debit: 'Debit',
    adjustTitle: 'Adjust the balance',
    direction: 'Direction',
    amountLabel: 'Amount (€)',
    amountHint: 'Positive, 1000 € maximum per adjustment.',
    noteLabel: 'Reason (required)',
    notePlaceholder: 'Why this adjustment (10 characters minimum)',
    apply: 'Apply',
    confirmTitle: 'Confirm the adjustment',
    confirmCredit: "You are about to credit {{amount}} € to this member's wallet.",
    confirmDebit: "You are about to debit {{amount}} € from this member's wallet.",
    cancel: 'Cancel',
    adjusted: 'Done. The new balance is {{balance}} €.',
    loadError: 'Could not load this wallet.',
    adjustError: 'The adjustment did not go through.',
    invalidUserId: 'Enter a member id first.',
    invalidAmount: 'Enter an amount between 0.01 and 1000 €.',
    invalidNote: 'The reason must be at least 10 characters.',
  },
  legalPages: {
    siteTextInForce: 'The text in force, as the application holds it',
    writtenOnTheSite: 'Written on the site, not here',
    writtenOnTheSiteExplained:
      'A legal text is edited in Strapi. Our API pulls it down and the application shows what came down, which is what you are reading here.',
    loading: 'Loading…',
    nothingSyncedYet: 'Nothing has been synchronised for this text yet.',
    pageTitle: 'Terms & conditions content',
    proTab: 'Pro Organizer Policies',
    siteTab: 'Site legal texts',
    privacyTab: 'Privacy policy',
    cguTab: 'Terms of use',
    gcsTab: 'Conditions of sale',
    legalNoticesTab: 'Legal notices',
    editSiteText: 'Edit the text the site publishes',
    publishToSite: 'Publish to the site',
    publishConfirm: 'This replaces the text on the website and in the application, in both languages, for everybody.',
    publishingNotConfigured: 'Publishing is not configured: the API has no site token.',
    charteRestored: 'The look was left behind, the words were not',
    charteRestoredExplained:
      'The site and the application each style these texts with their own charte, so the presentation was removed before publishing: {{removed}}. The headings, paragraphs and lists are unchanged.',
    onlyOneLanguagePublished: 'Only one language was published',
    onlyOneLanguageExplained:
      'The site now shows the new text in {{landed}} and the old one in the other language. Publish again to finish, or put the first one back.',
    nothingWasPublished: 'Nothing was published. The site still shows what it showed.',
    editTerms: 'Edit Terms & Conditions Content',
    languageWarning: 'Make sure to update all language versions before publishing',
    currentVersion: 'Current Version:',
    appliesTo: 'Applies to:',
    proOrganizers: 'Pro Organizers',
    lastUpdatedOn: 'Last updated on',
    byWord: 'by',
    editorPlaceholder: 'Please add Terms and Conditions...',
    publishNewVersion: 'Publish new version',
    publishing: 'Publishing...',
    versionHistory: 'Version History',
    colVersion: 'Version',
    colPublishedDate: 'Published date',
    colPublishedBy: 'Published by',
    colActions: 'Actions',
    view: 'View',
    publishOrganizerTitle: 'Publish new Pro Organizer Terms?',
    confirmOrganizerBody:
      'Publishing this version will require all <span class="text-[#FCA13B]">Pro Organizer</span> to review and accept the updated Terms & Conditions again.<br />Organizers who do not accept the new version will not be able to create or manage paid events until acceptance.<br />This action creates a new legal version and cannot be undone.',
    confirmParticipantBody:
      'Publishing this version will require all <span class="text-[#479E9C]">Smatchy users</span> to accept the updated policy again.<br />The new terms will apply to all future registrations and checkouts after publication.<br />This action creates a new legal version and cannot be undone.',
    cancel: 'Cancel',
  },
  dashboard: {
    futureActivities: 'Upcoming activities',
    registeredUsers: 'Registered users',
    groups: 'Groups',
    pastActivities: 'Past activities',
    activeUsers: 'Active users',
    engagedDesc: 'Created or joined at least one activity in the last {{days}} days',
    during: 'During the last',
    days: 'days',
    pickDuration: 'Pick a duration',
    colName: 'Name',
    colActivitiesJoined: 'Activities joined',
    colLastActivity: 'Last activity joined',
  },
  dialogs: {
    cancel: 'Cancel',
    close: 'Close',
    understand: 'I understand and want to proceed',
    cannotBeUndone: 'This action cannot be undone.',
    deletedItems: 'Deleted items',
    cancelActivityTitle: 'Cancel activity?',
    cancelActivityQuestion: 'Are you sure you want to cancel this activity:',
    cancelActivityBtn: 'Cancel activity',
    paidCancelBody:
      'This is a paid activity. Cancelling it will automatically cancel all registrations and process <span>full refunds</span> for participants.',
    targets: { group: 'group', subgroup: 'subgroup', activity: 'activity' },
    targetsDefinite: { group: 'the group', subgroup: 'the subgroup', activity: 'the activity' },
    deleteTitle: 'Delete {{target}}?',
    deleteQuestion: 'Are you sure you want to delete {{target}}:',
    deletingAuto: 'Deleting this {{target}} will automatically:',
    liCancelIncoming:
      'Cancel any incoming activities in this group and its nested subgroups, with full refunds for paid activity registrations, where applicable',
    liDeleteActivities: 'Delete all activities in this {{target}} and its nested subgroups',
    liDeleteSubgroups: 'Delete all nested subgroups',
    movedTo: 'The {{target}} and its nested subgroups will be moved to',
    activityMovedTo: 'This activity will be moved to',
    deleteGroupBtn: 'Delete group',
    deleteAccountTitle: 'Delete account?',
    deleteAccountQuestion: 'Are you sure you want to delete the account',
    accountAuto: 'Deleting this account will automatically:',
    liCancelIncomingUser:
      'Cancel any incoming activities created by this account with full refunds for paid activity registrations, where applicable',
    liRemoveAccess: "Remove the user's access to the mobile app permanently",
    accountMovedTo: 'The account will be moved to',
    deleteAccountBtn: 'Delete account',
    creditOnDeletionTitle: "The member's credit",
    creditOnDeletionToCard: 'Refunded to their card',
    creditOnDeletionForfeited: 'Bonus lost',
    creditOnDeletionNone: 'This member has no credit: nothing to refund, nothing lost.',
    creditOnDeletionUnknown:
      'Amounts unavailable: the deletion still settles the credit, but they cannot be shown here.',
    blockedTitle: 'Unable to delete account',
    suspendIntro:
      'You can suspend this account now: the member can no longer use the app and other members no longer see them. The account is deleted automatically as soon as nothing blocks it any more.',
    suspendReasonLabel: 'Reason',
    suspendReasonMinor: 'The member is a minor',
    suspendReasonOther: 'Other',
    suspendBtn: 'Suspend and delete as soon as possible',
    alreadySuspended:
      'This account is already suspended. It will be deleted automatically as soon as nothing blocks it any more.',
    refundIntro: 'Refunds depend on the cancellation timing:',
    refundGt48: '>48h before event starting:',
    refundLe48: '≤48h before event starting:',
    refund24: '24h before event starting:',
    refundFull: 'Full refund',
    refund70: '>70% refund',
    refundNone: 'No refund',
  },
  commissionDialogs: {
    historyTitle: 'Commission Changed History',
    updatedGlobal: 'Updated Global Commission Rate',
    dateTime: 'Date & Time:',
    rateLabel: 'Commission rate:',
    nameLabel: 'Name:',
    legallyBinding: 'Legally binding and irreversible action',
    cannotUndo:
      'Once published, this change cannot be undone without a new legal amendment process. Please review the impact carefully.',
    willReaccept: 'The affected Pro Organizers will be required to re-accept the updated Terms & Conditions.',
    acknowledge:
      'I acknowledge that this action will trigger legal notifications to all affected users and update the global platform commission rate.',
    viewAffected: 'View all {{count}} affected Pro Organizers',
    publishing: 'Publishing…',
    publishChanges: 'Publish changes',
    toastUpdatedTitle: 'Commission updated',
    toastSuccess: 'The global commission has been changed successfully.',
    toastError: 'The global commission change failed.',
    organizersAffected: 'Pro Organizers Affected',
    newGlobalRate: 'New global rate',
    currentRate: 'Current Rate',
    newRate: 'New Rate',
    existingUnaffected: 'Existing events will not be affected until they are renewed',
    selectOrganizer: 'Please select an organizer',
    ratePercent: 'Commission Rate (%)',
    ratePlaceholder: 'Commission rate',
    enterRate: 'Please enter commission rate',
    overridesGlobal: 'Overrides Global Settings',
    appliesFuture: 'This rate will apply to all future events created by',
    setGlobalTitle: 'Set new Global Commission Rate',
    reviewImpact: 'Review impact',
    organizerLabel: 'Organizer',
    currentRateCaps: 'CURRENT RATE',
    globalDefault: 'Global Default',
    backToBalances: 'Back to balances',
    organizerNotFound: 'Organizer not found',
    organizerRemoved: 'This organizer may have been removed or the link is invalid.',
    somethingWrong: 'Something went wrong',
    couldNotLoad: "We couldn't load this organizer.",
    goodToKnow: 'Good to know',
    eventEarnings: 'Event Earnings',
    withdrawalRequest: 'Withdrawal Request',
    noPreview: 'No preview.',
    noVersionFound: 'No version found',
  },
  financePages: {
    auditTitle: 'Audit logs',
    clearFilters: 'Clear filters',
    dateRange: 'Date range',
    scope: 'Scope',
    action: 'Action',
    actorType: 'Actor type',
    allScopes: 'All scopes',
    allActions: 'All actions',
    allActorTypes: 'All actor types',
    copyId: 'Copy ID',
    balancesTitle: "Organizer's balances",
    payoutTitle: 'Withdrawal request',
    payoutDetailTitle: 'Withdrawal request details',
    decline: 'Decline',
    approve: 'Approve',
    transactionsTitle: 'Payment transactions',
    amountFrom: 'Amount from (EUR)',
    amountTo: 'Amount to (EUR)',
    noMinimum: 'No minimum',
    noMaximum: 'No maximum',
    eventRevenueTitle: 'Event Revenue',
    monTitle: 'Monetization',
    monSubtitle: 'Monetization health, alongside the revenue totals.',
    monLimitedData:
      'Limited data so far: these ratios are computed on very few transactions and will firm up as volume grows.',
    monCompletionHint:
      'Completion rate is derived from transaction statuses (completed or refunded ÷ initiated) while dedicated API instrumentation is pending; the five other tiles come from the revenue aggregate.',
    monCompletionRate: 'Completion rate',
    monRefundRate: 'Refund rate',
    monAvgBasket: 'Average basket',
    monMarginAfterStripe: 'Margin after Stripe',
    monStripeShare: 'Stripe share',
    monNetSmatchy: 'Net Smatchy revenue',
    reset: 'Reset',
    monthRange: 'Month range',
    view: 'View',
    searchOrganizer: 'Search organizer',
    payoutColId: 'Withdrawal ID',
    payoutColOrganizer: 'Organizer',
    payoutColAmount: 'Withdrawal amount',
    payoutColEvents: 'Number of events request',
    payoutColRequestedDate: 'Requested date',
    payoutColStatus: 'Withdrawal status',
    payoutColVatStatus: 'VAT status',
    payoutVatStatusMissingMsg:
      'This pro has not declared a VAT status yet: their monthly invoice cannot be drawn up, so the withdrawal cannot be approved.',
    vatStatus: {
      vat_registered: 'VAT registered',
      franchise: 'Franchise',
      association: 'Association',
      missing: 'Not declared',
    },
    payoutKpiAwaitingTitle: 'Total awaiting approval',
    payoutKpiAwaitingSubtitle: 'Awaiting Smatchy review.',
    payoutKpiProcessingTitle: 'Total amount processing',
    payoutKpiProcessingSubtitle: 'Awaiting processing by Stripe.',
    payoutApproveFailedTitle: 'Approve failed',
    payoutApproveFailedMsg: 'Unable to approve withdrawal request. Please try again.',
    payoutApprovedTitle: 'Withdrawal approved',
    payoutApprovedMsg: 'The request has been sent to Stripe for processing.',
    payoutDeclineFailedTitle: 'Decline failed',
    payoutDeclineFailedMsg: 'Unable to decline withdrawal request. Please try again.',
    payoutDeclinedTitle: 'Withdrawal declined',
    payoutDeclinedMsg: 'The organizer has been notified with your message.',
    txColActivity: 'Activity',
    txColUser: 'User',
    txColAmount: 'Amount',
    txColFee: 'Fee',
    txColNet: 'Net',
    txColStatus: 'Status',
    txColDate: 'Date',
    txColActions: 'Actions',
    txPaymentStatus: 'Payment status',
    txAllStatuses: 'All statuses',
    txPaid: 'Paid',
    txPending: 'Pending',
    txRefunded: 'Refunded',
    txTabPayment: 'Payment',
    txTabPayout: 'Payout',
    peTabPending: 'Pending requests',
    peTabApproved: 'Approved organizers',
    obColProOrganizer: 'Pro organizer',
    obColIncoming: 'Incoming earnings',
    obColAvailable: 'Available to withdraw',
    obColPendingPayout: 'Pending payout',
    obColStripeStatus: 'Stripe connection status',
    obColActions: 'Actions',
    obFilterStripeStatus: 'Stripe status',
    obFilterAll: 'All',
    bdColFinalEventRevenue: 'Final event revenue',
    bdColFinalSmatchyRevenue: 'Final Smatchy revenue',
    bdColOrganizerRevenue: 'Organizer revenue',
    bdEarningsStatus: 'Earnings status',
    bdSearchPlaceholder: 'Search activity or location',
    esSearchPlaceholder: 'Search invoice ID or organizer',
    esColInvoiceId: 'Invoice ID',
    esColPeriod: 'Period',
    esColTotalEvents: 'Total events',
    esColEventRevenue: 'Event revenue',
    esColSmatchyCommissions: 'Smatchy commissions',
    prdBankAccount: 'Bank account',
    diUser: 'User',
    diAllSports: 'All sports',
    erKpiTotalEvent: 'Total event revenue',
    erKpiTotalSmatchy: 'Total Smatchy revenue',
    erKpiTotalOrganizers: 'Total organizers revenue',
    erKpiTicketsSold: 'Tickets sold',
    erKpiRefundRate: 'Refund rate',
    erKpiStripeFees: 'Total Stripe fees',
    erKpiUnknown: 'Unknown',
    erFromLastMonth: 'from last month',
    erChartTitle: 'Final Smatchy revenue over time',
    tmSport: 'Sport',
    tmAllSport: 'All sports',
    tmStatus: 'Status',
    tmAllStatus: 'All statuses',
    tmTitle: 'Ticket metrics by event',
    bkSubIncoming: 'Awaiting transfer to available for withdrawal',
    bkSubPending: 'Awaiting review',
    adParticipant: 'Participant',
    adRefundProcessed: 'Refund processed',
    adLastUpdated: 'Last updated',
    tmTicketPrice: 'Ticket price',
    tmSearchPlaceholder: 'Search activity, location, country, organizer',
    tmTicketsProcessed: '{{count}} tickets processed',
    tmTicketRefundOne: '{{count}} ticket refund',
    tmTicketRefundMany: '{{count}} ticket refunds',
    tmTicketsSold: '{{count}} tickets sold',
    txSearchPlaceholder: 'Search activity or user',
    bkTotalIncoming: 'Total incoming earnings',
    bkTotalBalances: "Total organizer's balances",
    bkTotalPending: 'Total pending payout',
    bkSubFunds: 'Funds available to withdraw',
  },
  legacyUsers: {
    search: 'Search',
    name: 'Name',
    email: 'Email',
    registeredOn: 'Registered on',
    sportsCount: 'Number of sports',
    pushNotification: 'Push notification',
    noUserFound: 'No user found',
    registeredSince: 'Registered since:',
    sports: 'Sports',
    position: 'Position',
    noPosition: 'No known position',
    createdActivities: 'Created activities',
    myGroups: 'Groups',
    pastActivities: 'Past activities',
  },
  payouts: {
    organizerInfo: 'Organizer Information',
    withdrawalId: 'Withdrawal ID:',
    requestedDate: 'Requested Date:',
    breakdownTitle: 'Withdrawal breakdown',
    organizerRevenue: 'Organizer revenue',
    totalWithdrawal: 'Total withdrawal amount:',
    detailsTitle: 'Withdrawal Details',
    status: 'Status',
    amount: 'Amount',
    stripeProcessing: 'The withdrawal has been initiated and is being processed by Stripe',
    approveTitle: 'Approve withdrawal request?',
    trackInTransactions: 'You can track its status in Transactions page.',
    approveConfirm: 'I confirm that this withdrawal request has been reviewed and approved.',
    approveBtn: 'Approve payout',
    declineTitle: 'Decline withdrawal request?',
    declineStops: 'Declining this withdrawal will stop the transfer.',
    organizerNotified: 'The organizer will be notified with the reason provided below.',
    declineReason: 'Reason for declining this payout',
    reasonPlaceholder: 'Enter reason here...',
    declineBtn: 'Decline payout',
    transactionDetails: 'Transaction details',
  },
  readonly: {
    noMembers: 'No members in this group yet',
    noSubgroups: 'No subgroups in this group yet',
    noActivities: 'No activities in this group yet',
    subGroup: 'Sub-group',
  },
  commonTable: {
    filters: 'Filters',
    noData: 'No data found',
    search: 'Search',
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    dateRange: 'Date range',
    clearFilter: 'Clear filter',
    review: 'Review',
    smatchyRevenue: 'Smatchy revenue',
    totalRefunds: 'Total refunds processed',
    pageReport: 'Showing {first} to {last} of {totalRecords} results',
  },
  groupEngage: {
    title: 'Engage nearby members',
    context: 'Campaign for the group',
    groupSport: 'Group sport',
    noLocation: 'This group carries no location, so there is no "around" to target. Add a location to the group first.',
    channelInvitation: 'In-app group invitation',
    channelInvitationHint: 'The member can accept from the app; carries its own push.',
    channelEmailHint: 'The SendGrid group-invitation template, in the member language. Creates the invitation too.',
    channelPush: 'Discovery push',
    channelPushHint: 'A group near you is looking for members, through the marketing preference.',
    resultLine: 'Campaign sent: {{invited}} invitation(s), {{emails}} email(s), {{pushed}} discovery push(es).',
  },
  groupSheet: {
    createdOn: 'created on',
    by: 'by',
    membersOf: 'members, {{active}} active in 90d',
    activitiesCreated: 'activities created',
    joinedShare: 'of activities joined',
    lastActivity: 'last activity',
    daysAgo: '{{days}}d ago',
    namedAdmins: 'named admin(s)',
    signal: 'Signal',
    signalAliveNoAdmin:
      'living group without any admin: naming {{owner}} (creator, active {{days}}d ago) would keep it alive if they leave.',
    signalEmptyNoAdmin: 'no admin and no activity ever: a candidate for a wake-up campaign or a cleanup.',
    signalNoAdmin: 'no named admin: the group depends entirely on its creator.',
    signalDozing: 'the group is dozing: last activity {{days}}d ago. A campaign could wake it.',
    signalHealthy: 'healthy group: administered and recently active.',
    ownerAndAdmins: 'Owner and admins',
    creator: 'Creator',
    admin: 'Admin',
    activeAgo: 'active {{days}}d ago',
    noAdmin: 'No named admin',
    recentActivities: 'Latest group activities',
    partners: '{{count}} partner(s)',
    nobody: 'nobody',
    noActivity: 'No activity in this group yet',
    members: 'Members',
    noMembers: 'No members yet',
    moreMembers: '+ {{count}} more',
    description: 'Description',
    hasCover: 'Cover photo present',
    loading: 'Loading the sheet…',
    failed: 'Could not load this group sheet.',
  },
  strategy: {
    title: 'Strategy · 6 months',
    fullMonths: 'full months only',
    signups: 'Signups · monthly growth',
    membersTotal: 'members in the base',
    active90: 'Active in 90 days',
    activeMembers: 'members having acted',
    avgFill: 'Average fill',
    fillNote: 'partner seats, organiser excluded',
    paid: 'Paid volume',
    paidNote: 'deposits collected that month',
    vsPrev: 'vs previous month',
  },
  studio: {
    title: 'Marketing studio',
    badge: 'ACTIVATION',
    subtitle: 'Three problems, one decision file, everything written down. Your morning command post.',
    loadFailed: 'The studio could not load. Try again.',
    loading: 'Sweeping the three problems…',
    pulseJoins: 'attributed joins · 7d',
    pulsePending: 'actions to decide',
    pulseExpiring: '{{count}} expiring within 24h',
    pulseCampaigns: 'campaigns · 7d',
    pulseReached: '{{count}} members reached',
    pulsePressure: 'of the base solicited · 7d',
    pulsePressureNote: 'guardrail: watch this before sending more',
    deckTitle: 'To decide now',
    deckEmpty: 'Nothing to decide: no empty event, no small living group, no sleeping organizer. Enjoy.',
    kind: { fill_event: 'Fill', fill_group: 'Groups', organizer_nudge: 'Organizers' },
    whyFillEvent: 'Nobody joined the organiser yet, and {{candidates}} candidates live within 25 km.',
    whyFillGroup:
      'Living group with only {{members}} members (last activity {{days}}d ago); {{candidates}} candidates around.',
    whyOrganizers: '{{count}} proven organizers have posted nothing for 30 days.',
    chInvitation: 'In-app invitation',
    chPush: 'Discovery push',
    chEmail: 'Email',
    launch: 'Launch as-is',
    confirmLaunch: 'Confirm the launch',
    adjust: 'Adjust',
    discard: 'Discard',
    discardLater: 'Too early — propose again later',
    discardLaterHint: 'Sleeps 48h, comes back if still relevant.',
    discardTargeting: 'Wrong targeting — to review',
    discardTargetingHint: 'Recorded as a signal the rule aims wrong.',
    discardNever: 'Never for this one',
    discardNeverHint: 'Silences this target for good.',
    journalTitle: 'Journal and attribution',
    journalEmpty: 'No campaign yet. The first launch will write here.',
    colWhen: 'When',
    colCampaign: 'Campaign',
    colChannels: 'Channels',
    colFunnel: 'Funnel',
    colJoined: 'Joined',
    funnelNums: '{{targeted}} targeted · {{invited}} invited · {{emails}} emails',
    source: { action: 'Studio launch', composer: 'Sheet composer' },
    adjusted: 'adjusted',
    adjustTitle: 'Adjust and send',
    campaignFor: 'Campaign for',
    emailHint: 'Rides the invitation; skipped cleanly while the SendGrid template is pending.',
    eventResultLine: 'Sent: {{invited}} invitation(s), {{pushed}} push(es), {{emails}} email(s).',
    emailTemplateMissing: 'email skipped, template not created yet',
    autosTitle: 'Automates',
    autosNote: 'Soft channels act alone inside their rails; email and big audiences stay in your file.',
    killSwitch: 'All automates',
    autoName: {
      save_activity: 'Save-activity',
      welcoming_group: 'Welcoming group',
      organizer_detector: 'Organizer detector',
    },
    autoRule: {
      save_activity: 'At J-5, an activity nobody joined with ≥20 candidates around gets one discovery push.',
      welcoming_group:
        'A two-day-old signup with a declared sport gets one in-app invitation to the nearest living group.',
      organizer_detector: 'A proven organizer quiet for 30 days becomes a card in your file. Never a direct send.',
    },
    autoRails: {
      save_activity: 'rails: 1 solicitation/member/week · preferences respected · never twice the same activity',
      welcoming_group: 'rails: one group only · never a dead group · one welcome per member, ever',
      organizer_detector: 'the email leaves only after your approval',
    },
    autoStats: '7d: {{triggered}} triggered · {{sent}} sends',
    autoJoined: '{{count}} joined',
    autoProposesOnly: 'proposes only',
    seqsTitle: 'Sequences',
    seqName: { organizer_redepose: 'Redépose (organizers)', welcome_first_step: 'Welcome to the first step' },
    seqMetrics: '{{active}} walking · {{success}} exited by success · {{ended}} reached the end',
    seqExit: {
      organizer_redepose: 'success exit: an activity gets posted, the remaining reminders die unsent',
      welcome_first_step: 'success exit: the member joins or creates anything',
    },
    funnelTracking: '{{opened}} opened · {{clicked}} clicked',
  },
  activityStatus: {
    incoming: 'Incoming',
    completed: 'Completed',
    canceled: 'Canceled',
    total: 'Total activities',
    totalParticipants: 'Total participants',
    completionRate: 'Completion rate',
  },
  datePicker: {
    startDate: 'Start date',
    endDate: 'End date',
    startMonth: 'Start month',
    endMonth: 'End month',
    yesterday: 'Yesterday',
    last7days: 'Last 7 days',
    last14days: 'Last 14 days',
    last30days: 'Last 30 days',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    thisQuarter: 'This quarter',
    lastQuarter: 'Last quarter',
    thisYear: 'This year',
    clear: 'Clear',
  },
  activityDrawer: {
    name: 'Activity name',
    nameRequired: 'Activity name is required',
    coverPhoto: 'Cover photo',
    dateTime: 'Date and time',
    description: 'Description',
    duration: 'Duration',
    level: 'Level',
    sportType: 'Sport type',
    visibility: 'Visibility',
    startDate: 'Start date',
    endDate: 'End date',
    organizer: 'Organizer',
    participants: 'Participants',
    noParticipants: 'No participants in this activity yet',
    editPhoto: 'Edit photo',
    uploadPhoto: 'Upload new photo',
    removePhoto: 'Remove photo',
    coverPhotoEmptyTitle: 'Add a cover photo',
    coverPhotoEmptyHint: 'Optional · JPG or PNG',
    saveAll: 'Save all changes',
    discardAll: 'Discard all',
    undo: 'Undo',
    close: 'Close',
    edit: 'Edit',
    changesSaved: 'Your changes to the activity have been saved',
    changesDiscarded: 'All changes to the activity have been discarded',
    createdOn: 'Created on {{ date }}',
    noDescription: 'No description provided',
    routeLink: 'Route link',
    noRouteLink: 'No route link provided',
    handiFriendly: 'Handi Friendly',
    handiFriendlyYes: 'Yes, declared by the organiser',
    handiFriendlyNo: 'No',
    cancelActivity: 'Cancel this activity',
    deleteActivity: 'Delete this activity',
    visibilityEveryone: 'Everyone',
    visibilityPrivate: 'Private',
  },
  auditDetail: {
    title: 'Audit log detail',
    selectEntry: 'Select an audit log entry to view details.',
    actor: 'Actor',
    actorId: 'Actor ID',
    entity: 'Entity',
    entityId: 'Entity ID',
    logId: 'Log ID',
    name: 'Name',
    type: 'Type',
    version: 'Version',
    field: 'Field',
    before: 'Before',
    after: 'After',
    stateChange: 'State change',
    noChanges: 'No state changes detected.',
    metadata: 'Metadata',
    technicalDetail: 'Technical details',
    idempotencyKey: 'Idempotency key',
    stripeEventId: 'Stripe event ID',
  },
  paidRequest: {
    whichActivities: 'Which paid activities will you offer?',
    title: 'Request for creating paid activities',
    detail: 'Details',
    history: 'History',
    companyInfo: 'Company / association information',
    nameLabel: 'Name:',
    address: 'Address:',
    siret: 'SIRET / RNA number:',
    roleDescription: 'Role description:',
    roleType: 'Role type:',
    requiredProof: 'Required proof',
    insuranceCertificate: 'Insurance certificate',
    insuranceExpiry: 'Insurance expiration date:',
    continueApprove: 'Continue to approve',
    rejectWithComment: 'Reject with comment',
    revokeWithComment: 'Revoke access with comment',
    requestResubmission: 'Request resubmission',
    rejectionReason: 'Reason for rejection:',
    submittedOn: 'Submitted on:',
    rejectedOn: 'Rejected on:',
    cancelledOn: 'Cancelled on:',
    close: 'Close',
  },
  activityEarnings: {
    title: 'Activity earnings details',
    location: 'Location of the activity',
    ticketPrice: 'Ticket price',
    eventBreakdown: 'Activity revenue breakdown',
    smatchyBreakdown: 'Smatchy revenue breakdown',
    eventRevenue: 'Activity revenue:',
    finalEventRevenue: 'Final activity revenue:',
    organizerRevenue: 'Organizer revenue:',
    smatchyRevenue: 'Smatchy revenue:',
    finalSmatchyRevenue: 'Final Smatchy revenue:',
    smatchyCommissions: 'Smatchy commissions',
    stripeFees: 'Stripe processing fees:',
    serviceFees: 'Service fees:',
    refundProcessed: 'Refund processed:',
    participantTransaction: 'Participant transactions',
  },
  userProfile: {
    loadFailed: 'The profile could not be loaded. The environment may not serve it yet.',
    actedAgo: 'acted {{days}} day(s) ago',
    sex: { male: 'man', female: 'woman' },
    factActivities: 'activities',
    factCreated: 'created',
    factGroups: 'groups',
    factPaid: 'paid',
    months: '{{months}} month(s)',
    membershipAge: 'of membership',
    daysAgoShort: '{{days}} d ago',
    lastLogin: 'last login',
    untracked: 'untracked',
    years: '{{age}} yrs',
    bracket: 'bracket {{label}}',
    bracketShare: '{{share}}% of the base',
    nearby: 'members within {{radius}} km',
    nearbySport: 'incl. {{count}} in {{sport}}',
    topShare: 'top {{share}}%',
    topShareLabel: 'most active members, last 90 days',
    signalLabel: 'Marketing signal:',
    signals: {
      ambassador: 'Complete profile, reachable, hot: an ideal ambassador candidate{{sport}} .',
      activation: 'Never acted and reachable: a first-activity push is the move.',
      reactivation: 'Quiet for {{days}} days and reachable: a reactivation message can bring them back.',
      unreachable: 'No push token: this member cannot be reached by push at all.',
      optedOut: 'Marketing pushes explicitly refused: respect it, only transactional messages.',
      organiserToCoach: '{{count}} creation(s) nobody joined: worth coaching before they give up organising.',
    },
    timelineTitle: 'Activity journey',
    kind: { created: 'Created', joined: 'Joined', next: 'Upcoming' },
    attendees: '{{count}} partner(s), organiser excluded',
    noTimeline: 'No activity yet.',
    groupsTitle: 'Groups',
    admin: 'ADMIN',
    memberRole: 'MEMBER',
    groupMembers: '{{count}} members',
    noGroups: 'No group.',
    moneyTitle: 'Money',
    paid: 'paid in total',
    refunded: 'refunded',
    released: 'earned as organiser',
    positionTitle: 'Declared position',
    sportsTitle: 'Sports and levels',
    declaredAt: 'declared on {{date}}',
    noSports: 'No declared sport.',
    completenessTitle: 'Profile completeness',
    fields: {
      firstName: 'first name',
      city: 'city',
      position: 'position',
      sport: 'sport',
      birth: 'birth date',
      sex: 'gender',
      avatar: 'avatar',
      description: 'description',
      push: 'push',
    },
    reachTitle: 'Reachability',
    pushToken: 'Push token:',
    present: 'present',
    absent: 'absent',
    defaultSettings: 'Settings never touched: defaults apply, everything on.',
    pushGroup: 'Mobile push (14)',
    mailGroup: 'Email (9)',
    switches: {
      event_edit_or_cancel: 'edit/cancel',
      event_new_participant: 'new participant',
      event_reminder: 'reminder',
      event_share: 'share',
      event_new_event_proximity: 'proximity',
      group_invitation: 'group invite',
      group_accept: 'group accept',
      group_new_event: 'group activity',
      group_new_member: 'group member',
      group_deleted: 'group deleted',
      chat_new_message_smatcher: 'smatcher chat',
      chat_new_message_event: 'activity chat',
      chat_new_message_group: 'group chat',
      marketing: 'marketing',
      event_participant_unregister: 'unregistered',
      event_confirm_register: 'register confirm',
      event_confirm_unregister: 'unregister confirm',
    },
  },
  eventDetail: {
    today: 'Today',
    inDays: 'D-{{days}}',
    copyLink: 'Copy the link',
    storyCta: 'Instagram story',
    level: 'Level',
    anyLevel: 'All levels welcome',
    partnersSeats: 'partner seats filled, organiser excluded',
    engageTitle: 'Engage nearby members',
    engageAround: '<strong>{{total}}</strong> members live within {{radius}} km of this activity.',
    engageSameSport: 'Including <strong>{{count}}</strong> who declare {{sport}}.',
    exportCta: 'Export these {{count}} contacts (CSV)',
    pushNote: 'The CSV below feeds a manual email campaign while the SendGrid invitation template is pending.',
    radiusLabel: 'Radius',
    sportLabel: 'Sport',
    allSports: 'All sports',
    levelLabel: 'Level',
    levelAny: 'Any level',
    levelFrom: 'From',
    levelTo: 'To',
    channelsLabel: 'Channels',
    channelInvitation: 'In-app invitation',
    channelInvitationHint: 'The member can accept from the app; carries its own push.',
    channelPush: 'Proximity push',
    channelPushHint: 'An activity near you is looking for partners.',
    channelEmail: 'Email',
    channelEmailHint: 'Coming once the SendGrid invitation template exists.',
    previewCount: '<strong>{{count}}</strong> members targeted, participants and already-invited excluded (500 max).',
    previewLoading: 'Counting the audience…',
    send: 'Send to {{count}} members',
    confirmSend: 'Confirm: really send to {{count}} members',
    cancelSend: 'Cancel',
    resultLine: 'Campaign sent: {{invited}} invitation(s) created, {{pushed}} proximity push(es).',
    sendFailed: 'The campaign failed. Nothing may have gone out; try again.',
    participants: 'Participants',
    attendeesNote: 'The raw count includes the organiser.',
    noAttendees: 'Nobody has joined yet.',
    viewProfile: 'View profile',
    description: 'Description',
  },
  eventsList: {
    title: 'Upcoming activities',
    subtitle: 'The list first; the map on demand; a click slides the sheet in from the right',
    jMinus: 'D−{{n}}',
    daysAgo: '{{n}}d ago',
    today: 'today',
    searchPlaceholder: 'Search by keyword…',
    cityPlaceholder: 'City…',
    allSports: 'All sports',
    emptyOnly: 'Nobody joined',
    paidOnly: 'Paid',
    recurringOnly: 'Recurring',
    showMap: 'Show the map',
    hideMap: 'Hide the map',
    mapNote: 'folded by default: the list is the work surface',
    start: 'Start',
    activity: 'Activity',
    fill: 'Fill',
    level: 'Level',
    price: 'Price',
    anyLevel: 'Any level',
    free: 'free',
    empty: 'No upcoming activity matches the filters.',
    count: '{{count}} activities shown',
    fillNote: 'fill excludes the organiser',
  },
  marketingUsers: {
    title: 'Users',
    subtitle: 'Who is worth what, what they last did, and how they can be reached',
    searchPlaceholder: 'Search a name, an email, a city… Enter to apply',
    reachableOnly: 'Reachable for marketing',
    loadFailed: 'The user list could not be loaded. The environment may not serve it yet.',
    member: 'Member',
    heat: 'Heat',
    lastAction: 'Last action',
    lastLogin: 'Last login',
    sports: 'Sports and levels',
    reachable: 'Reachable',
    activities: 'Activities',
    since: 'since',
    created: 'Created',
    joined: 'Joined',
    daysAgo: '{{days}} day(s) ago',
    noAction: 'No action ever',
    noSport: 'no declared sport',
    noPushToken: 'no push token',
    defaultSettings: 'default settings (all on)',
    pushMarketing: 'marketing',
    pushReminder: 'reminders',
    pushProximity: 'proximity',
    pageOf: 'Page {{page}} of {{last}} · {{total}} members',
    note: 'Heat follows the segments screen rule, on the last action. Last login only exists since July 2026: a dash means untracked, not never came. Untouched settings mean defaults, everything on. The full 23 notification switches will live in the profile view.',
  },
  commission: {
    title: 'Commissions rate',
    organizerCommission: 'Organizer commission',
    addCustom: 'Add custom commission',
    customRate: 'Custom rate',
    commissionRate: 'Commission rate',
    currentGlobalRate: 'Current global commission rate',
    globalRate: 'Global commission rate',
    editGlobalRate: 'Edit global rate',
    legalSource: 'Legal source of truth',
    searchOrganizer: 'Search organizer',
    action: 'Action',
    actions: 'Actions',
    noData: 'No data',
    lastUpdated: 'Last updated:',
    colOrganizerName: 'Organizer name',
    colRoleType: 'Role type',
    colLastUpdated: 'Last updated',
    globalRateNote: 'Applicable to all events unless overridden by a specific agreement.',
    editRateNote: 'Changes initiate a legally binding re-acceptance flow for all pro organizers',
    rateBounds: 'Minimum and Maximum allowed commission rate is 0.1% and 50%',
  },
  transactionDetails: {
    title: 'Payment details',
    header: 'Transaction details',
    participantInfo: 'Participant information',
    breakdown: 'Payment breakdown',
    paymentAmount: 'Payment amount:',
    participantAmount: 'Participant amount:',
    stripeFees: 'Stripe processing fees:',
    refundedAmount: 'Refunded amount:',
    amount: 'Amount',
    status: 'Status',
  },
  groupsManagement: {
    archive: {
      badge: 'Archived',
      archiveAction: 'Archive group',
      reactivateAction: 'Reactivate group',
      bannerTitle: 'Archived group — read-only',
      bannerMessage:
        'This group is frozen: it can be viewed, but it cannot be edited, deleted, or sent a campaign while archived.',
      confirmArchiveTitle: 'Archive this group?',
      confirmArchiveMessage:
        'The group becomes read-only. Members keep access to the group and its chat, but no one can create activities in it or edit it any more. You can reactivate it at any time.',
      confirmArchiveCta: 'Archive',
      confirmReactivateTitle: 'Reactivate this group?',
      confirmReactivateMessage:
        'The group becomes active again: members can create activities in it and edit it once more.',
      confirmReactivateCta: 'Reactivate',
      cancel: 'Cancel',
      archivedToast: '"{{name}}" has been archived',
      reactivatedToast: '"{{name}}" has been reactivated',
    },
    title: 'Groups management',
    kpiTotal: 'Total groups',
    kpiNew: 'New this month',
    kpiMembers: 'Total members',
    kpiActivities: 'Group activities',
    kpiTop: 'Top sport',
    kpiThisMonth: 'this month',
    kpiAcrossGroups: 'Across all groups',
    kpiOfGroups: 'of groups',
    kpiNoAdmins: 'Without any admin',
    kpiNoAdminsNote: 'The lever: name an admin per living group',
    cityFilter: 'City…',
    withoutAdmins: 'Without admin',
    privateOnly: 'Private',
    exportCsvTooltip:
      'Exports every group, roots and subgroups, with a parent column. The on-screen filters are not applied.',
    privacy: 'Privacy',
    adminsColumn: 'Admins',
    public: 'Public',
    private: 'Private',
    filters: 'Filters',
    clearFilter: 'Clear filter',
    backDrawer: 'Back to previous drawer',
    searchGroup: 'Search group or admin',
    deleteGroup: 'Delete group',
    engageGroup: 'Engage nearby members',
    editGroup: 'Edit group',
    groupName: 'Group name',
    groupLead: 'Group lead',
    sportType: 'Sport type',
    totalSubgroups: 'Total subgroups',
    totalMembers: 'Total members',
    totalActivities: 'Total activities',
    total: 'Total',
    subgroups: 'Subgroups',
    members: 'Members',
    activities: 'Activities',
    actions: 'Actions',
    groupNameLabel: 'Group name',
    nameRequired: 'Group name is required',
    descriptionRequired: 'Description is required',
    coverPhoto: 'Cover photo',
    description: 'Description',
    handiFriendly: 'Handi Friendly',
    handiFriendlyYes: 'Yes, declared by the group admins',
    handiFriendlyNo: 'No',
    sportTypeLabel: 'Sport type',
    editPhoto: 'Edit photo',
    uploadPhoto: 'Upload new photo',
    removePhoto: 'Remove photo',
    deleteThisGroup: 'Delete this group',
    saveAll: 'Save all changes',
    discardAll: 'Discard all',
    undo: 'Undo',
    close: 'Close',
    edit: 'Edit',
    loadingGroups: 'Loading groups…',
    noGroupsMatch: 'No groups match your search or filters',
    changesSaved: 'Your changes to the group have been saved',
    changesDiscarded: 'All changes to the group have been discarded',
  },
  usersManagement: {
    suspendSuccess: 'Account suspended. It will be deleted as soon as nothing blocks it any more.',
    suspendFailed: 'Failed to suspend the account',
    liftSuccess: 'Suspension lifted',
    liftFailed: 'Failed to lift the suspension',
    title: 'Users management',
    totalUsers: 'Total users',
    allAccounts: 'All registered accounts',
    proUsers: 'Pro users',
    engagement: 'Engagement',
    profileCompletion: 'Profile completion',
    pushNotification: 'Push notification',
    filters: 'Filters',
    apply: 'Apply',
    resetAll: 'Reset all',
    searchUser: 'Search user name or email',
    exactCity: 'Exact city name',
    startDate: 'Start date',
    endDate: 'End date',
    min: 'Min',
    max: 'Max',
    userName: 'User name',
    email: 'Email',
    city: 'City',
    cityLabel: 'City',
    sports: 'Sports',
    activities: 'Activities',
    actions: 'Actions',
    status: 'Status',
    userStatus: 'User status',
    userType: 'User type',
    userTypeHeader: 'User type',
    user: 'User',
    lastLogin: 'Last login date',
    lastLoginHeader: 'Last login date',
    createdAt: 'Account creation date',
    createdOn: 'Created on',
    completionRange: 'Profile completion (%)',
    noUsers: 'No users found.',
    deletedItem: 'Deleted item',
    deletedItems: 'Deleted items',
    movedTo: 'has been moved to',
    active: 'Active',
    inactive: 'Inactive',
    temperature: {
      hot: 'Hot',
      warm: 'Warm',
      cool: 'Cool',
      frozen: 'Frozen',
      never: 'Never acted',
      groupOnly: 'Group only',
      untouched: 'Untouched',
    },
    proColumn: 'Pro',
    allStatuses: 'All statuses',
    all: 'All',
    pushOn: 'On',
    pushOff: 'Off',
    types: {
      all: 'All account types',
      USER: 'User',
      PERSONAL_TRAINER_COACH: 'Personal trainer / coach',
      PROFESSIONAL_ATHLETE: 'Professional athlete',
      OTHER: 'Other pro accounts',
      ASSOCIATION_CLUB: 'Association / club',
    },
    ofTotal: 'of the base',
    deleteUser: 'Delete user',
    editUser: 'Edit user',
  },
  userDetail: {
    completion: 'Profile completion',
    completionCount: '{{met}} of {{total}}',
    completionFilled: 'Filled',
    completionMissing: 'Missing',
    completionSameList: 'The same list the member sees in the application.',
    completionCriteria: {
      firstName: 'First name',
      lastName: 'Last name',
      gender: 'Gender',
      dateOfBirth: 'Date of birth',
      city: 'City',
      photo: 'Photo',
      sports: 'Sports chosen',
      sportLevels: 'A level on each sport',
      bio: 'Bio',
      availability: 'When they are free',
      intention: 'What they are looking for',
      goals: 'A goal for each sport',
    },
    personalInfo: 'Personal information',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    enterPhone: 'Enter phone number',
    position: 'Position',
    sportsLevel: 'Sports level',
    pushNotification: 'Push notification',
    willReceivePush: 'User will receive push notification',
    consent: {
      title: 'Notifications & marketing consent',
      hint: 'Each switch saves on its own. “On” means the user receives it; a switch is on until it is turned off.',
      pushTitle: 'Push notifications',
      mailTitle: 'Emails',
      cutMarketing: 'Turn off all marketing',
      cutAll: 'Turn off everything',
      on: 'On',
      off: 'Off',
      others: 'Other',
      push: { activities: 'Activities', groups: 'Groups', chat: 'Chat', profile: 'Profile' },
      mail: { activities: 'Activities', groups: 'Groups' },
      keys: {
        event_edit_or_cancel: 'Activity change or cancellation',
        event_new_participant: 'New participant',
        event_reminder: 'Day-before reminder',
        event_share: 'Shared or suggested activity',
        event_new_event_proximity: 'Activities around you',
        event_confirm_register: 'Sign-up confirmation',
        event_confirm_unregister: 'Cancellation confirmation',
        event_participant_unregister: 'A participant unregisters',
        group_invitation: 'Group invitation',
        group_accept: 'Invitation accepted',
        group_new_event: 'New group activity',
        group_new_member: 'New group member',
        group_life: 'Group life (refusals, departures…)',
        group_my_role: 'My role in the group',
        chat_new_message_smatcher: 'Messages from a smatcher',
        chat_new_message_event: 'Messages in an activity',
        chat_new_message_group: 'Messages in a group',
        profile_new_follower: 'New follower',
        marketing: 'Marketing & promotions',
      },
    },
    noLocation: 'No location data',
    noSports: 'No sports registered.',
    group: 'Group',
    groups: 'Groups',
    activities: 'Activities',
    created: 'Created',
    joined: 'Joined',
    organized: 'Organized',
    canceled: 'Canceled',
    lastLoginOn: 'Last login on',
    userType: 'User type:',
    nameNeeded: 'Enter both first and last name to create a profile.',
    loadingActivities: 'Loading activities…',
    loadingGroups: 'Loading groups…',
    noActivities: 'No activities found.',
    noGroups: 'No groups found.',
    deleteAccount: 'Delete this account',
    suspendedBadge: 'Suspended, waiting for deletion',
    suspendedReasonMinor: 'Minor',
    suspendedReasonOther: 'Other reason',
    liftSuspension: 'Lift the suspension',
    discardAll: 'Discard all',
    undo: 'Undo',
    close: 'Close',
    edit: 'Edit',
  },
  deletedItems: {
    title: 'Deleted items',
    tabGroups: 'Groups',
    tabUsers: 'Users',
    tabActivities: 'Activities',
    filters: 'Filters',
    clearFilter: 'Clear filter',
    backDrawer: 'Back to previous drawer',
    searchGroup: 'Search group',
    searchEmail: 'Search email',
    searchActivity: 'Search activity',
    searchAddress: 'City, venue, country',
    pageReport: 'Showing {first} to {last} of {totalRecords} results',
    groupName: 'Group name',
    groupLead: 'Group lead',
    sportType: 'Sport type',
    totalSubgroups: 'Total subgroups',
    totalMembers: 'Total members',
    totalActivities: 'Total activities',
    total: 'Total',
    subgroups: 'Subgroups',
    member: 'Members',
    activitiesHeader: 'Activities',
    userName: 'User name',
    email: 'Email',
    userType: 'User type',
    lastLogin: 'Last login date',
    pushNotif: 'Push notif.',
    status: 'Status',
    createdOn: 'Created on',
    deletedBy: 'Deleted by',
    activity: 'Activity',
    group: 'Group',
    participantsHeader: 'Participants',
    organizer: 'Organizer',
    participants: 'Participants',
    deletedItem: 'Deleted item',
    deletedByLabel: 'Deleted by',
    deletedRange: 'Deleted date range',
    addressContain: 'Address contains',
    coverPhoto: 'Cover photo',
    description: 'Description',
    sport: 'Sport',
    sportTypeLabel: 'Sport type',
    dateTime: 'Date and time',
    duration: 'Duration',
    level: 'Level',
    visibility: 'Visibility',
    startDate: 'Start date',
    endDate: 'End date',
    weekly: 'Weekly',
    close: 'Close',
    showLess: 'Show less',
    loadingGroups: 'Loading groups…',
    loadingUsers: 'Loading users…',
    loading: 'Loading…',
    noGroups: 'No deleted groups found',
    noUsers: 'No deleted users found',
    noActivities: 'No deleted activities found',
    noGroupsMatch: 'No groups match your search or filters',
    noUsersMatch: 'No users match your search or filters',
    noActivitiesMatch: 'No activities match your search or filters',
    noParticipants: 'No participants in this activity yet',
  },
  report: {
    reliability: 'Built on the {{share}}% of profiles where this field is filled',
    crossLink: 'Cross sport with city and duration',
    title: 'User analytics',
    trendsTitle: 'Sport participation trends',
    trendsNote: 'Number of participants per month per sport',
    ageTitle: 'Age distribution',
    ageNote: 'Users across age brackets',
    genderTitle: 'Gender distribution',
    genderNote: 'Users by gender',
    levelTitle: 'Level distribution',
    levelNote: 'Participants by skill level',
    sportTitle: 'Sport distribution',
    sportNote: 'Percentage of users per sport',
    activityTitle: 'User activity status',
    activityNote: 'Based on activity creation or participation',
    totalUsers: 'Total users',
    allAccounts: 'All registered accounts',
    averageAge: 'Average age',
    yearsOld: 'Years old',
    eventsInPeriod: 'Activities in period',
    noSports: 'No matching sports found',
    period: 'Period',
    sports: 'Sports',
    total: 'Total',
    filters: 'Filters',
    allSports: 'All sports',
    apply: 'Apply',
    resetAll: 'Reset all',
    mostPopular: 'Most popular sport',
    searchSports: 'Search sports',
    gender: 'Gender',
    level: 'Level',
    levelAll: 'All levels',
    level1: 'Level 1 · beginner',
    level2: 'Level 2 · intermediate',
    level3: 'Level 3 · advanced',
    levelNoteScale: "Each band is a third of the sport's own scale (6, 10 or 12 steps).",
    activeUsers: 'Active users',
    inactiveUsers: 'Inactive users',
    participantsWord: 'Participants',
    participantsWordLower: 'participants',
    usersWord: 'Users',
    eventsWord: 'activities',
    tooltipNoUsers: 'no users',
    eventsCreatedLegend: 'Activities created • Participants joined',
    sportsWord: 'sports',
    city: 'City',
    cityPlaceholder: 'City, spelling variants folded — Enter to apply',
    cityClear: 'Clear',
    genderAll: 'Everyone',
    genderMale: 'Men',
    genderFemale: 'Women',
    genderNote2: 'Filters the demographic charts; activity counts stay all-genders.',
    preset30d: 'Last 30 days',
    preset90d: 'Last 90 days',
    preset6m: 'Last 6 months',
    preset12m: 'Last 12 months',
    vsLastYear: 'vs the same period last year',
    participantsVsLastYear: 'participants vs last year',
    notFilled: '{{count}} not filled ({{share}}% of the whole base), kept out of the chart',
  },
  journeyFunnel: {
    title: 'Parcours',
    subtitle: 'From a completed profile to a first join, and where new members drop off',
    cityFilterLabel: 'Parcours by city',
    cityFilterAll: 'All',
    cityFilterNote: "Segments the parcours by the member's profile city.",
    loadFailed: 'Could not load the parcours funnel',
    verdict: '{{joined}} of the {{base}} members who completed their profile went on to join an activity ({{share}}%).',
    storyTitle: 'The parcours, step by step',
    keptFromPrevious: '{{share}}% of the previous step',
    lostHere: 'here',
    note:
      'Each step counts distinct members and is nested inside the step before it, so a rate is never above 100%. ' +
      'Members who onboarded before the mobile emission shipped are outside this funnel by design, so it reads zero ' +
      'until events accrue.',
    stage: {
      profile_completed: 'Completed profile',
      search_performed: 'Ran a search',
      activity_viewed: 'Viewed an activity',
      joined: 'Joined an activity',
    },
  },
  matching: {
    title: 'Matching',
    subtitle: 'From sign-up to actually meeting someone, and where people are lost',
    cityFilterLabel: 'Funnel by city',
    cityFilterAll: 'All',
    cityFilterNote: "Segments the funnel by the member's city. The action cards below stay platform-wide.",
    stage: {
      signedUp: 'Signed up',
      matchable: 'Matchable profile',
      involved: 'Involved in an activity',
      joinedSomeoneElse: 'Joined someone else',
      paid: 'Paid',
    },
    verdict: 'The biggest loss is {{lost}} people, at the step « {{stage}} ». That is where acting pays.',
    keptFromPrevious: '{{share}}% of the previous step',
    note:
      'Each stage counts distinct people, so the drop between two stages is real loss and not double ' +
      'counting. Matchable means a declared sport and a location, the two things matching needs. Joined ' +
      'someone else excludes organisers counting themselves, which the raw data does.',
    seeActivities: 'See the activities where the organiser is still alone',
    loadFailed: 'Could not load the funnel',
    verdictJoined: '{{joined}} people actually joined someone, out of {{base}} sign-ups.',
    verdictWaiting:
      '{{waiting}} people are ready to play and have nothing to join: that is where every marketing euro pays the most.',
    actionsTitle: 'The 3 actions that unlock the most',
    action1Title: 'Reach the {{count}} profiles missing ONLY their sport',
    action1Body: 'They filled everything but the sport. One field separates them from being matchable.',
    action1Push: 'About {{share}}% of the base is reachable by push: a zero-cost "pick your sport" campaign.',
    action1Gain: 'Potential gain: +{{gain}}% matchable profiles',
    action1Cta: 'See profile quality',
    action2Title: 'Create offer where {{waiting}} matchable people are waiting',
    action2Body: 'The most expensive step of the funnel: people ready, nothing to join nearby.',
    action2Candidates:
      '{{candidates}} group leaders never organised anything; running a group multiplies the odds of organising by 19.7, they are your best candidates.',
    action2Cta: 'See cities to activate',
    action2Cta2: 'The organiser candidates',
    action3Title: 'Rescue the {{count}} activities where the organiser is still alone',
    action3Body:
      'Each one saved moves several people from "matchable" to "joined" at once: the fastest lever of the funnel.',
    action3Cta: 'See struggling activities',
    storyTitle: 'The journey, step by step',
    lossHere: 'here',
    lossLink: '→ treat it',
    loss: {
      gap1: 'incomplete profiles, mostly sport and position',
      gap2: 'ready, but nothing to join nearby',
      gap3: 'created something, nobody came',
      gap4: 'payment only concerns paid activities',
    },
  },
  finals: {
    title: 'Competitions',
    subtitle:
      'A final entered here becomes a badge in the app: one outing of its sport in the seven days that end on the day of the final. Generic names, never a brand.',
    loading: 'Loading…',
    failed: 'The finals could not be loaded.',
    none: 'No final entered yet.',
    name: 'Name shown',
    sport: 'Sport',
    week: 'Week that counts',
    state: 'State',
    holders: 'Earned by',
    states: { ahead: 'To come', running: 'This week', past: 'Gone by' },
    edit: 'Edit',
    delete: 'Delete',
    locked: 'Locked',
    lockedHint: 'Somebody has earned this badge: it can no longer be changed or deleted.',
    areYouSure: 'Delete « {{name}} »?',
    formAdd: 'Add a final',
    formEdit: 'Edit the final',
    nameFr: 'Name in French',
    nameEn: 'Name in English',
    chooseSport: 'Choose a sport',
    finalDay: 'Day of the final',
    rule: 'The week that counts is the seven days that end on the day of the final.',
    weekHint: 'Week that counts: from {{from}} to {{to}}, in each member’s own time.',
    save: 'Save',
    cancel: 'Cancel',
    refused: {
      final_day_past: 'This final is already past: there is no catch-up.',
      final_already_earned: 'Somebody has earned this badge: it can no longer be changed or deleted.',
      sport_not_found: 'This sport no longer exists.',
      unknown: 'The final could not be saved.',
    },
  },
  partners: {
    loading: 'Loading…',
    failed: 'The brands could not be loaded.',
    premium: 'PREMIUM',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    uploading: 'Sending the image…',
    uploadFailed: 'The image could not be sent.',
    brands: {
      title: 'Partner brands',
      subtitle: 'The brands that can carry a partner event. Admins only.',
      none: 'No brand yet.',
      add: '+ Add a brand',
      newEvent: 'New partner event',
      brand: 'Brand',
      color: 'Colour',
      link: 'Link',
      events: 'Events',
      next: 'Next',
      status: 'Status',
      active: 'Active',
      archived: 'Archived',
      edit: 'Edit',
      addTitle: 'New brand',
      editTitle: 'Edit the brand',
      name: 'Name',
      logo: 'Logo',
      logoHint: 'Square, 512 × 512 px minimum, PNG or SVG',
      choose: 'Choose',
      replace: 'Replace',
      removeLogo: 'Remove',
      colorHint: 'Used behind the initials when the brand has no logo.',
      siteLink: 'Link to the website',
      account: 'Organiser account of the brand (email)',
      accountHint: 'Only needed for events the brand organises alone. The account must already exist in the app.',
      preview: 'Preview in the app',
      archive: 'Archive',
      unarchive: 'Restore',
    },
    event: {
      title: 'New partner event',
      subtitle: "Shown in everybody's feed, open to Premium subscribers only. Always free.",
      toBrands: 'Partner brands',
      created: 'The event is published. It appears in the app feed.',
      who: 'Who organises it',
      brand: 'Brand',
      chooseBrand: 'Choose a brand',
      organiser: 'Organiser shown',
      bySmatchy: 'Smatchy, with the brand',
      bySmatchyHint: 'The Smatchy account organises. Participants write to Smatchy.',
      byBrand: 'The brand alone',
      byBrandHint: 'The brand has its own organiser account, linked to its record.',
      outing: 'The outing',
      name: 'Title',
      nameHint: 'Can be the name of an athlete: it stays visible to non-subscribers.',
      sport: 'Sport',
      chooseSport: 'Choose a sport',
      kind: 'Type',
      chooseKind: 'Choose a type',
      day: 'Date',
      hour: 'Time (Paris)',
      hiddenHour: 'Hidden from non-subscribers',
      place: 'Address',
      placeHint: 'Type an address, a place or a town, in France or abroad',
      placeNoTown: 'Google does not name the town of this place: choose an address nearby.',
      city: 'Town shown to non-subscribers: {{city}}',
      exactPlace: 'Exact place',
      hiddenPlace: 'Hidden from non-subscribers',
      duration: 'Duration',
      places: 'Places',
      photo: 'Photo',
      description: 'Description',
      hiddenDescription: 'Hidden from non-subscribers',
      publish: 'Publish',
      preview: 'Preview in the feed',
      asSubscriber: 'Premium subscriber',
      asOther: 'Not subscribed',
      free: 'Free with Premium',
      discover: 'Discover Premium',
      previewHint:
        'Seen by a non-subscriber: title, brand, date, town and type visible, time and exact place hidden, and a button to discover Premium.',
    },
    events: {
      title: 'Partner events',
      subtitle: 'The partner events written in the back office. Admins only.',
      upcoming: 'Upcoming',
      past: 'Past and cancelled',
      allBrands: 'All brands',
      failed: 'The partner events could not be loaded.',
      none: 'No partner event here.',
      event: 'Event',
      when: 'Date',
      where: 'Town',
      registered: 'Registered',
      status: { upcoming: 'Upcoming', past: 'Past', cancelled: 'Cancelled' },
      remove: 'Remove',
      editTitle: 'Edit the partner event',
      fixed: 'The brand, the sport and the town do not change: for another one, write a new event.',
      belowRegistered: '{{n}} people are already registered: there cannot be fewer places.',
      toldOfChange:
        'The {{n}} registered people are told if the date, the time or the duration changes. A new exact place alone is not sent to them.',
      removeTitle: 'Cancel « {{title}} »?',
      cancel: 'Cancel the outing',
      cancelHint: 'It will no longer take place. The {{n}} registered people are told.',
    },
    refused: {
      brand_account_not_found: 'No account in the app has this email.',
      brand_archived: 'This brand is archived: restore it first.',
      brand_has_no_account: 'This brand has no organiser account: add its email on the brand, or choose Smatchy.',
      brand_not_found: 'This brand no longer exists.',
      partner_event_duration_invalid: 'This duration is not possible.',
      partner_event_in_the_past: 'This date has already passed.',
      session_kind_not_in_sport: 'This type does not belong to this sport.',
      smatchy_account_missing: 'The Smatchy account does not exist on this server yet.',
      event_not_found: 'This outing no longer exists, or it is already cancelled.',
      partner_event_cancel_failed: 'The outing could not be cancelled. Try again, or look at it in the activities.',
      partner_event_not_found: 'This outing is not a partner event.',
      partner_event_places_below_registered: 'There cannot be fewer places than people registered.',
      unknown: 'It could not be saved.',
    },
  },
  gamificationSettings: {
    title: 'Gamification settings',
    subtitle: 'Applied without a new version of the API.',
    loading: 'Loading…',
    failed: 'The settings could not be loaded.',
    presence: {
      title: 'Presence at an outing',
      what: "The greatest distance between a participant's position and the place of the outing, start or finish, for their presence to be verified.",
      radius: 'Radius',
      walk: 'About {{minutes}} min on foot.',
      bounds: 'Between {{min}} and {{max}} m. Default: {{fallback}} m.',
      counts:
        'Only the referral rewards require a verified presence. The streak, the badges and the XP count without it.',
      when: 'A change applies to the presences decided after it is saved. A presence already verified stays verified.',
    },
    save: 'Save',
    saved: 'Saved.',
    refused: 'The setting could not be saved.',
    changed: 'Changed on {{day}} by {{who}}.',
    default: 'Default value, never changed.',
  },
  collusionCases: {
    title: 'Pairs set aside',
    subtitle:
      'Two members whose outings look like XP farmed off each other. While a case waits, the XP of the outings they shared counts for no league.',
    loading: 'Loading the cases...',
    failed: 'The cases could not be loaded. Try again in a moment.',
    none: 'Nothing here.',
    people: 'Members',
    signalsTitle: 'Why',
    outings: 'Outings set aside',
    when: 'Spotted',
    decision: 'Decision',
    dismiss: 'Dismiss',
    cancelXp: 'Cancel the XP',
    confirm: 'Confirm',
    dismissHelp: 'Dismiss: it was fine. The XP comes back and the pair is not looked at again.',
    cancelXpHelp:
      'Cancel the XP: the XP of those outings is set aside for good, with every bonus they brought: the week’s +20, the reliability +25 and the duo bonus, the later duo weeks being counted again. Streak and badges already earned are left as they are.',
    confirmHelp:
      'Confirm: the XP is set aside for good with every bonus the outings brought (the week’s +20, the reliability +25, the duo bonus, the later duo weeks counted again), and the outings are taken back from the streak and the counters, closed weeks included. A badge they alone earned is taken back with its XP.',
    nobodyIsTold: 'Neither member is told what you decide here.',
    areYouSure: {
      cancel_xp: 'Set aside the XP and the bonuses of these outings for good? This cannot be undone.',
      confirm:
        'Take these outings away from both members: XP, bonuses, streak, counters and the badges they alone earned? This cannot be undone.',
    },
    filters: {
      open: 'Waiting',
      dismissed: 'Dismissed',
      confirmed: 'Confirmed',
      cancelled: 'XP cancelled',
      all: 'Everything',
    },
    statuses: {
      open: 'Waiting',
      dismissed: 'Dismissed',
      confirmed: 'Confirmed',
      cancelled: 'XP cancelled',
    },
    signals: {
      always_together: 'Nearly every outing of each includes the other',
      tiny_and_frequent: 'Small outings, often, with no verified position',
      recent_accounts: 'Both accounts are recent',
      same_place_same_time: 'Same place, same weekday and hour, again and again',
    },
  },
  groupReview: {
    title: 'Group review',
    subtitle: 'Brand safety: every new group lands here. It stays live while you look; refusing one takes it down.',
    waiting: '{{count}} group(s) waiting for a decision',
    filters: {
      pending: 'Pending',
      info_requested: 'Info requested',
      approved: 'Approved',
      refused: 'Refused',
      all: 'All',
    },
    statuses: {
      pending: 'Pending',
      approved: 'Approved',
      info_requested: 'Info requested',
      refused: 'Refused',
    },
    flags: {
      coarse: 'Coarse word',
      brand: 'Brand name',
    },
    by: 'by',
    sportUnknown: 'Sport not set',
    descriptionEmpty: 'No description',
    reasonGiven: 'Reason given:',
    reasonLabel: 'Reason (shown to the creator on a refusal or an information request)',
    reasonPlaceholder: 'Why it is refused, or what is missing…',
    approve: 'Approve',
    requestInfo: 'Request info',
    refuse: 'Refuse',
    loading: 'Loading…',
    failed: 'Could not load the review queue.',
    none: 'Nothing to review here.',
    footnote:
      'A group stays visible and joinable while under review. Only a refusal takes it down, and the creator is shown the reason. Approving clears any earlier reason.',
  },

  memberReports: {
    messageQuoted: 'Message reported, as it read then',
    reviewQuoted: 'Review reported, as it read then',
    reviewHandledEffect:
      'Closing as handled removes the words of this review from the profile, for everybody. Its stars stay in the score.',
    title: 'Moderation',
    subtitle: 'Reported members, groups and activities, waiting for somebody to look.',
    waiting: '{{count}} waiting',
    loading: 'Loading the reports...',
    failed: 'The reports could not be loaded. Try again in a moment.',
    none: 'Nothing here.',
    type: 'Type',
    reported: 'Reported',
    by: 'By',
    reason: 'Reason',
    when: 'Received',
    decision: 'Decision',
    markHandled: 'Handled',
    markDismissed: 'Dismissed',
    nobodyIsTold:
      'Nobody is told what you decide here. Whoever or whatever was reported never learns of it, and the person who sent the report is only told that it arrived.',
    types: {
      member: 'Member',
      group: 'Group',
      activity: 'Activity',
    },
    typeFilters: {
      all: 'All types',
      member: 'Members',
      group: 'Groups',
      activity: 'Activities',
    },
    filters: {
      pending: 'Waiting',
      handled: 'Handled',
      dismissed: 'Dismissed',
      all: 'Everything',
    },
    statuses: {
      pending: 'Waiting',
      handled: 'Handled',
      dismissed: 'Dismissed',
    },
    reasons: {
      hate: 'Hate speech or discrimination',
      harassment: 'Harassment or intimidation',
      sexual: 'Sexual content',
      impersonation: 'Impersonation, fake profile',
      scam: 'Scam, spam or advertising',
      danger: 'Dangerous behaviour during an outing',
      minor: 'Account that appears to belong to a minor',
      other: 'Something else',
    },
  },
  blockedPayouts: {
    title: 'Money that cannot be paid out',
    subtitle:
      'Organisers holding earnings Stripe refuses to transfer. Escrow included. Until this screen, the only trace was a nightly error report.',
    loading: 'Loading…',
    failed: 'The list could not be loaded.',
    none: 'Nobody is stuck. Every organiser holding money can receive it.',
    total: '{{amount}} € held and unpayable',
    unfinished: 'Account created, never finished',
    unfinishedHelp: 'They can complete it themselves today, and they have been told once since 20 September 2026.',
    neverStarted: 'No Stripe account at all',
    neverStartedHelp: 'They never started. A conversation rather than a reminder.',
    organizer: 'Organiser',
    amount: 'Held',
    account: 'Stripe account',
    told: 'Told on',
    notTold: 'Never told',
  },
  needingHelp: {
    yourMove: 'Your move:',
    openManagement: 'open activities management',
    openCities: 'open cities to activate',
    daysShort: 'D−{{days}}',
    recurring: 'recurring',
    nearby: 'Within {{radius}} km',
    inSport: 'In this sport',
    empty: 'Every upcoming activity has at least one participant. Nothing to rescue today.',
    case: {
      audienceReady: {
        name: 'Audience present, not reached',
        why: 'People of the right sport live within reach. The activity simply has not found its crowd yet: the cheapest save on this page.',
        action: 'Tell the organiser to share it, and consider a targeted reminder to the sport folks nearby.',
        reading:
          '{{inSport}} people of this sport within reach have not joined, and the activity starts in {{days}} day(s). Reaching them is the most profitable move on this page.',
      },
      fewInSport: {
        name: 'Thin audience',
        why: 'A handful of people declare this sport nearby. Widening the target or refiling the sport are both plausible.',
        action: 'Check whether the title and sport say what really happens there, and widen the wording if they do.',
        reading:
          '{{nearby}} people around, only {{inSport}} in this sport. A broader title or a neighbouring sport could multiply the audience before D-day.',
      },
      invisible: {
        name: 'Invisible to its audience',
        why: 'People are around, none declare the sport it is filed under. A listing problem, not a demand problem.',
        action: 'Fix the sport or the title with the organiser so the right people can see it.',
        reading:
          '{{nearby}} people around and 0 in this sport: the activity is probably filed under the wrong sport, or its sport does not exist here. Fix the listing.',
      },
      desert: {
        name: 'Empty area',
        why: 'Nobody within reach, whatever the sport. An acquisition problem, not a matching one.',
        action: 'Mark the area as an acquisition target and warn the organiser about the context.',
        reading:
          'Only {{nearby}} people within reach: the app cannot fill this one. It will live on the organiser word of mouth; the area is an acquisition target.',
      },
      unknownLocation: {
        name: 'No usable position',
        why: 'The activity carries no coordinates, so nothing can be measured around it.',
        action: 'Have the location fixed; every diagnosis follows from it.',
        reading: 'No position on this activity: fix the location first, everything else follows.',
      },
    },
    title: 'Activities in trouble',
    subtitle: 'Upcoming activities the organiser is still alone on, and why',
    total: 'Activities',
    distinctOwners: 'Organisers to contact',
    activity: 'Activity',
    place: 'Place',
    daysUntilStart: 'Days left',
    nearbyUsers: 'People nearby',
    nearbySameSport: 'In this sport',
    diagnosisColumn: 'Reading',
    recurringHint: 'One occurrence of a repeating series. Several rows can be the same problem.',
    diagnosis: {
      unknownLocation: 'No location',
      noOneAround: 'Nobody nearby',
      wrongSport: 'Nobody in this sport',
      audienceExists: 'Audience is there',
    },
    note:
      'Two counts per activity, both within {{radius}} km: everyone, and those who declared the sport it is ' +
      'filed under. People around but none in the sport means the activity is invisible to those who would ' +
      'want it, usually because the sport it is filed under is not really its own. Nobody around at all is a ' +
      'question of how many members there are locally, not of matching.',
    none: 'No upcoming activity is short of people',
    loadFailed: 'Could not load the activities',
  },
  fillRate: {
    title: 'Activity fill rate',
    subtitle: 'How many of the seats offered to others actually get taken',
    activities: 'Activities',
    neverJoined: 'Nobody joined',
    averageAttendees: 'People per activity',
    adjustedFillRate: 'Seats filled',
    sport: 'Sport',
    reference: 'Sport capacity',
    declared: 'Typed capacity',
    note:
      'The rate counts the seats offered to others, so an activity where the organiser stayed alone scores zero ' +
      'whatever the sport. Capacity comes from each sport definition rather than from what organisers typed, ' +
      'because half the activities carry the form default of 21.',
    noReference: 'No sport definition',
    scope: 'Which activities',
    vsLastMonth: 'vs the month before, full months',
    boardTitle: 'Month-by-month evolution',
    boardNote:
      'Twelve full months, each rate recomputed from that month raw counts. Each curve lives on its own scale; the dot marks the last full month.',
    board: {
      activities: 'Activities held',
      neverJoined: 'Share nobody joined',
      partners: 'Partners per activity',
      declared: 'Declared fill rate',
    },
    scopeAll: 'All',
    scopePast: 'Past',
    scopeUpcoming: 'Upcoming',
    upcomingNote:
      'Upcoming activities have not happened yet, so a high never-joined share here is a normal lead time ' +
      'rather than a failure. Past activities are where the figure means something.',
    loadFailed: 'Could not load the fill rate',
  },
  userActivity: {
    title: 'User activity',
    subtitle: 'Who actually does something, against the background of who merely exists',
    loadFailed: 'The counters could not be loaded. The rest of the page stays up.',
    registered: 'Registered profiles',
    engaged: 'Active over the last {{days}} days',
    engagedShare: 'of the base',
    inactiveShare: '{{share}}% did nothing over the window',
    averagePerActive: '≈ {{count}} activities per active member',
    shareOfAll: '{{share}}% of all activities',
    perGroup: '≈ {{count}} activities per group',
    activesGrowth: 'vs the previous window',
    creationsGrowth: 'creations vs the month before, full months',
    heldGrowth: 'activities held vs the month before, full months',
    groupsGrowth: 'groups created vs the month before, full months',
    signupsGrowth: 'sign-ups, last full month vs the one before',
    upcoming: 'Upcoming activities',
    past: 'Past activities',
    groups: 'Groups',
    engagedTitle: 'The people who act',
    engagedNote:
      'Members who created or joined at least one activity over the window, the busiest first. A row opens the member.',
    period: 'Window',
    periodDays: '{{days}} days',
    member: 'Member',
    activityCount: 'Activities',
    lastActivity: 'Last activity',
    empty: 'Nobody acted over this window.',
  },
  synthesis: {
    paymentAlerts: {
      title: 'Payment alerts',
      allQuiet:
        'Nothing calls for action: no pending withdrawal, every positive balance has a verified bank account, none is stale.',
      neverUsed:
        'Note: no withdrawal request has ever been made, so the zero above means untested rather than healthy.',
      pendingPayouts: '{{count}} withdrawal request(s) waiting, the oldest for {{days}} day(s)',
      withoutBank: '{{count}} organiser(s) hold {{euros}} \u20ac with no verified bank account: it cannot be paid out',
      stale: '{{count}} positive balance(s) untouched for over ninety days',
    },
    title: 'Summary',
    subtitle: 'Is it working, where does it break, who to talk to, what to do',
    vsLastMonth: 'vs the month before, full months',
    users: 'Registered profiles',
    activities: 'Activities created',
    neverJoined: 'of activities with no participants',
    dormant: 'of profiles with no activity',
    funnelTitle: 'From signing up to actually meeting someone',
    funnelNote:
      'Each stage counts distinct people, so they are comparable. The right-hand figure is the share of the ' +
      'stage just before, which is where the drop-off shows: a stage can look small only because everything ' +
      'upstream is.',
    worstStep: 'The step losing the most people:',
    stage: {
      signedUp: 'Signed up',
      matchable: 'Profile matchable',
      involved: 'Created or joined',
      joinedSomeoneElse: 'Joined someone else',
      paid: 'Paid',
    },
    sportsTitle: 'Where matching fails most',
    sportsNote: 'Sports ordered by the share of activities nobody ever joined, worst first.',
    seeFillRate: 'See every sport',
    leverTitle: 'The shortest lever',
    leverNote:
      'People who run a group but never created a single activity. Running a group multiplies the odds of ' +
      'organising by 19.7, where a complete profile only multiplies them by 1.5.',
    leverCount: 'group leaders have never created an activity',
    seeSegments: 'See the shortlist',
    seeTodo: 'What to do about it',
    loadFailed: 'Some figures could not be loaded',
  },
  segments: {
    export: 'Export',
    exporting: 'Exporting\u2026',
    title: 'User segments',
    subtitle: 'The base split by what people have actually done',
    breakdown: 'Breakdown',
    note:
      'Recency counts creating or joining an activity, never logging in: that field only started being ' +
      'recorded in July 2026, so its absence says nothing. People who never acted are kept apart from those ' +
      'who went quiet, because bringing someone back and getting someone started are different messages.',
    exportFailed: 'The export failed to download. The environment may not serve it yet.',
    totalShort: 'profiles counted',
    total: 'Profiles counted:',
    candidatesTitle: 'Most likely to start organising',
    candidatesNote:
      'People who run a group but never created a single activity. Running a group multiplies the odds of ' +
      'organising by 19.7, where a complete profile only multiplies them by 1.5.',
    outOf: 'of',
    groupLeaders: 'group leaders have never created an activity',
    minGroups: 'Groups led, at least',
    groupsLed: 'Groups led',
    name: 'Name',
    searchPlaceholder: 'First or last name',
    profileGone: 'Profile no longer available',
    truncated: 'More candidates exist than were loaded: the filters below work on the loaded ones only.',
    loadFailed: 'Could not load the segments',
    key: {
      hot: 'Hot, under a month',
      warm: 'Warm, one to three months',
      cool: 'Cool, three to twelve months',
      frozen: 'Frozen, over a year',
      groupOnly: 'In a group only',
      untouched: 'Never acted',
    },
  },
  common: {
    all: 'All',
    search: 'Search',
    reset: 'Reset',
    filters: 'Filters',
    noData: 'No data',
    loading: 'Loading',
    export: 'Export',
    sport: 'Sport',
    city: 'City',
    gender: 'Gender',
    period: 'Period',
  },
} as const;

export const FR = {
  statusLabels: {
    inEscrow: 'Sous séquestre',
    available: 'Disponible',
    noParticipants: 'Aucun participant',
    paid: 'Payé',
    refunded: 'Remboursé',
    failed: 'Échoué',
    ineligible: 'Non éligible',
    eligibleForPayout: 'Éligible au versement',
    pendingEligibility: 'Éligibilité en attente',
    revoked: 'Révoqué',
    completed: 'Terminé',
    pending: 'En attente',
    expired: 'Expiré',
    declined: 'Refusé',
    awaitingTransfer: 'En attente de virement',
    approved: 'Approuvé',
    inTransit: 'En transit',
    canceled: 'Annulé',
    cancelled: 'Annulé',
    rejected: 'Rejeté',
    resubmitted: 'Re-soumis',
    transferred: 'Viré',
    incoming: 'Entrant',
    waitingApproval: "En attente d'approbation",
    connected: 'Connecté',
    notStarted: 'Non démarré',
    accessRevoked: 'Accès révoqué',
    infoRequested: 'Infos demandées',
    insuranceExpired: 'Assurance expirée',
    active: 'Actif',
  },
  auditLogs: {
    relativeTime: {
      justNow: 'à l’instant',
      inAMoment: 'dans un instant',
      minutesAgo: 'il y a {{n}} min',
      inMinutes: 'dans {{n}} min',
      hoursAgo: 'il y a {{n}} h',
      inHours: 'dans {{n}} h',
      daysAgo: 'il y a {{n}} j',
      inDays: 'dans {{n}} j',
      monthsAgo: 'il y a {{n}} mois',
      inMonths: 'dans {{n}} mois',
      yearsAgo: 'il y a {{n}} an(s)',
      inYears: 'dans {{n}} an(s)',
    },
    scopeAll: 'Tous les périmètres',
    transaction: 'Transaction',
    wallet: 'Portefeuille',
    platform: 'Plateforme',
    adminAccess: 'Accès admin',
    organizerAccess: 'Accès organisateur (espace pro)',
    system: 'Système',
    actionAll: 'Toutes les actions',
    depositCharged: 'Dépôt prélevé',
    depositRefunded: 'Dépôt remboursé',
    depositReleased: 'Dépôt libéré',
    depositCreated: 'Dépôt créé',
    depositFailed: 'Dépôt échoué',
    escrowHold: 'Mise sous séquestre',
    escrowRelease: 'Libération du séquestre',
    payoutInitiated: 'Versement initié',
    payoutCompleted: 'Versement terminé',
    payoutPaid: 'Versement payé',
    payoutFailed: 'Versement échoué',
    payoutCanceled: 'Versement annulé',
    connectAccountCreated: 'Compte Connect créé',
    bankVerificationChanged: 'Vérification bancaire modifiée',
    transferConfirmed: 'Virement confirmé',
    earningsRecorded: 'Gains enregistrés',
    balanceSync: 'Synchronisation du solde',
    balanceAdjustment: 'Ajustement du solde',
    adminRead: 'Lecture admin',
    organizerRead: 'Lecture organisateur (personnes d’une sortie)',
    organizerMessage: 'Message organisateur (aux personnes d’une sortie)',
    adminWrite: 'Écriture admin',
    adminDelete: 'Suppression admin',
    systemCron: 'Tâche planifiée',
    systemReconciliation: 'Réconciliation système',
    actorAll: "Tous les types d'acteur",
    user: 'Utilisateur',
    admin: 'Admin',
    webhook: 'Webhook',
    colTimestamp: 'Horodatage',
    colScope: 'Périmètre',
    colAction: 'Action',
    colActorType: "Type d'acteur",
    colActor: 'Acteur',
    colEntity: 'Entité',
    colAmount: 'Montant',
  },
  menu: {
    steering: 'PILOTAGE',
    users: 'UTILISATEURS',
    activities: 'ACTIVITÉS',
    payments: 'PAIEMENTS',
    compliance: 'CONFORMITÉ',
    administration: 'ADMINISTRATION',
    gamification: 'GAMIFICATION',
    finals: 'Compétitions',
    gamificationSettings: 'Réglages',
    brands: 'Marques partenaires',
    partnerEvent: 'Nouvel événement partenaire',
    partnerEvents: 'Événements partenaires',

    synthesis: 'Synthèse',
    studio: 'Studio marketing',
    todo: 'À traiter',
    userActivity: 'Activité des utilisateurs',
    userAnalytics: 'Analyse des utilisateurs',
    segments: 'Segments utilisateurs',
    fillRate: 'Remplissage des activités',
    acquisition: 'Acquisition',
    activation: 'Qualit\u00e9 des profils',
    matching: 'Appariement',
    journeyFunnel: 'Parcours',
    prizeDraw: 'Jeu-concours',
    needingHelp: 'Activités en difficulté',
    crossAnalysis: 'Analyse crois\u00e9e',
    citiesToActivate: 'Villes \u00e0 activer',
    duplicateCities: 'Villes en doublon',
    stores: 'Stores',
    heatmap: 'Carte de chaleur',
    eventRevenue: 'Revenus des activités',
    monetisation: 'Monétisation',
    userLists: 'Liste des utilisateurs',
    usersManagement: 'Gestion des utilisateurs',
    groups: 'Groupes',
    futureActivities: 'Activités à venir',
    pastActivities: 'Activités passées',
    activitiesManagement: 'Gestion des activités',
    paidEventAccess: 'Accès aux activités payantes',
    transactions: 'Transactions',
    withdrawalRequest: 'Demandes de retrait',
    organizerBalances: 'Soldes des organisateurs',
    clientWallets: 'Portefeuilles clients',
    blockedPayouts: 'Argent bloqué',
    memberReports: 'Modération',
    collusionCases: 'Duos mis de côté',
    groupReview: 'Validation des groupes',
    earningsStatement: 'Relevé de gains',
    commissionsRate: 'Taux de commission',
    termsContent: 'Contenu des CGV',
    termsAcceptances: 'Acceptations des CGV',
    auditLogs: "Journal d'audit",
    deletedItems: 'Éléments supprimés',
    accessManagement: 'Gestion des accès',
  },
  accessMgmt: {
    title: 'Accès au back-office',
    subtitle: 'Qui peut accéder au back-office, et à quel niveau.',
    grantTitle: 'Accorder ou modifier un accès',
    emailPlaceholder: 'Email du compte',
    levelAdmin: 'Accès complet',
    levelReadonly: 'Lecture seule',
    grantCta: 'Accorder',
    granting: 'En cours…',
    grantHint:
      'Le compte doit déjà exister dans l’app. L’accès complet peut tout faire ; la lecture seule voit tous les écrans mais ne modifie rien.',
    granted: 'Accès mis à jour.',
    listTitle: 'Accès actuels',
    loadFailed: 'Impossible de charger la liste des accès.',
    colUser: 'Compte',
    colLevel: 'Niveau',
    colActions: 'Actions',
    noName: 'Sans nom',
    level_admin: 'Accès complet',
    level_readonly: 'Lecture seule',
    level_superadmin: 'Superadmin',
    you: '(toi)',
    managedByEnv: 'Géré par l’environnement',
    makeAdmin: 'Accès complet',
    makeReadonly: 'Lecture seule',
    revoke: 'Révoquer',
    empty: 'Aucun compte back-office pour l’instant.',
    errorNotFound: 'Aucun compte trouvé pour cet email.',
    errorSuperadmin: 'Un superadmin est géré par l’environnement et ne peut pas être modifié ici.',
    errorSelf: 'Tu ne peux pas révoquer ton propre accès.',
    errorGeneric: 'La modification n’a pas pu être appliquée.',
  },
  login: {
    title: 'Bienvenue !',
    subtitle: 'Connectez-vous pour continuer',
    email: 'Adresse électronique',
    emailPlaceholder: 'Adresse électronique',
    password: 'Mot de passe',
    passwordPlaceholder: 'Mot de passe',
    submit: 'Se connecter',
    googleAlt: 'Se connecter avec Google',
    logoAlt: 'Logo Smatchy',
  },
  topbar: {
    calendar: 'Calendrier',
    profile: 'Profil',
    language: 'Langue',
    switchToFrench: 'Passer en français',
    synthese: 'Synthèse',
    todo: 'À traiter',
    studio: 'Studio',
    switchToEnglish: 'Passer en anglais',
  },
  profileMenu: {
    open: 'Ouvrir le menu profil',
    admin: 'Administratrice',
    noEmail: 'Email inconnu',
    changePassword: 'Changer mon mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    confirmPassword: 'Confirmer le nouveau mot de passe',
    pwdTooShort: 'Au moins 6 caractères.',
    pwdMismatch: 'Les deux mots de passe ne correspondent pas.',
    cancel: 'Annuler',
    save: 'Mettre à jour',
    saving: 'Mise à jour…',
    providerNote: 'Tu es connectée via un compte externe (Google ou Facebook) ; gère ton mot de passe là-bas.',
    pwdSuccess: 'Mot de passe mis à jour.',
    pwdErrorWrong: 'Le mot de passe actuel est incorrect.',
    pwdErrorWeak: 'Le nouveau mot de passe est trop faible.',
    pwdErrorTooMany: 'Trop de tentatives, réessaie plus tard.',
    pwdErrorGeneric: 'Le mot de passe n’a pas pu être modifié.',
    logout: 'Se déconnecter',
  },
  todo: {
    attentionGo: 'Traiter',
    vatStatusMissing: {
      title: '{{count}} pro(s) sans statut de TVA',
      hint: "Leurs retraits attendent qu'ils l'indiquent dans l'appli (Profil, Mon portefeuille). Montant en attente pour chacun :",
      asked: "demande envoyée dans l'appli",
      reminded: 'relancé',
    },
    attention: {
      pro_requests: "{{count}} demande(s) d'accès aux activités payantes en attente de ta décision",
      payouts: '{{count}} demande(s) de retrait à approuver ou refuser',
      member_reports: '{{count}} membre(s) signalé(s), à regarder',
      groups_to_review: '{{count}} groupe(s) en attente de validation',
    },

    liveBadge: 'chiffre du jour',
    live: {
      paymentAlerts: {
        title: "Traiter l'argent en attente",
        finding:
          '{{pending}} demande(s) de retrait en attente et {{withoutBank}} solde(s) positif(s) sans compte bancaire vérifié, en ce moment même.',
        action:
          "Ouvrir l'écran des soldes et traiter chaque ligne ; l'argent qui attend érode la confiance des organisateurs plus vite que tout.",
      },
      sportMissing: {
        title: 'Relancer les {{count}} profils à qui il ne manque que le sport',
        finding:
          "{{count}} profils ont tout rempli sauf le sport, aujourd'hui. Un seul champ les sépare d'être appariables.",
        action: 'Lancer une campagne push « choisis ton sport ». Gain potentiel : +{{gain}} % de profils appariables.',
      },
      needingHelp: {
        title: "Sauver les {{count}} activités où l'organisateur est seul",
        finding: "{{count}} activités à venir n'ont encore aucun participant, au moment de ce chargement.",
        action: "Ouvrir l'écran des activités en difficulté et traiter le diagnostic de chacune.",
      },
      organiserCandidates: {
        title: "Parler aux {{count}} chefs de groupe qui n'ont jamais organisé",
        finding:
          "{{count}} personnes mènent un groupe aujourd'hui sans avoir créé une seule activité ; mener un groupe multiplie par 19,7 la probabilité d'organiser.",
        action: "Ouvrir l'écran des segments et contacter la liste des candidats, les plus solides d'abord.",
      },
      noOfferCities: {
        title: "Créer de l'offre dans les {{count}} villes qui n'en ont pas",
        finding:
          '{{count}} villes comptent des membres et presque aucune activité ; la plus grosse est {{city}} avec {{profiles}} profils.',
        action: 'Ouvrir les villes à activer et y recruter ou amorcer des organisateurs.',
      },
      duplicateCities: {
        title: 'Nettoyer les orthographes de villes en doublon',
        finding: 'Une recherche exacte rate en ce moment {{missed}} profils parce que leur ville est écrite autrement.',
        action: 'Ouvrir les villes en doublon et dérouler les fusions suggérées.',
      },
    },
    title: 'À traiter',
    subtitle: 'Actions recommandées, mesurées sur les données de production',
    openCount: 'en cours',
    finding: 'Ce que disent les chiffres',
    action: 'Ce qu’il faut faire',
    done: 'Fait',
    openInAsana: 'Ouvrir dans Asana',
    archivedNote: 'action(s) cochée(s) précédemment ont été archivées',
    empty: 'Plus rien à traiter ici',
    loadFailed: 'Impossible de charger les actions traitées',
    category: {
      security: 'Sécurité',
      marketing: 'Marketing',
      product: 'Produit',
      finance: 'Finance',
      data: 'Données',
      compliance: 'Conformité',
    },
    priority: {
      critical: 'Critique',
      high: 'Élevée',
      medium: 'Moyenne',
    },
  },
  heatmap: {
    title: 'Carte de chaleur',
    subtitle: 'Où sont les membres, où sont les activités, et où les deux ne se rencontrent jamais',
    profilesWithoutActivity: 'Membres sans rien près de chez eux',
    activitiesWithoutProfile: 'Activités tenues sans membre à proximité',
    profilesPlaced: 'Membres placés',
    activitiesPlaced: 'Activités placées',
    layer: 'Couche',
    layerGap: "L'écart",
    layerProfiles: 'Membres',
    layerActivities: 'Activités',
    members: 'Membres',
    activities: 'Activités',
    cell: 'Cellule',
    city: 'Ville',
    country: 'Pays',
    recommendSports: 'Sports à recommander',
    noDeclaredSports: "aucun sport déclaré sur place pour l'instant",
    cityPending: 'Recherche…',
    worstGapsTitle: "Là où le plus de membres n'ont rien à rejoindre",
    svgLabel: 'Densité des membres et des activités, un point par cellule',
    computedAt: "Calculé le {{date}}. Recalculé une fois par nuit, donc ces chiffres peuvent avoir jusqu'à un jour.",
    note:
      "Un point par cellule d'environ {{km}} km. C'est la surface qui porte le nombre, pas la largeur. La " +
      "couche « écart » n'affiche que les cellules où des membres habitent et où aucune activité n'a jamais " +
      "eu lieu, c'est-à-dire le cas sur lequel agir. Aucune position ne quitte la base : la carte est " +
      'dessinée à partir de comptes par cellule.',
    outside:
      '{{count}} zones actives sont hors du cadre initial : elles sont bien sur la carte, déplace-la pour les voir.',
    loadFailed: 'Impossible de charger la carte',
  },
  stores: {
    title: 'Stores',
    subtitle: "Ce que l'App Store et Google Play disent de l'application",
    downloadsTitle: 'Installations',
    reviewsTitle: 'Avis et notes',
    iosInstalls: 'Installations iOS',
    androidInstalls: 'Installations Android',
    activeDevices: "Appareils Android portant l'app",
    daysReported: 'Jours renseignés',
    average: 'Note moyenne',
    reviewCount: 'Avis',
    negative: 'Trois étoiles ou moins',
    territories: 'Pays',
    androidPending:
      "Google Play n'est pas encore connecté. Les rapports existent et la permission est accordée, mais " +
      "l'accès est encore en cours de propagation du côté de Google.",
    appleNotConfigured:
      "Les identifiants App Store ne sont pas renseignés sur cet environnement, il n'y a donc rien à lire.",
    downloadsNote:
      "Uniquement les premières installations, donc le chiffre mesure l'acquisition et non l'activité : les " +
      'mises à jour et les réinstallations sont exclues. Apple publie avec un jour ou deux de retard, et un ' +
      "jour absent signifie qu'aucun rapport n'existe encore, pas que personne n'a installé.",
    averageNote:
      'La moyenne est calculée sur les avis affichés ici, pas sur la note publique du store, qui compte aussi ' +
      "les notes laissées sans avis. Les deux chiffres n'ont pas à coïncider.",
    androidRating: 'Note Google Play',
    androidRatingCount: 'Notes Google Play',
    androidRatingManual:
      "Saisie à la main, relevée sur la fiche Play le {{asOf}}. Le chiffre de l'export groupé contredit la " +
      "fiche en ligne et l'API Play n'est pas encore accessible, donc cette valeur est tenue à jour manuellement.",
    androidRatingManualNoDate:
      "Saisie à la main depuis la fiche Play. Le chiffre de l'export groupé contredit la fiche en ligne et " +
      "l'API Play n'est pas encore accessible, donc cette valeur est tenue à jour manuellement.",
    noDays: "Aucun jour de la période n'a encore de rapport publié",
    noReviews: "Personne n'a encore laissé d'avis",
    acquisitionTitle: "D'o\u00f9 viennent les installations",
    acquisitionPending:
      "Les rapports d\u00e9taill\u00e9s existent mais n'ont pas encore produit de donn\u00e9es : ils n'ont d\u00e9marr\u00e9 que le " +
      "14 ao\u00fbt 2026. Ce n'est pas z\u00e9ro installation, c'est Apple qui n'a pas encore publi\u00e9.",
    newUsers: 'Nouveaux utilisateurs',
    redownloads: 'Ret\u00e9l\u00e9chargements et mises \u00e0 jour',
    deletions: 'D\u00e9sinstallations',
    netInstalls: 'Solde',
    bySource: 'Par source',
    byVersion: 'Par version',
    byDevice: 'Par appareil',
    byTerritory: 'Par pays',
    acquisitionNote:
      'Les nouveaux utilisateurs ne comptent que les premiers t\u00e9l\u00e9chargements : compter les ret\u00e9l\u00e9chargements et ' +
      "les mises \u00e0 jour transformerait chaque sortie de version en faux pic d'acquisition. Les d\u00e9sinstallations " +
      "sont affich\u00e9es \u00e0 part plut\u00f4t que fondues dans un solde, parce que gagner dix et perdre six n'est pas le " +
      'm\u00eame produit que gagner quatre.',
    acquisitionFailed: "Impossible de charger le d\u00e9tail d'acquisition",
    listingTitle: 'Fiche Play Store',
    listingVisitors: 'Visiteurs de la fiche',
    listingAcquisitions: 'Ont install\u00e9 depuis',
    listingConversion: 'Conversion',
    listingNote:
      'Sur les {{days}} jours publi\u00e9s par Play. La conversion est recalcul\u00e9e depuis les visiteurs et les ' +
      "installations, jamais moyenn\u00e9e depuis les taux quotidiens. Apple n'a pas d'\u00e9quivalent tant que ses " +
      "rapports d'engagement ne produisent pas.",
    crashNote: '{{count}} crash(s) sur les six derniers mois publi\u00e9s, le dernier le {{day}}.',
    noCrashNote: 'Aucun crash sur les six derniers mois publi\u00e9s.',
    listingFailed: 'Impossible de charger la fiche',
    downloadsFailed: 'Impossible de charger les installations',
    reviewsFailed: 'Impossible de charger les avis',
    minimumTitle: "Version minimale de l'app",
    minimumIntro:
      "Une version plus ancienne affiche un écran qui oblige à mettre à jour avant d'ouvrir l'app. À 0.0.0, personne n'est bloqué. Si notre serveur ne répond pas, l'app s'ouvre normalement.",
    minimumCurrent: { ios: 'iOS, minimum actuel', android: 'Android, minimum actuel' },
    minimumPlaceholder: 'ex. 2.2.8',
    minimumSave: 'Enregistrer',
    minimumWarning:
      "Montez une valeur seulement quand cette version est en ligne sur le store concerné : sinon l'écran envoie vers une mise à jour qui n'existe pas encore.",
    minimumInvalid: 'Écrivez la version en trois nombres, par exemple 2.2.8.',
    minimumFailed: "La version minimale n'a pas pu être lue.",
    minimumSaveFailed: "L'enregistrement a échoué, rien n'a changé.",
    platform: { ios: 'iOS', android: 'Android' },
    minimumConfirmTitle: 'Bloquer les versions {{platform}} plus anciennes que {{version}} ?',
    minimumConfirmBody: {
      ios: "Les personnes sur une version plus ancienne ne pourront plus ouvrir l'app sans la mettre à jour depuis l'App Store.",
      android:
        "Les personnes sur une version plus ancienne ne pourront plus ouvrir l'app sans la mettre à jour depuis Google Play.",
    },
    minimumUnblockTitle: 'Ne plus bloquer aucune version {{platform}} ?',
    minimumUnblockBody: "Toutes les versions pourront de nouveau ouvrir l'app.",
    minimumCancel: 'Annuler',
    minimumConfirm: 'Confirmer',
  },
  crossAnalysis: {
    title: 'Analyse crois\u00e9e',
    subtitle: "Quel sport marche dans quelle ville et sur quelle dur\u00e9e, plut\u00f4t qu'une moyenne nationale",
    groupBy: 'Grouper par',
    all: 'Toutes',
    dimension: {
      sport: 'Sport',
      city: 'Ville',
      duration: 'Dur\u00e9e',
    },
    duration: {
      upTo45: '45 min ou moins',
      h1: '1 h',
      h1x5: '1 h 30',
      h2: '2 h',
      h3: '3 h',
      over3: 'Plus de 3 h',
      unknown: 'Non renseign\u00e9e',
    },
    activities: 'Activit\u00e9s',
    joined: 'Rejointes',
    joinedShare: 'Part rejointe',
    other: 'Autres, groupes trop petits pour \u00eatre lus seuls',
    counted: '{{filtered}} activit\u00e9s dans cette coupe, sur {{total}} au total',
    note:
      'Le taux est recalcul\u00e9 pour le groupement affich\u00e9, jamais moyenn\u00e9 depuis des taux stock\u00e9s. Les groupes ' +
      'de moins de cinq activit\u00e9s se replient sur une ligne plut\u00f4t que de dispara\u00eetre, pour que le total ' +
      "visible colle toujours \u00e0 l'en-t\u00eate. Vert \u00e0 partir de 40 % de rejointes, orange \u00e0 partir de 20 %, " +
      'rouge en dessous.',
    loadFailed: "Impossible de charger l'analyse crois\u00e9e",
  },
  citiesToActivate: {
    title: 'Villes \u00e0 activer',
    subtitle: "Chaque ville o\u00f9 agir vaut la peine, et quel genre d'action elle appelle",
    status: {
      replicate: 'Mod\u00e8le \u00e0 r\u00e9pliquer',
      dormant: 'Potentiel dormant',
      noOffer: "Manque d'offre",
      watch: '\u00c0 surveiller',
    },
    hint: {
      replicate: '\u00c7a marche ici : copier la recette ailleurs',
      dormant: "Les gens sont l\u00e0, l'activit\u00e9 non : il faut des organisateurs",
      noOffer: 'Des membres et presque rien \u00e0 rejoindre : il faut des activit\u00e9s',
      watch: "Rien n'appelle d'action pour l'instant",
    },
    city: 'Ville',
    profiles: 'Membres',
    withSport: 'Avec un sport',
    activities: 'Activit\u00e9s',
    joined: 'Rejointes',
    upcoming: '\u00c0 venir',
    statusColumn: 'Statut',
    note:
      'Les orthographes sont repli\u00e9es avec la m\u00eame r\u00e8gle que le filtre ville, et une activit\u00e9 compte pour la ' +
      'ville que son adresse nomme. Les villes de moins de {{floor}} membres sont \u00e9cart\u00e9es, {{folded}} en tout : ' +
      'en dessous, chaque taux est du bruit. Clique une carte pour restreindre le tableau \u00e0 ce statut.',
    loadFailed: 'Impossible de charger les villes',
  },
  duplicateCities: {
    title: 'Villes en doublon',
    subtitle: 'Le même lieu enregistré sous plusieurs orthographes',
    distinctSpellings: 'Valeurs écrites',
    distinctPlaces: 'Lieux réels',
    profilesMissed: "Profils qu'une recherche exacte rate",
    missedShare: 'Sur ceux qui ont une ville',
    place: 'Lieu',
    spellings: 'Orthographes',
    profiles: 'Profils',
    missed: 'Ratés',
    showSpellings: 'Voir les orthographes',
    hideSpellings: 'Masquer',
    showAll: 'Afficher les {{count}} autres',
    partOf: 'partie de {{city}} ?',
    partOfHint:
      "Ressemble à un arrondissement d'une ville présente elle aussi. Proposé, pas appliqué : savoir si les " +
      "deux sont le même lieu est une décision sur ce qu'une ville veut dire ici.",
    note:
      'La ville est saisie à la main et jamais vérifiée, donc un même lieu arrive sous plusieurs formes. ' +
      "L'orthographe retenue est simplement la plus fréquente. « Ratés » compte les profils qu'une recherche " +
      "exacte sur leur propre ville ne renverrait pas, et c'est ce chiffre qui dit si le nettoyage vaut la " +
      'peine. Rien sur cet écran ne modifie un profil.',
    none: "Chaque lieu est écrit d'une seule façon",
    loadFailed: 'Impossible de charger les villes',
  },
  acquisition: {
    title: 'Acquisition',
    subtitle: 'Les inscriptions par mois, et ce que chaque cohorte est devenue',
    totalSignups: 'Inscrits, au total',
    totalActivated: 'Ont agi un jour',
    totalShare: 'De toute la base',
    totalWithin7dShare: 'Ont agi sous 7 jours',
    note:
      'La barre grise est la cohorte, la partie color\u00e9e ceux qui ont un jour cr\u00e9\u00e9 ou rejoint une activit\u00e9, ' +
      "le pourcentage est leur rapport. L'activation est mesur\u00e9e \u00e0 date, donc les mois r\u00e9cents paraissent " +
      "pires qu'ils ne finiront : une cohorte continue de m\u00fbrir. Sur la production les deux lignes se " +
      "contredisent bruyamment : les inscriptions ont \u00e9t\u00e9 divis\u00e9es par deux sur l'ann\u00e9e pendant que " +
      "l'activation doublait. Il arrive moins de monde, mais du meilleur.",
    within7dNote:
      'La part \u00ab sous 7 jours \u00bb compte les membres qui ont cr\u00e9\u00e9 ou rejoint une premi\u00e8re activit\u00e9 d\u00e8s leur ' +
      "premi\u00e8re semaine, la fen\u00eatre de la relance d'onboarding. Contrairement \u00e0 la part \u00e0 vie, elle ne " +
      'continue pas de m\u00fbrir : les cohortes se comparent \u00e0 armes \u00e9gales. Rejoindre ne laisse aucun horodatage, ' +
      "donc un \u00ab rejoint \u00bb est dat\u00e9 par l'activit\u00e9 elle-m\u00eame, l'approximation que la relance accepte d\u00e9j\u00e0 ; " +
      'survole un mois pour son chiffre.',
    sourcesNote: "D'o\u00f9 viennent les installations, store par store, se lit sur l'",
    sourcesLink: '\u00e9cran Stores',
    loadFailed: "Impossible de charger l'acquisition",
  },
  activation: {
    title: 'Qualit\u00e9 des profils',
    subtitle: "Ce que les profils contiennent vraiment, et qui est \u00e0 un pas d'\u00eatre appariable",
    onlySportMissing: "\u00c0 un sport d'\u00eatre appariables",
    matchable: "Appariables aujourd'hui",
    onlyPositionMissing: 'Position manquante',
    bothMissing: 'Sport et position manquants',
    blockersNote:
      "L'appariement a besoin d'un sport d\u00e9clar\u00e9 et d'une position. La premi\u00e8re carte est le gain le plus " +
      "court de tout l'entonnoir : ces personnes ont d\u00e9j\u00e0 dit o\u00f9 elles sont, une seule question dans " +
      "l'application les rend appariables.",
    fieldsTitle: 'Champ par champ',
    field: {
      firstName: 'Pr\u00e9nom',
      avatar: 'Photo',
      city: 'Ville',
      position: 'Position',
      dateOfBirth: 'Date de naissance',
      sex: 'Genre',
      sport: 'Sport d\u00e9clar\u00e9',
      push: 'Jeton de notification',
    },
    dead: 'jamais rempli par personne',
    fieldsNote:
      "Compt\u00e9 sur les {{total}} profils vivants. Un champ que personne n'a jamais rempli dit autre chose " +
      "qu'un champ mal rempli : il dit que le produit pose une question \u00e0 laquelle personne ne r\u00e9pond, ou " +
      "qu'il ne la pose jamais vraiment.",
    loadFailed: 'Impossible de charger la qualit\u00e9 des profils',
  },
  prizeDraw: {
    title: 'Jeu-concours',
    subtitle:
      'Tire un gagnant parmi les profils compl\u00e9t\u00e9s \u00e0 100%, de fa\u00e7on reproductible et trac\u00e9e.',
    eligibleLabel: 'Profils \u00e9ligibles (100% compl\u00e9t\u00e9s)',
    cityLabel: 'Restreindre \u00e0 une ville (optionnel)',
    cityPlaceholder: 'ex. Annecy',
    refresh: 'Rafra\u00eechir le compte',
    formTitle: 'Lancer un tirage',
    seedLabel: 'Graine',
    seedHelp: 'Enregistr\u00e9e avec le tirage pour pouvoir le rejouer. G\u00e9n\u00e8re-en une ou saisis la tienne.',
    generateSeed: 'G\u00e9n\u00e9rer',
    winnerCountLabel: 'Gagnants \u00e0 tirer',
    labelLabel: 'Libell\u00e9 du tirage (optionnel)',
    labelPlaceholder: 'ex. Campagne profils octobre',
    run: 'Lancer le tirage',
    running: 'Tirage\u2026',
    noEligible: "Aucun profil n'est \u00e9ligible pour le tirage pour l'instant.",
    resultTitle: 'Gagnant',
    resultTitlePlural: 'Gagnants',
    main: 'Gagnant principal',
    suppleant: 'Suppl\u00e9ant',
    pool: 'Vivier',
    listHash: 'Empreinte de la liste',
    drawnAt: 'Tir\u00e9 le',
    historyTitle: 'Tirages pass\u00e9s',
    historyEmpty: 'Aucun tirage pour le moment.',
    loadFailed: 'Chargement impossible. R\u00e9essaie.',
  },
  activitiesManagement: {
    cancellationsTitle: 'Pourquoi ces activités ont été annulées',
    cancellationsEmpty: 'Aucune annulation sur cette période.',
    cancellationsWithAWord: 'Annulations accompagnées d’un mot pour les personnes inscrites',
    cancellationsTotal: 'Annulations',
    reasonColumn: 'Raison',
    reasonWeather: 'Météo',
    reasonNotEnoughRegistrations: 'Pas assez d’inscriptions',
    reasonOrganiserUnavailable: 'Organisation indisponible',
    reasonVenueUnavailable: 'Lieu indisponible',
    reasonOther: 'Autre',
    reasonNotAsked: 'Aucune raison demandée',
    reasonNotAskedHelp: 'Annulée avant que la question existe, ou sans personne inscrite.',
    title: 'Gestion des activités',
    subtitle: "Créer, corriger, annuler : le guichet d'intervention sur les activités.",
    searchPlaceholder: 'Rechercher une activité ou un groupe',
    filters: 'Filtres',
    exportCsv: 'Export CSV',
    exporting: 'Export en cours',
    clearFilter: 'Effacer les filtres',
    dateRange: 'Plage de dates',
    statusLabel: "Statut de l'activité",
    sportLabel: 'Sport',
    addressContains: "L'adresse contient",
    addressPlaceholder: 'Ville, lieu, pays',
    allStatuses: 'Tous les statuts',
    allSports: 'Tous les sports',
    chipUpcoming: 'À venir',
    chipThisWeek: 'Cette semaine',
    chipWithoutPartners: 'Sans partenaire',
    chipFull: 'Complètes',
    chipWeekly: 'Hebdomadaires',
    partnerSeats: 'Sièges partenaires occupés',
    uniqueParticipants: 'participants uniques',
    upcomingDesc: 'Programmées ou en cours',
    withoutPartnersTitle: 'À venir sans partenaire',
    seeThem: 'Voir ces activités',
    thisMonth: 'ce mois-ci',
    fullMonths: 'mois plein vs précédent',
    vsLastMonth: 'vs mois dernier',
    colActivity: 'ACTIVITÉS',
    colGroup: 'GROUPE',
    colDate: 'DATE ET HEURE',
    colStatus: 'STATUT',
    colFill: 'REMPLISSAGE',
    colActions: 'ACTIONS',
    by: 'par',
    weekly: 'Hebdo',
    empty: 'Aucune activité ne correspond à votre recherche ou vos filtres',
    editTooltip: "Modifier l'activité",
    pageReport: 'Affichage de {first} à {last} sur {totalRecords} résultats',
    fillNote: "Le remplissage compte les places partenaires : une activité de 4 places dont l'organisateur en offre 3.",
  },
  paidEvents: {
    pageTitle: 'Accès aux activités payantes',
    filters: 'Filtres',
    clearFilter: 'Effacer les filtres',
    searchOrganizer: 'Rechercher un organisateur',
    searchOrganizerName: "Rechercher un nom d'organisateur",
    searchParticipantName: 'Rechercher un nom de participant',
    submittedDateRange: 'Période de soumission',
    grantDateRange: "Période d'octroi",
    dateRange: 'Plage de dates',
    roleType: 'Type de rôle',
    allRoleTypes: 'Tous les types de rôle',
    status: 'Statut',
    allStatuses: 'Tous les statuts',
    statusChip: {
      PENDING: 'En attente',
      RESUBMITTED: 'Resoumis',
      REQUESTED_INFO: 'Info demandée',
      INSURANCE_EXPIRED: 'Assurance expirée',
      REJECTED: 'Rejeté',
      CANCELLED: 'Annulé',
      APPROVED: 'Actif',
      REVOKED: 'Révoqué',
    },
    roleLabels: {
      PERSONAL_TRAINER_COACH: 'Coach / éducateur sportif',
      PROFESSIONAL_ATHLETE: 'Athlète professionnel',
      OTHER: 'Autre',
      ASSOCIATION_CLUB: 'Association / club',
      USER: 'Utilisateur',
    },
    pageReport: 'Affichage de {first} à {last} sur {totalRecords} résultats',
    noResults: 'Aucun résultat',
    colOrganizer: 'Organisateur',
    colSubmittedOn: 'Soumis le',
    colRoleType: 'Type de rôle',
    colRequestNote: 'Note de la demande',
    colSubmissionCount: 'Nombre de soumissions',
    colStatus: 'Statut',
    approveTitle: 'Organisateurs autorisés aux activités payantes',
    popupRejectTitle: 'Rejeter la demande ?',
    popupRejectIntro:
      'Vous êtes sur le point de rejeter cette demande.<br />Indiquez la raison de cette action. La personne sera notifiée.',
    reasonRejection: 'Raison du rejet',
    rejectPlaceholder: 'Saisissez la raison du rejet...',
    reject: 'Rejeter',
    popupApproveTitle: 'Approuver la demande ?',
    approveOtherWarning:
      "Cette personne a soumis sa demande avec le rôle « Autre ». Vous devez définir un rôle officiel avant d'approuver.",
    currentRate: 'Taux actuel',
    globalDefault: 'Taux global par défaut',
    assignRoleTag: 'Attribuer un rôle officiel',
    roleTagHint: "Rôle défini par l'administration, affiché sur le profil",
    popupApproveIntro:
      'Vous êtes sur le point d\'approuver cette demande.<br />La personne deviendra <span class="text-[#FCA13B]">organisateur pro</span> et pourra créer des activités payantes.',
    approve: 'Approuver',
    legalSetupTitle: 'Textes légaux requis avant approbation ?',
    legalSetupBody:
      "Vous ne pouvez pas approuver cet organisateur : les textes légaux requis ne sont pas publiés.<br />Pour approuver des organisateurs pro et activer les activités payantes, publiez d'abord :<ul><li>Les conditions organisateur pro</li><li>Les CGV participants</li></ul>Sans ces textes, l'approbation produirait un consentement légal invalide.",
    goToLegal: 'Aller aux textes légaux',
    additionalProofTitle: 'Demander un justificatif',
    messageToOrganizer: "Message à l'organisateur",
    enterText: 'Saisissez votre message',
    sendRequest: 'Envoyer la demande',
    popupRevokeTitle: "Révoquer l'accès ?",
    revokeWarning:
      'Cette personne perdra le droit de créer des activités payantes. Les activités payantes existantes ne bougent pas, mais aucune nouvelle ne pourra être créée.',
    popupRevokeIntro:
      'Vous êtes sur le point de révoquer cet accès.<br />Indiquez la raison de cette action. La personne sera notifiée.',
    reasonRevoked: 'Raison de la révocation',
    nameLabel: 'Nom',
    statusApproved: 'Demande approuvée par Smatchy',
    statusRevoked: 'Accès révoqué par Smatchy',
    statusRequestedInfo: 'Nouvelle soumission demandée par Smatchy',
    statusRejected: 'Demande rejetée par Smatchy',
    statusCancelled: "Demande annulée par l'utilisateur",
    statusRequested: "Demande soumise par l'utilisateur",
    statusInsuranceExpired: "L'assurance de l'utilisateur a expiré",
    colGrantedOn: 'Accordé le',
    colGrantedBy: 'Accordé par',
    colActions: 'Actions',
    revoke: 'Révoquer',
    viewHistory: "Voir l'historique",
  },
  cgvAcceptance: {
    colProOrganizer: 'Organisateur pro',
    colUsers: 'Utilisateurs',
    colCommissionRate: 'Taux de commission',
    colCgvVersion: 'Version des CGV',
    colGcsVersion: 'Version des CGV',
    colAcceptanceStatus: "Statut d'acceptation",
    colLastUpdated: 'Dernière mise à jour',
    colActions: 'Actions',
    pendingAcceptance: "En attente d'acceptation",
    accepted: 'Acceptées',
    acceptedOn: 'Acceptées le',
    changedOn: 'Modifiées le',
    by: 'par',
    customRate: 'Taux personnalisé',
    viewHistory: "Voir l'historique",
    noData: 'Aucune donnée',
    kpiOrganizersTitle: 'Organisateurs pro (actifs)',
    kpiOrganizersSub: 'Organisateurs éligibles aux activités payantes',
    kpiAcceptedSub: 'Dernières conditions organisateur acceptées',
    kpiPendingSub: "La création d'activités payantes est bloquée",
    kpiTotalUsers: 'Utilisateurs au total',
    kpiTotalUsersSub: 'Tous les utilisateurs du système',
    pageTitle: 'Acceptations des CGV et conditions',
    tabOrganizers: 'Organisateurs pro',
    tabUsers: 'Tous les utilisateurs Smatchy',
    historyTitle: 'Historique des acceptations',
    acceptedTermsCommission: 'Conditions et commission acceptées',
    acceptedTerms: 'Conditions générales acceptées',
    dateTime: 'Date et heure',
    organizerTermsDoc: 'Conditions organisateur pro',
    gcsDoc: 'Conditions Générales de Vente',
    close: 'Fermer',
    kpiAcceptedRefundSub: 'Dernière politique de remboursement acceptée',
    kpiPendingCheckoutSub: 'Requise avant le prochain paiement',
  },
  walletPages: {
    title: 'Portefeuille client',
    subtitle: "Consulter le solde in-app d'un membre et l'ajuster, avec un motif.",
    userIdLabel: 'Identifiant du membre',
    userIdPlaceholder: "Colle l'identifiant du membre",
    load: 'Charger',
    balance: 'Solde',
    totalCredited: 'Total crédité',
    totalSpent: 'Total dépensé',
    movements: 'Mouvements',
    noMovements: 'Aucun mouvement pour l’instant.',
    colDate: 'Date',
    colDirection: 'Sens',
    colAmount: 'Montant',
    colBalanceAfter: 'Solde après',
    colReason: 'Motif',
    credit: 'Crédit',
    debit: 'Débit',
    adjustTitle: 'Ajuster le solde',
    direction: 'Sens',
    amountLabel: 'Montant (€)',
    amountHint: 'Positif, 1000 € maximum par ajustement.',
    noteLabel: 'Motif (obligatoire)',
    notePlaceholder: 'Pourquoi cet ajustement (10 caractères minimum)',
    apply: 'Appliquer',
    confirmTitle: "Confirmer l'ajustement",
    confirmCredit: 'Tu vas créditer {{amount}} € sur le portefeuille de ce membre.',
    confirmDebit: 'Tu vas débiter {{amount}} € du portefeuille de ce membre.',
    cancel: 'Annuler',
    adjusted: 'Fait. Le nouveau solde est {{balance}} €.',
    loadError: 'Impossible de charger ce portefeuille.',
    adjustError: "L'ajustement n'a pas abouti.",
    invalidUserId: "Saisis d'abord un identifiant de membre.",
    invalidAmount: 'Saisis un montant entre 0,01 et 1000 €.',
    invalidNote: 'Le motif doit faire au moins 10 caractères.',
  },
  legalPages: {
    siteTextInForce: "Le texte en vigueur, tel que l'application le détient",
    writtenOnTheSite: 'Écrit sur le site, pas ici',
    writtenOnTheSiteExplained:
      "Un texte juridique se modifie dans Strapi. Notre API le descend et l'application affiche ce qui est descendu, c'est-à-dire ce que vous lisez ici.",
    loading: 'Chargement…',
    nothingSyncedYet: "Aucun contenu n'a encore été synchronisé pour ce texte.",
    pageTitle: 'Contenus légaux et CGV',
    proTab: 'Conditions organisateur pro',
    siteTab: 'Textes légaux du site',
    privacyTab: 'Politique de confidentialité',
    cguTab: "Conditions d'utilisation",
    gcsTab: 'Conditions de vente',
    legalNoticesTab: 'Mentions légales',
    editSiteText: 'Modifier le texte publié par le site',
    publishToSite: 'Publier sur le site',
    publishConfirm:
      "Cela remplace le texte sur le site et dans l'application, dans les deux langues, pour tout le monde.",
    publishingNotConfigured: "La publication n'est pas configurée : l'API n'a pas de jeton pour le site.",
    charteRestored: 'La mise en forme est restée, le texte est parti',
    charteRestoredExplained:
      "Le site et l'application appliquent chacun leur propre charte à ces textes, la présentation a donc été retirée avant publication : {{removed}}. Les titres, les paragraphes et les listes sont inchangés.",
    onlyOneLanguagePublished: 'Une seule langue a été publiée',
    onlyOneLanguageExplained:
      "Le site affiche désormais le nouveau texte en {{landed}} et l'ancien dans l'autre langue. Republie pour terminer, ou remets le premier en place.",
    nothingWasPublished: "Rien n'a été publié. Le site affiche toujours ce qu'il affichait.",
    editTerms: 'Modifier les conditions organisateur',
    languageWarning: 'Pensez à mettre à jour toutes les versions linguistiques avant de publier',
    currentVersion: 'Version actuelle :',
    appliesTo: "S'applique à :",
    proOrganizers: 'Les organisateurs pro',
    lastUpdatedOn: 'Dernière mise à jour le',
    byWord: 'par',
    editorPlaceholder: 'Saisissez les conditions générales...',
    publishNewVersion: 'Publier une nouvelle version',
    publishing: 'Publication...',
    versionHistory: 'Historique des versions',
    colVersion: 'Version',
    colPublishedDate: 'Date de publication',
    colPublishedBy: 'Publié par',
    colActions: 'Actions',
    view: 'Voir',
    publishOrganizerTitle: 'Publier de nouvelles conditions organisateur pro ?',
    confirmOrganizerBody:
      'Publier cette version demandera à tous les <span class="text-[#FCA13B]">organisateurs pro</span> de relire et accepter à nouveau les conditions mises à jour.<br />Un organisateur qui ne les accepte pas ne pourra plus créer ni gérer d\'activités payantes.<br />Cette action crée une nouvelle version légale et ne peut pas être annulée.',
    confirmParticipantBody:
      'Publier cette version demandera à tous les <span class="text-[#479E9C]">utilisateurs Smatchy</span> d\'accepter à nouveau la politique mise à jour.<br />Les nouvelles conditions s\'appliqueront à toutes les inscriptions et paiements après publication.<br />Cette action crée une nouvelle version légale et ne peut pas être annulée.',
    cancel: 'Annuler',
  },
  dashboard: {
    futureActivities: 'Activités futures',
    registeredUsers: 'Utilisateurs inscrits',
    groups: 'Groupes',
    pastActivities: 'Activités passées',
    activeUsers: 'Utilisateurs actifs',
    engagedDesc: 'Ayant créé ou participé à au moins une activité durant les {{days}} derniers jours',
    during: 'Durant les',
    days: 'derniers jours',
    pickDuration: 'Sélectionne une durée',
    colName: 'Nom',
    colActivitiesJoined: "Nombre d'activités participées",
    colLastActivity: 'Dernière activité participée',
  },
  dialogs: {
    cancel: 'Annuler',
    close: 'Fermer',
    understand: 'Je comprends et je veux continuer',
    cannotBeUndone: 'Cette action est irréversible.',
    deletedItems: 'Éléments supprimés',
    cancelActivityTitle: "Annuler l'activité ?",
    cancelActivityQuestion: 'Veux-tu vraiment annuler cette activité :',
    cancelActivityBtn: "Annuler l'activité",
    paidCancelBody:
      'Cette activité est payante. Son annulation annulera toutes les inscriptions et déclenchera le <span>remboursement intégral</span> des participants.',
    targets: { group: 'groupe', subgroup: 'sous-groupe', activity: 'activité' },
    targetsDefinite: { group: 'le groupe', subgroup: 'le sous-groupe', activity: "l'activité" },
    deleteTitle: 'Supprimer {{target}} ?',
    deleteQuestion: 'Veux-tu vraiment supprimer {{target}} :',
    deletingAuto: 'Supprimer ce {{target}} entraînera automatiquement :',
    liCancelIncoming:
      "L'annulation des activités à venir du groupe et de ses sous-groupes, avec remboursement intégral des inscriptions payantes le cas échéant",
    liDeleteActivities: 'La suppression de toutes les activités de ce {{target}} et de ses sous-groupes',
    liDeleteSubgroups: 'La suppression de tous les sous-groupes',
    movedTo: 'Le {{target}} et ses sous-groupes seront déplacés vers',
    activityMovedTo: 'Cette activité sera déplacée vers',
    deleteGroupBtn: 'Supprimer le groupe',
    deleteAccountTitle: 'Supprimer le compte ?',
    deleteAccountQuestion: 'Veux-tu vraiment supprimer le compte',
    accountAuto: 'Supprimer ce compte entraînera automatiquement :',
    liCancelIncomingUser:
      "L'annulation des activités à venir créées par ce compte, avec remboursement intégral des inscriptions payantes le cas échéant",
    liRemoveAccess: "La suppression définitive de l'accès à l'application mobile",
    accountMovedTo: 'Le compte sera déplacé vers',
    deleteAccountBtn: 'Supprimer le compte',
    creditOnDeletionTitle: "L'avoir du membre",
    creditOnDeletionToCard: 'Remboursé sur sa carte',
    creditOnDeletionForfeited: 'Bonus perdu',
    creditOnDeletionNone: "Ce membre n'a aucun avoir : rien à rembourser, rien de perdu.",
    creditOnDeletionUnknown:
      "Montants indisponibles : la suppression règle quand même l'avoir, mais ils ne peuvent pas s'afficher ici.",
    blockedTitle: 'Impossible de supprimer le compte',
    suspendIntro:
      "Tu peux suspendre ce compte dès maintenant : le membre ne peut plus utiliser l'application et les autres membres ne le voient plus. Le compte est supprimé automatiquement dès que plus rien ne bloque.",
    suspendReasonLabel: 'Motif',
    suspendReasonMinor: 'Le membre est mineur',
    suspendReasonOther: 'Autre',
    suspendBtn: 'Suspendre et supprimer dès que possible',
    alreadySuspended: 'Ce compte est déjà suspendu. Il sera supprimé automatiquement dès que plus rien ne bloque.',
    refundIntro: "Le remboursement dépend du moment de l'annulation :",
    refundGt48: ">48 h avant le début de l'activité :",
    refundLe48: "≤48 h avant le début de l'activité :",
    refund24: "24 h avant le début de l'activité :",
    refundFull: 'Remboursement intégral',
    refund70: '>70 % remboursés',
    refundNone: 'Aucun remboursement',
  },
  commissionDialogs: {
    historyTitle: 'Historique des changements de commission',
    updatedGlobal: 'Taux de commission global mis à jour',
    dateTime: 'Date et heure :',
    rateLabel: 'Taux de commission :',
    nameLabel: 'Nom :',
    legallyBinding: 'Action juridiquement engageante et irréversible',
    cannotUndo:
      'Une fois publié, ce changement ne peut être annulé que par un nouvel avenant juridique. Vérifie bien l’impact.',
    willReaccept: 'Les organisateurs pro concernés devront ré-accepter les conditions mises à jour.',
    acknowledge:
      'Je comprends que cette action déclenchera des notifications juridiques à tous les utilisateurs concernés et mettra à jour le taux de commission global de la plateforme.',
    viewAffected: 'Voir les {{count}} organisateurs pro concernés',
    publishing: 'Publication…',
    publishChanges: 'Publier les changements',
    toastUpdatedTitle: 'Commission mise à jour',
    toastSuccess: 'Le taux de commission global a été modifié avec succès.',
    toastError: 'La modification du taux de commission global a échoué.',
    organizersAffected: 'Organisateurs pro concernés',
    newGlobalRate: 'Nouveau taux global',
    currentRate: 'Taux actuel',
    newRate: 'Nouveau taux',
    existingUnaffected: "Les activités existantes ne sont pas affectées tant qu'elles ne sont pas renouvelées",
    selectOrganizer: 'Sélectionne un organisateur',
    ratePercent: 'Taux de commission (%)',
    ratePlaceholder: 'Taux de commission',
    enterRate: 'Saisis le taux de commission',
    overridesGlobal: 'Remplace le réglage global',
    appliesFuture: "Ce taux s'appliquera à toutes les futures activités créées par",
    setGlobalTitle: 'Définir un nouveau taux de commission global',
    reviewImpact: "Vérifier l'impact",
    organizerLabel: 'Organisateur',
    currentRateCaps: 'TAUX ACTUEL',
    globalDefault: 'Taux global par défaut',
    backToBalances: 'Retour aux soldes',
    organizerNotFound: 'Organisateur introuvable',
    organizerRemoved: 'Cet organisateur a peut-être été supprimé, ou le lien est invalide.',
    somethingWrong: "Quelque chose s'est mal passé",
    couldNotLoad: 'Impossible de charger cet organisateur.',
    goodToKnow: 'Bon à savoir',
    eventEarnings: 'Gains par activité',
    withdrawalRequest: 'Demandes de retrait',
    noPreview: 'Aucun aperçu.',
    noVersionFound: 'Aucune version trouvée',
  },
  financePages: {
    auditTitle: "Journaux d'audit",
    clearFilters: 'Effacer les filtres',
    dateRange: 'Plage de dates',
    scope: 'Périmètre',
    action: 'Action',
    actorType: "Type d'acteur",
    allScopes: 'Tous les périmètres',
    allActions: 'Toutes les actions',
    allActorTypes: "Tous les types d'acteur",
    copyId: "Copier l'identifiant",
    balancesTitle: 'Soldes des organisateurs',
    payoutTitle: 'Demandes de retrait',
    payoutDetailTitle: 'Détails de la demande de retrait',
    decline: 'Refuser',
    approve: 'Approuver',
    transactionsTitle: 'Transactions de paiement',
    amountFrom: 'Montant min (EUR)',
    amountTo: 'Montant max (EUR)',
    noMinimum: 'Aucun minimum',
    noMaximum: 'Aucun maximum',
    eventRevenueTitle: 'Revenus des activités',
    monTitle: 'Monétisation',
    monSubtitle: 'La santé de la monétisation, à côté des totaux de revenus.',
    monLimitedData:
      'Données encore limitées : ces ratios sont calculés sur très peu de transactions et se préciseront avec le volume.',
    monCompletionHint:
      'Le taux d’aboutissement est dérivé des statuts de transactions (aboutis ou remboursés ÷ initiés) en attendant une instrumentation API dédiée ; les cinq autres tuiles viennent de l’agrégat de revenus.',
    monCompletionRate: 'Taux d’aboutissement',
    monRefundRate: 'Taux de remboursement',
    monAvgBasket: 'Panier moyen',
    monMarginAfterStripe: 'Marge après Stripe',
    monStripeShare: 'Part Stripe',
    monNetSmatchy: 'Revenu net Smatchy',
    reset: 'Réinitialiser',
    monthRange: 'Plage de mois',
    view: 'Voir',
    searchOrganizer: 'Rechercher un organisateur',
    payoutColId: 'ID de retrait',
    payoutColOrganizer: 'Organisateur',
    payoutColAmount: 'Montant du retrait',
    payoutColEvents: "Nombre d'activités concernées",
    payoutColRequestedDate: 'Date de demande',
    payoutColStatus: 'Statut du retrait',
    payoutColVatStatus: 'Statut de TVA',
    payoutVatStatusMissingMsg:
      "Ce pro n'a pas encore indiqué son statut de TVA : sa facture du mois ne peut pas être établie, le retrait ne peut donc pas être validé.",
    vatStatus: {
      vat_registered: 'Soumis à la TVA',
      franchise: 'Franchise',
      association: 'Association',
      missing: 'Non renseigné',
    },
    payoutKpiAwaitingTitle: "Total en attente d'approbation",
    payoutKpiAwaitingSubtitle: 'En attente de la revue Smatchy.',
    payoutKpiProcessingTitle: 'Total en cours de traitement',
    payoutKpiProcessingSubtitle: 'En attente de traitement par Stripe.',
    payoutApproveFailedTitle: "Échec de l'approbation",
    payoutApproveFailedMsg: "Impossible d'approuver la demande de retrait. Réessaie.",
    payoutApprovedTitle: 'Retrait approuvé',
    payoutApprovedMsg: 'La demande a été envoyée à Stripe pour traitement.',
    payoutDeclineFailedTitle: 'Échec du refus',
    payoutDeclineFailedMsg: 'Impossible de refuser la demande de retrait. Réessaie.',
    payoutDeclinedTitle: 'Retrait refusé',
    payoutDeclinedMsg: "L'organisateur a été notifié avec ton message.",
    txColActivity: 'Activité',
    txColUser: 'Utilisateur',
    txColAmount: 'Montant',
    txColFee: 'Frais',
    txColNet: 'Net',
    txColStatus: 'Statut',
    txColDate: 'Date',
    txColActions: 'Actions',
    txPaymentStatus: 'Statut de paiement',
    txAllStatuses: 'Tous les statuts',
    txPaid: 'Payé',
    txPending: 'En attente',
    txRefunded: 'Remboursé',
    txTabPayment: 'Paiement',
    txTabPayout: 'Versement',
    peTabPending: 'Demandes en attente',
    peTabApproved: 'Organisateurs approuvés',
    obColProOrganizer: 'Organisateur pro',
    obColIncoming: 'Revenus entrants',
    obColAvailable: 'Disponible au retrait',
    obColPendingPayout: 'Versement en attente',
    obColStripeStatus: 'Statut de connexion Stripe',
    obColActions: 'Actions',
    obFilterStripeStatus: 'Statut Stripe',
    obFilterAll: 'Tous',
    bdColFinalEventRevenue: "Revenu final de l'activité",
    bdColFinalSmatchyRevenue: 'Revenu final Smatchy',
    bdColOrganizerRevenue: "Revenu de l'organisateur",
    bdEarningsStatus: 'Statut des gains',
    bdSearchPlaceholder: 'Rechercher une activité ou un lieu',
    esSearchPlaceholder: 'Rechercher un numéro de facture ou un organisateur',
    esColInvoiceId: 'Numéro de facture',
    esColPeriod: 'Période',
    esColTotalEvents: 'Total activités',
    esColEventRevenue: "Revenu de l'activité",
    esColSmatchyCommissions: 'Commissions Smatchy',
    prdBankAccount: 'Compte bancaire',
    diUser: 'Utilisateur',
    diAllSports: 'Tous les sports',
    erKpiTotalEvent: 'Revenu total des activités',
    erKpiTotalSmatchy: 'Revenu total Smatchy',
    erKpiTotalOrganizers: 'Revenu total des organisateurs',
    erKpiTicketsSold: 'Billets vendus',
    erKpiRefundRate: 'Taux de remboursement',
    erKpiStripeFees: 'Total des frais Stripe',
    erKpiUnknown: 'Inconnu',
    erFromLastMonth: 'vs le mois dernier',
    erChartTitle: 'Revenu Smatchy final dans le temps',
    tmSport: 'Sport',
    tmAllSport: 'Tous les sports',
    tmStatus: 'Statut',
    tmAllStatus: 'Tous les statuts',
    tmTitle: 'Billets par activité',
    bkSubIncoming: 'En attente de transfert vers le disponible',
    bkSubPending: 'En attente de revue',
    adParticipant: 'Participant',
    adRefundProcessed: 'Remboursement traité',
    adLastUpdated: 'Dernière mise à jour',
    tmTicketPrice: 'Prix du billet',
    tmSearchPlaceholder: 'Rechercher une activité, un lieu, un pays, un organisateur',
    tmTicketsProcessed: '{{count}} billets traités',
    tmTicketRefundOne: '{{count}} remboursement',
    tmTicketRefundMany: '{{count}} remboursements',
    tmTicketsSold: '{{count}} billets vendus',
    txSearchPlaceholder: 'Rechercher une activité ou un utilisateur',
    bkTotalIncoming: 'Total des revenus entrants',
    bkTotalBalances: 'Total des soldes organisateurs',
    bkTotalPending: 'Total des versements en attente',
    bkSubFunds: 'Fonds disponibles au retrait',
  },
  legacyUsers: {
    search: 'Rechercher',
    name: 'Nom',
    email: 'Mail',
    registeredOn: 'Inscrit le',
    sportsCount: 'Nombre de sports',
    pushNotification: 'Notification push',
    noUserFound: 'Aucun utilisateur trouvé',
    registeredSince: 'Inscrit depuis le :',
    sports: 'Sports',
    position: 'Position',
    noPosition: 'Pas de position connue',
    createdActivities: 'Activités créées',
    myGroups: 'Groupes',
    pastActivities: 'Activités passées',
  },
  payouts: {
    organizerInfo: "Informations sur l'organisateur",
    withdrawalId: 'Identifiant du retrait :',
    requestedDate: 'Date de la demande :',
    breakdownTitle: 'Détail du retrait',
    organizerRevenue: "Revenus de l'organisateur",
    totalWithdrawal: 'Montant total du retrait :',
    detailsTitle: 'Détails du retrait',
    status: 'Statut',
    amount: 'Montant',
    stripeProcessing: 'Le retrait a été lancé et est en cours de traitement par Stripe',
    approveTitle: 'Approuver la demande de retrait ?',
    trackInTransactions: 'Tu peux suivre son statut dans la page Transactions.',
    approveConfirm: 'Je confirme que cette demande de retrait a été vérifiée et approuvée.',
    approveBtn: 'Approuver le retrait',
    declineTitle: 'Refuser la demande de retrait ?',
    declineStops: 'Refuser ce retrait arrêtera le virement.',
    organizerNotified: "L'organisateur sera notifié avec la raison indiquée ci-dessous.",
    declineReason: 'Raison du refus de ce retrait',
    reasonPlaceholder: 'Saisis la raison ici...',
    declineBtn: 'Refuser le retrait',
    transactionDetails: 'Détails de la transaction',
  },
  readonly: {
    noMembers: 'Aucun membre dans ce groupe pour le moment',
    noSubgroups: 'Aucun sous-groupe dans ce groupe pour le moment',
    noActivities: 'Aucune activité dans ce groupe pour le moment',
    subGroup: 'Sous-groupe',
  },
  commonTable: {
    filters: 'Filtres',
    noData: 'Aucune donnée',
    search: 'Rechercher',
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    dateRange: 'Plage de dates',
    clearFilter: 'Effacer les filtres',
    review: 'Examiner',
    smatchyRevenue: 'Revenus Smatchy',
    totalRefunds: 'Total des remboursements traités',
    pageReport: 'Affichage de {first} à {last} sur {totalRecords} résultats',
  },
  groupEngage: {
    title: 'Engager des membres à proximité',
    context: 'Campagne pour le groupe',
    groupSport: 'Sport du groupe',
    noLocation:
      "Ce groupe n'a pas de localisation, il n'y a donc pas d'« autour » à cibler. Ajoute d'abord une localisation au groupe.",
    channelInvitation: 'Invitation in-app au groupe',
    channelInvitationHint: "Le membre peut accepter depuis l'app ; porte son propre push.",
    channelEmailHint: "Le template SendGrid d'invitation au groupe, dans la langue du membre. Crée aussi l'invitation.",
    channelPush: 'Push découverte',
    channelPushHint: 'Un groupe près de chez toi cherche des membres, via la préférence marketing.',
    resultLine: 'Campagne envoyée : {{invited}} invitation(s), {{emails}} email(s), {{pushed}} push découverte.',
  },
  groupSheet: {
    createdOn: 'créé le',
    by: 'par',
    membersOf: 'membres, dont {{active}} actifs 90 j',
    activitiesCreated: 'activités créées',
    joinedShare: 'des activités rejointes',
    lastActivity: 'dernière activité',
    daysAgo: 'il y a {{days}} j',
    namedAdmins: 'admin(s) nommé(s)',
    signal: 'Signal',
    signalAliveNoAdmin:
      "groupe vivant sans aucun admin : nommer {{owner}} (créateur·rice, actif·ve il y a {{days}} j) éviterait qu'il s'éteigne en cas de départ.",
    signalEmptyNoAdmin: 'sans admin et sans aucune activité : candidat à une campagne de réveil ou à un nettoyage.',
    signalNoAdmin: 'aucun admin nommé : le groupe repose entièrement sur sa personne créatrice.',
    signalDozing: "le groupe s'endort : dernière activité il y a {{days}} j. Une campagne peut le réveiller.",
    signalHealthy: 'groupe en bonne santé : administré et actif récemment.',
    ownerAndAdmins: 'Responsable et admins',
    creator: 'Créateur·rice',
    admin: 'Admin',
    activeAgo: 'actif·ve il y a {{days}} j',
    noAdmin: 'Aucun admin nommé',
    recentActivities: 'Dernières activités du groupe',
    partners: '{{count}} partenaire(s)',
    nobody: 'personne',
    noActivity: 'Aucune activité dans ce groupe pour le moment',
    members: 'Membres',
    noMembers: 'Aucun membre pour le moment',
    moreMembers: '+ {{count}} autres',
    description: 'Description',
    hasCover: 'Photo de couverture présente',
    loading: 'Chargement de la fiche…',
    failed: 'Impossible de charger la fiche de ce groupe.',
  },
  strategy: {
    title: 'Stratégie · 6 mois',
    fullMonths: 'mois pleins uniquement',
    signups: 'Inscrits · croissance mensuelle',
    membersTotal: 'membres dans la base',
    active90: 'Actifs 90 jours',
    activeMembers: 'membres ayant agi',
    avgFill: 'Remplissage moyen',
    fillNote: 'places partenaires, hors organisateur',
    paid: 'Volume payant',
    paidNote: 'dépôts encaissés sur le mois',
    vsPrev: 'vs mois précédent',
  },
  studio: {
    title: 'Studio marketing',
    badge: 'ACTIVATION',
    subtitle: 'Trois enjeux, une file de décisions, tout est écrit. Ton poste de commandement du matin.',
    loadFailed: "Le studio n'a pas pu charger. Réessaie.",
    loading: 'Balayage des trois enjeux…',
    pulseJoins: 'inscriptions attribuées · 7 j',
    pulsePending: 'actions à décider',
    pulseExpiring: '{{count}} expirent sous 24 h',
    pulseCampaigns: 'campagnes · 7 j',
    pulseReached: '{{count}} membres touchés',
    pulsePressure: 'de la base sollicitée · 7 j',
    pulsePressureNote: "garde-fou : à surveiller avant d'envoyer plus",
    deckTitle: 'À décider maintenant',
    deckEmpty:
      'Rien à décider : aucune activité vide, aucun petit groupe vivant, aucun organisateur endormi. Profites-en.',
    kind: { fill_event: 'Remplissage', fill_group: 'Groupes', organizer_nudge: 'Organisateurs' },
    whyFillEvent: "Personne n'a rejoint l'organisateur, et {{candidates}} candidats vivent à moins de 25 km.",
    whyFillGroup:
      'Groupe vivant de seulement {{members}} membres (dernière activité il y a {{days}} j) ; {{candidates}} candidats autour.',
    whyOrganizers: "{{count}} organisateurs confirmés n'ont rien déposé depuis 30 jours.",
    chInvitation: 'Invitation in-app',
    chPush: 'Push découverte',
    chEmail: 'Email',
    launch: 'Lancer tel quel',
    confirmLaunch: "Confirmer l'envoi",
    adjust: 'Ajuster',
    discard: 'Écarter',
    discardLater: 'Trop tôt — repropose plus tard',
    discardLaterHint: 'Se rendort 48 h, revient si toujours pertinent.',
    discardTargeting: 'Mauvais ciblage — à revoir',
    discardTargetingHint: 'Enregistré comme signal que la règle vise mal.',
    discardNever: 'Jamais pour celui-ci',
    discardNeverHint: 'Silence définitif sur cette cible.',
    journalTitle: 'Journal et attribution',
    journalEmpty: 'Aucune campagne pour le moment. Le premier envoi écrira ici.',
    colWhen: 'Quand',
    colCampaign: 'Campagne',
    colChannels: 'Canaux',
    colFunnel: 'Entonnoir',
    colJoined: 'Inscrits',
    funnelNums: '{{targeted}} ciblés · {{invited}} invités · {{emails}} emails',
    source: { action: 'Lancement studio', composer: 'Composeur de fiche' },
    adjusted: 'ajustée',
    adjustTitle: 'Ajuster et envoyer',
    campaignFor: 'Campagne pour',
    emailHint: "Porte l'invitation ; sauté proprement tant que le template SendGrid attend.",
    eventResultLine: 'Parti : {{invited}} invitation(s), {{pushed}} push, {{emails}} email(s).',
    emailTemplateMissing: 'email sauté, template pas encore créé',
    autosTitle: 'Automates',
    autosNote:
      "Les canaux doux agissent seuls dans leurs rails ; l'email et les grosses audiences restent dans ta file.",
    killSwitch: 'Tous les automates',
    autoName: {
      save_activity: 'Sauve-activité',
      welcoming_group: 'Groupe accueillant',
      organizer_detector: "Détecteur d'organisateurs",
    },
    autoRule: {
      save_activity: 'À J-5, une activité sans partenaire avec ≥20 candidats autour reçoit un push découverte.',
      welcoming_group:
        'Un inscrit de deux jours avec un sport déclaré reçoit une invitation in-app au groupe vivant le plus proche.',
      organizer_detector:
        "Un organisateur confirmé silencieux 30 jours devient une carte dans ta file. Jamais d'envoi direct.",
    },
    autoRails: {
      save_activity:
        'rails : 1 sollicitation/membre/semaine · préférences respectées · jamais deux fois la même activité',
      welcoming_group: 'rails : un seul groupe · jamais un groupe mort · un seul accueil par membre, à vie',
      organizer_detector: "l'email ne part qu'après ton approbation",
    },
    autoStats: '7 j : {{triggered}} déclenchés · {{sent}} envois',
    autoJoined: '{{count}} inscrits',
    autoProposesOnly: 'propose seulement',
    seqsTitle: 'Séquences',
    seqName: { organizer_redepose: 'Redépose (organisateurs)', welcome_first_step: 'Bienvenue vers le premier pas' },
    seqMetrics: '{{active}} en cours · {{success}} sortis par succès · {{ended}} arrivés au bout',
    seqExit: {
      organizer_redepose: 'sortie de succès : une activité est déposée, les rappels restants meurent sans partir',
      welcome_first_step: 'sortie de succès : le membre rejoint ou crée quoi que ce soit',
    },
    funnelTracking: '{{opened}} ouverts · {{clicked}} cliqués',
  },
  activityStatus: {
    incoming: 'À venir',
    completed: 'Terminée',
    canceled: 'Annulée',
    total: 'Activités au total',
    totalParticipants: 'Participants au total',
    completionRate: 'Taux de tenue',
  },
  datePicker: {
    startDate: 'Date de début',
    endDate: 'Date de fin',
    startMonth: 'Mois de début',
    endMonth: 'Mois de fin',
    yesterday: 'Hier',
    last7days: '7 derniers jours',
    last14days: '14 derniers jours',
    last30days: '30 derniers jours',
    thisWeek: 'Cette semaine',
    lastWeek: 'La semaine dernière',
    thisMonth: 'Ce mois-ci',
    lastMonth: 'Le mois dernier',
    thisQuarter: 'Ce trimestre',
    lastQuarter: 'Le trimestre dernier',
    thisYear: 'Cette année',
    clear: 'Effacer',
  },
  activityDrawer: {
    name: "Nom de l'activité",
    nameRequired: "Le nom de l'activité est obligatoire",
    coverPhoto: 'Photo de couverture',
    dateTime: 'Date et heure',
    description: 'Description',
    duration: 'Durée',
    level: 'Niveau',
    sportType: 'Type de sport',
    visibility: 'Visibilité',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    organizer: 'Organisateur',
    participants: 'Participants',
    noParticipants: 'Pas encore de participant sur cette activité',
    editPhoto: 'Modifier la photo',
    uploadPhoto: 'Téléverser une nouvelle photo',
    coverPhotoEmptyTitle: 'Ajouter une photo de couverture',
    coverPhotoEmptyHint: 'Optionnel · JPG ou PNG',
    removePhoto: 'Retirer la photo',
    saveAll: 'Enregistrer les modifications',
    discardAll: 'Tout annuler',
    undo: 'Annuler',
    close: 'Fermer',
    edit: 'Modifier',
    changesSaved: "Les modifications de l'activité ont été enregistrées",
    changesDiscarded: "Toutes les modifications de l'activité ont été annulées",
    createdOn: 'Créée le {{ date }}',
    noDescription: 'Aucune description',
    routeLink: 'Lien de parcours',
    noRouteLink: 'Aucun lien de parcours',
    handiFriendly: 'Handi Friendly',
    handiFriendlyYes: 'Oui, déclaré par l’organisateur',
    handiFriendlyNo: 'Non',
    cancelActivity: 'Annuler cette activité',
    deleteActivity: 'Supprimer cette activité',
    visibilityEveryone: 'Tout le monde',
    visibilityPrivate: 'Privée',
  },
  auditDetail: {
    title: "Détail du journal d'audit",
    selectEntry: 'Sélectionne une entrée du journal pour voir le détail.',
    actor: 'Acteur',
    actorId: "ID de l'acteur",
    entity: 'Entité',
    entityId: "ID de l'entité",
    logId: 'ID du journal',
    name: 'Nom',
    type: 'Type',
    version: 'Version',
    field: 'Champ',
    before: 'Avant',
    after: 'Après',
    stateChange: "Changement d'état",
    noChanges: "Aucun changement d'état détecté.",
    metadata: 'Métadonnées',
    technicalDetail: 'Détails techniques',
    idempotencyKey: "Clé d'idempotence",
    stripeEventId: "ID d'événement Stripe",
  },
  paidRequest: {
    whichActivities: 'Quelles activités payantes proposerez-vous ?',
    title: "Demande de création d'activités payantes",
    detail: 'Détails',
    history: 'Historique',
    companyInfo: 'Informations société / association',
    nameLabel: 'Nom :',
    address: 'Adresse :',
    siret: 'Numéro SIRET / RNA :',
    roleDescription: 'Description du rôle :',
    roleType: 'Type de rôle :',
    requiredProof: 'Justificatifs requis',
    insuranceCertificate: "Attestation d'assurance",
    insuranceExpiry: "Date d'expiration de l'assurance :",
    continueApprove: "Continuer vers l'approbation",
    rejectWithComment: 'Rejeter avec commentaire',
    revokeWithComment: "Révoquer l'accès avec commentaire",
    requestResubmission: 'Demander un nouvel envoi',
    rejectionReason: 'Motif du rejet :',
    submittedOn: 'Envoyée le :',
    rejectedOn: 'Rejetée le :',
    cancelledOn: 'Annulée le :',
    close: 'Fermer',
  },
  activityEarnings: {
    title: "Détail des revenus de l'activité",
    location: "Lieu de l'activité",
    ticketPrice: 'Prix du billet',
    eventBreakdown: "Décomposition des revenus de l'activité",
    smatchyBreakdown: 'Décomposition des revenus Smatchy',
    eventRevenue: "Revenus de l'activité :",
    finalEventRevenue: "Revenus finaux de l'activité :",
    organizerRevenue: "Revenus de l'organisateur :",
    smatchyRevenue: 'Revenus Smatchy :',
    finalSmatchyRevenue: 'Revenus Smatchy finaux :',
    smatchyCommissions: 'Commissions Smatchy',
    stripeFees: 'Frais Stripe :',
    serviceFees: 'Frais de service :',
    refundProcessed: 'Remboursement effectué :',
    participantTransaction: 'Transactions des participants',
  },
  userProfile: {
    loadFailed: "La fiche n'a pas pu être chargée. L'environnement ne la sert peut-être pas encore.",
    actedAgo: 'a agi il y a {{days}} j',
    sex: { male: 'homme', female: 'femme' },
    factActivities: 'activités',
    factCreated: 'créées',
    factGroups: 'groupes',
    factPaid: 'payés',
    months: '{{months}} mois',
    membershipAge: "d'ancienneté",
    daysAgoShort: 'il y a {{days}} j',
    lastLogin: 'dernière connexion',
    untracked: 'non tracée',
    years: '{{age}} ans',
    bracket: 'tranche {{label}}',
    bracketShare: '{{share}} % de la base',
    nearby: 'membres à moins de {{radius}} km',
    nearbySport: 'dont {{count}} en {{sport}}',
    topShare: 'top {{share}} %',
    topShareLabel: 'des membres les plus actifs, 90 derniers jours',
    signalLabel: 'Signal marketing :',
    signals: {
      ambassador: "Profil complet, joignable, chaud : candidat idéal pour un rôle d'ambassadeur{{sport}} .",
      activation: "N'a jamais agi et joignable : un push « première activité » est le bon geste.",
      reactivation: 'Silencieux depuis {{days}} jours et joignable : un message de réactivation peut le ramener.',
      unreachable: 'Pas de jeton push : ce membre est injoignable en push.',
      optedOut: 'Push marketing explicitement refusé : à respecter, messages transactionnels uniquement.',
      organiserToCoach:
        "{{count}} création(s) restée(s) sans participant : à accompagner avant qu'il ne renonce à organiser.",
    },
    timelineTitle: "Parcours d'activité",
    kind: { created: 'A créé', joined: 'A rejoint', next: 'À venir' },
    attendees: '{{count}} partenaire(s), hors organisateur',
    noTimeline: "Aucune activité pour l'instant.",
    groupsTitle: 'Groupes',
    admin: 'ADMIN',
    memberRole: 'MEMBRE',
    groupMembers: '{{count}} membres',
    noGroups: 'Aucun groupe.',
    moneyTitle: 'Argent',
    paid: 'payés en tout',
    refunded: 'remboursés',
    released: 'encaissés (organisateur)',
    positionTitle: 'Position déclarée',
    sportsTitle: 'Sports et niveaux',
    declaredAt: 'déclaré le {{date}}',
    noSports: 'Aucun sport déclaré.',
    completenessTitle: 'Complétude du profil',
    fields: {
      firstName: 'prénom',
      city: 'ville',
      position: 'position',
      sport: 'sport',
      birth: 'naissance',
      sex: 'genre',
      avatar: 'avatar',
      description: 'description',
      push: 'push',
    },
    reachTitle: 'Joignabilité',
    pushToken: 'Jeton push :',
    present: 'présent',
    absent: 'absent',
    defaultSettings: "Réglages jamais touchés : les valeurs par défaut s'appliquent, tout est activé.",
    pushGroup: 'Push mobile (14)',
    mailGroup: 'Email (9)',
    switches: {
      event_edit_or_cancel: 'modif/annulation',
      event_new_participant: 'nouveau participant',
      event_reminder: 'rappel',
      event_share: 'partage',
      event_new_event_proximity: 'proximité',
      group_invitation: 'invitation groupe',
      group_accept: 'acceptation groupe',
      group_new_event: 'activité du groupe',
      group_new_member: 'membre du groupe',
      group_deleted: 'groupe supprimé',
      chat_new_message_smatcher: 'chat smatcher',
      chat_new_message_event: 'chat activité',
      chat_new_message_group: 'chat groupe',
      marketing: 'marketing',
      event_participant_unregister: 'désinscription',
      event_confirm_register: "confirmation d'inscription",
      event_confirm_unregister: 'confirmation de désinscription',
    },
  },
  eventDetail: {
    today: "Aujourd'hui",
    inDays: 'J-{{days}}',
    copyLink: 'Copier le lien',
    storyCta: 'Story Instagram',
    level: 'Niveau',
    anyLevel: 'Tous niveaux acceptés',
    partnersSeats: 'places partenaires occupées, hors organisateur',
    engageTitle: 'Engager des membres à proximité',
    engageAround: '<strong>{{total}}</strong> membres habitent à moins de {{radius}} km de cette activité.',
    engageSameSport: 'Dont <strong>{{count}}</strong> qui déclarent {{sport}}.',
    exportCta: 'Exporter ces {{count}} contacts (CSV)',
    pushNote: "Le CSV ci-dessous alimente une campagne email manuelle en attendant le template d'invitation SendGrid.",
    radiusLabel: 'Rayon',
    sportLabel: 'Sport',
    allSports: 'Tous les sports',
    levelLabel: 'Niveau',
    levelAny: 'Tous niveaux',
    levelFrom: 'De',
    levelTo: 'À',
    channelsLabel: 'Canaux',
    channelInvitation: 'Invitation in-app',
    channelInvitationHint: "Le membre peut accepter depuis l'app ; porte son propre push.",
    channelPush: 'Push proximité',
    channelPushHint: 'Une activité près de chez toi cherche des partenaires.',
    channelEmail: 'Email',
    channelEmailHint: "Disponible dès que le template d'invitation SendGrid existera.",
    previewCount: '<strong>{{count}}</strong> membres ciblés, participants et déjà invités exclus (500 max).',
    previewLoading: "Comptage de l'audience…",
    send: 'Envoyer à {{count}} membres',
    confirmSend: 'Confirmer : envoyer vraiment à {{count}} membres',
    cancelSend: 'Annuler',
    resultLine: 'Campagne envoyée : {{invited}} invitation(s) créée(s), {{pushed}} push de proximité.',
    sendFailed: "L'envoi a échoué. Rien n'est peut-être parti ; réessaie.",
    participants: 'Participants',
    attendeesNote: "Le décompte brut inclut l'organisateur.",
    noAttendees: "Personne n'a encore rejoint.",
    viewProfile: 'Voir la fiche',
    description: 'Description',
  },
  eventsList: {
    title: 'Activités à venir',
    subtitle: "La liste d'abord ; la carte à la demande ; un clic fait glisser la fiche à droite",
    jMinus: 'J−{{n}}',
    daysAgo: 'il y a {{n}} j',
    today: "aujourd'hui",
    searchPlaceholder: 'Chercher par mot-clé…',
    cityPlaceholder: 'Ville…',
    allSports: 'Tous les sports',
    emptyOnly: 'Sans participant',
    paidOnly: 'Payantes',
    recurringOnly: 'Récurrentes',
    showMap: 'Afficher la carte',
    hideMap: 'Masquer la carte',
    mapNote: 'repliée par défaut : la liste est la surface de travail',
    start: 'Départ',
    activity: 'Activité',
    fill: 'Remplissage',
    level: 'Niveau',
    price: 'Prix',
    anyLevel: 'Tous niveaux',
    free: 'gratuite',
    empty: 'Aucune activité à venir ne correspond aux filtres.',
    count: '{{count}} activités affichées',
    fillNote: "le remplissage exclut l'organisateur",
  },
  marketingUsers: {
    title: 'Utilisateurs',
    subtitle: "Qui vaut quoi, ce qu'il a fait en dernier, et comment le joindre",
    searchPlaceholder: 'Chercher un nom, un email, une ville… Entrée pour appliquer',
    reachableOnly: 'Joignables marketing',
    loadFailed: "La liste des utilisateurs n'a pas pu être chargée. L'environnement ne la sert peut-être pas encore.",
    member: 'Membre',
    heat: 'Chaleur',
    lastAction: 'Dernière action',
    lastLogin: 'Dernière connexion',
    sports: 'Sports et niveaux',
    reachable: 'Joignable',
    activities: 'Activités',
    since: 'inscrit le',
    created: 'A créé',
    joined: 'A rejoint',
    daysAgo: 'il y a {{days}} jour(s)',
    noAction: 'Aucune action',
    noSport: 'aucun sport déclaré',
    noPushToken: 'pas de jeton push',
    defaultSettings: 'réglages par défaut (tout activé)',
    pushMarketing: 'marketing',
    pushReminder: 'rappels',
    pushProximity: 'proximité',
    pageOf: 'Page {{page}} sur {{last}} · {{total}} membres',
    note: "La chaleur suit la règle de l'écran Segments, sur la dernière action. La dernière connexion n'existe que depuis juillet 2026 : un tiret signifie non tracé, pas jamais venu. Des réglages non touchés valent les réglages par défaut, tout activé. Le détail des 23 interrupteurs de notification vivra dans la fiche profil.",
  },
  commission: {
    title: 'Taux de commission',
    organizerCommission: 'Commission organisateur',
    addCustom: 'Ajouter une commission personnalisée',
    customRate: 'Taux personnalisé',
    commissionRate: 'Taux de commission',
    currentGlobalRate: 'Taux de commission global actuel',
    globalRate: 'Taux de commission global',
    editGlobalRate: 'Modifier le taux global',
    legalSource: 'Référence juridique',
    searchOrganizer: 'Chercher un organisateur',
    action: 'Action',
    actions: 'Actions',
    noData: 'Aucune donnée',
    lastUpdated: 'Dernière mise à jour :',
    colOrganizerName: 'Nom de l’organisateur',
    colRoleType: 'Type de rôle',
    colLastUpdated: 'Dernière mise à jour',
    globalRateNote: 'Applicable à toutes les activités, sauf accord spécifique.',
    editRateNote: 'Toute modification déclenche une réacceptation contractuelle pour tous les organisateurs pro.',
    rateBounds: 'Le taux de commission doit être compris entre 0,1 % et 50 %',
  },
  transactionDetails: {
    title: 'Détail du paiement',
    header: 'Détails de la transaction',
    participantInfo: 'Informations du participant',
    breakdown: 'Décomposition du paiement',
    paymentAmount: 'Montant payé :',
    participantAmount: 'Part du participant :',
    stripeFees: 'Frais Stripe :',
    refundedAmount: 'Montant remboursé :',
    amount: 'Montant',
    status: 'Statut',
  },
  groupsManagement: {
    archive: {
      badge: 'Archivé',
      archiveAction: 'Archiver le groupe',
      reactivateAction: 'Réactiver le groupe',
      bannerTitle: 'Groupe archivé — lecture seule',
      bannerMessage:
        "Ce groupe est figé : vous pouvez le consulter, mais vous ne pouvez ni le modifier, ni le supprimer, ni lui envoyer de campagne tant qu'il est archivé.",
      confirmArchiveTitle: 'Archiver ce groupe ?',
      confirmArchiveMessage:
        "Le groupe passe en lecture seule. Les membres gardent l'accès au groupe et à son chat, mais plus personne ne peut y créer d'activité ni le modifier. Vous pourrez le réactiver à tout moment.",
      confirmArchiveCta: 'Archiver',
      confirmReactivateTitle: 'Réactiver ce groupe ?',
      confirmReactivateMessage:
        'Le groupe redevient actif : les membres peuvent de nouveau y créer des activités et le modifier.',
      confirmReactivateCta: 'Réactiver',
      cancel: 'Annuler',
      archivedToast: '« {{name}} » a été archivé',
      reactivatedToast: '« {{name}} » a été réactivé',
    },
    title: 'Gestion des groupes',
    kpiTotal: 'Groupes au total',
    kpiNew: 'Nouveaux ce mois-ci',
    kpiMembers: 'Membres au total',
    kpiActivities: 'Activités des groupes',
    kpiTop: 'Sport le plus représenté',
    kpiThisMonth: 'ce mois-ci',
    kpiAcrossGroups: 'Sur tous les groupes',
    kpiOfGroups: 'des groupes',
    kpiNoAdmins: 'Sans aucun admin',
    kpiNoAdminsNote: 'Le levier : nommer un admin par groupe vivant',
    cityFilter: 'Ville…',
    withoutAdmins: 'Sans admin',
    privateOnly: 'Privés',
    exportCsvTooltip:
      'Exporte tous les groupes, racines et sous-groupes, avec une colonne parent. Les filtres affichés ne sont pas appliqués.',
    privacy: 'Confidentialité',
    adminsColumn: 'Admins',
    public: 'Public',
    private: 'Privé',
    filters: 'Filtres',
    clearFilter: 'Effacer le filtre',
    backDrawer: 'Revenir au volet précédent',
    searchGroup: 'Chercher un groupe ou un admin',
    deleteGroup: 'Supprimer le groupe',
    engageGroup: 'Engager des membres à proximité',
    editGroup: 'Modifier le groupe',
    groupName: 'Nom du groupe',
    groupLead: 'Responsable',
    sportType: 'Sport',
    totalSubgroups: 'Sous-groupes',
    totalMembers: 'Membres',
    totalActivities: 'Activités',
    total: 'Total',
    subgroups: 'Sous-groupes',
    members: 'Membres',
    activities: 'Activités',
    actions: 'Actions',
    groupNameLabel: 'Nom du groupe',
    nameRequired: 'Le nom du groupe est obligatoire',
    descriptionRequired: 'La description est obligatoire',
    coverPhoto: 'Photo de couverture',
    description: 'Description',
    handiFriendly: 'Handi Friendly',
    handiFriendlyYes: 'Oui, déclaré par les admins du groupe',
    handiFriendlyNo: 'Non',
    sportTypeLabel: 'Type de sport',
    editPhoto: 'Modifier la photo',
    uploadPhoto: 'Téléverser une nouvelle photo',
    removePhoto: 'Retirer la photo',
    deleteThisGroup: 'Supprimer ce groupe',
    saveAll: 'Enregistrer les modifications',
    discardAll: 'Tout annuler',
    undo: 'Annuler',
    close: 'Fermer',
    edit: 'Modifier',
    loadingGroups: 'Chargement des groupes…',
    noGroupsMatch: 'Aucun groupe ne correspond à la recherche ou aux filtres',
    changesSaved: 'Les modifications du groupe ont été enregistrées',
    changesDiscarded: 'Toutes les modifications du groupe ont été annulées',
  },
  usersManagement: {
    suspendSuccess: 'Compte suspendu. Il sera supprimé dès que plus rien ne bloque.',
    suspendFailed: 'Impossible de suspendre le compte',
    liftSuccess: 'Suspension levée',
    liftFailed: 'Impossible de lever la suspension',
    title: 'Gestion des utilisateurs',
    totalUsers: 'Utilisateurs au total',
    allAccounts: 'Tous les comptes inscrits',
    proUsers: 'Comptes pro',
    engagement: 'Engagement',
    profileCompletion: 'Complétion du profil',
    pushNotification: 'Notification push',
    filters: 'Filtres',
    apply: 'Appliquer',
    resetAll: 'Tout réinitialiser',
    searchUser: 'Chercher un nom ou un email',
    exactCity: 'Nom exact de la ville',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    min: 'Min',
    max: 'Max',
    userName: 'Nom',
    email: 'Email',
    city: 'Ville',
    cityLabel: 'Ville',
    sports: 'Sports',
    activities: 'Activités',
    actions: 'Actions',
    status: 'Statut',
    userStatus: 'Statut du compte',
    userType: 'Type de compte',
    userTypeHeader: 'Type de compte',
    user: 'Utilisateur',
    lastLogin: 'Dernière connexion',
    lastLoginHeader: 'Dernière connexion',
    createdAt: 'Date de création du compte',
    createdOn: 'Créé le',
    completionRange: 'Complétion du profil (%)',
    noUsers: 'Aucun utilisateur trouvé.',
    deletedItem: 'Élément supprimé',
    deletedItems: 'Éléments supprimés',
    movedTo: 'a été déplacé vers',
    active: 'Actif',
    inactive: 'Inactif',
    temperature: {
      hot: 'Chaud',
      warm: 'Tiède',
      cool: 'Refroidi',
      frozen: 'Gelé',
      never: 'Jamais agi',
      groupOnly: 'Groupe uniquement',
      untouched: 'Jamais engagé',
    },
    proColumn: 'Pro',
    allStatuses: 'Tous les statuts',
    all: 'Toutes',
    pushOn: 'Activées',
    pushOff: 'Désactivées',
    types: {
      all: 'Tous les types de compte',
      USER: 'Utilisateur',
      PERSONAL_TRAINER_COACH: 'Coach / éducateur sportif',
      PROFESSIONAL_ATHLETE: 'Athlète professionnel',
      OTHER: 'Autres comptes pro',
      ASSOCIATION_CLUB: 'Association / club',
    },
    ofTotal: 'de la base',
    deleteUser: "Supprimer l'utilisateur",
    editUser: "Modifier l'utilisateur",
  },
  userDetail: {
    completion: 'Complétion du profil',
    completionCount: '{{met}} sur {{total}}',
    completionFilled: 'Rempli',
    completionMissing: 'Manquant',
    completionSameList: "La même liste que le membre voit dans l'application.",
    completionCriteria: {
      firstName: 'Prénom',
      lastName: 'Nom',
      gender: 'Genre',
      dateOfBirth: 'Date de naissance',
      city: 'Ville',
      photo: 'Photo',
      sports: 'Sports choisis',
      sportLevels: 'Un niveau sur chaque sport',
      bio: 'Bio',
      availability: 'Quand la personne est libre',
      intention: 'Ce que la personne cherche',
      goals: 'Un objectif par sport',
    },
    personalInfo: 'Informations personnelles',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    enterPhone: 'Saisir le numéro de téléphone',
    position: 'Position',
    sportsLevel: 'Niveau sportif',
    pushNotification: 'Notification push',
    willReceivePush: "L'utilisateur recevra les notifications push",
    consent: {
      title: 'Notifications & consentement marketing',
      hint: "Chaque interrupteur s'enregistre seul. « On » = la personne le reçoit ; un réglage est actif tant qu'on ne le coupe pas.",
      pushTitle: 'Notifications push',
      mailTitle: 'Emails',
      cutMarketing: 'Couper tout le marketing',
      cutAll: 'Tout couper',
      on: 'On',
      off: 'Off',
      others: 'Autres',
      push: { activities: 'Activités', groups: 'Groupes', chat: 'Messages', profile: 'Profil' },
      mail: { activities: 'Activités', groups: 'Groupes' },
      keys: {
        event_edit_or_cancel: "Modification ou annulation d'une activité",
        event_new_participant: 'Nouveau participant',
        event_reminder: 'Rappel la veille',
        event_share: 'Activité partagée ou suggérée',
        event_new_event_proximity: 'Activités autour de toi',
        event_confirm_register: "Confirmation d'inscription",
        event_confirm_unregister: 'Confirmation de désinscription',
        event_participant_unregister: 'Un participant se désinscrit',
        group_invitation: 'Invitation à un groupe',
        group_accept: 'Invitation acceptée',
        group_new_event: 'Nouvelle activité de groupe',
        group_new_member: 'Nouveau membre du groupe',
        group_life: 'Vie du groupe (refus, départs…)',
        group_my_role: 'Mon rôle dans le groupe',
        chat_new_message_smatcher: "Messages d'un smatcheur",
        chat_new_message_event: 'Messages dans une activité',
        chat_new_message_group: 'Messages dans un groupe',
        profile_new_follower: 'Nouvel abonné',
        marketing: 'Marketing & promotions',
      },
    },
    noLocation: 'Aucune localisation',
    noSports: 'Aucun sport renseigné.',
    group: 'Groupe',
    groups: 'Groupes',
    activities: 'Activités',
    created: 'Créés',
    joined: 'Rejoints',
    organized: 'Organisées',
    canceled: 'Annulées',
    lastLoginOn: 'Dernière connexion le',
    userType: 'Type de compte :',
    nameNeeded: 'Saisis un prénom et un nom pour créer un profil.',
    loadingActivities: 'Chargement des activités…',
    loadingGroups: 'Chargement des groupes…',
    noActivities: 'Aucune activité trouvée.',
    noGroups: 'Aucun groupe trouvé.',
    deleteAccount: 'Supprimer ce compte',
    suspendedBadge: 'Suspendu, en attente de suppression',
    suspendedReasonMinor: 'Mineur',
    suspendedReasonOther: 'Autre motif',
    liftSuspension: 'Lever la suspension',
    discardAll: 'Tout annuler',
    undo: 'Annuler',
    close: 'Fermer',
    edit: 'Modifier',
  },
  deletedItems: {
    title: 'Éléments supprimés',
    tabGroups: 'Groupes',
    tabUsers: 'Utilisateurs',
    tabActivities: 'Activités',
    filters: 'Filtres',
    clearFilter: 'Effacer le filtre',
    backDrawer: 'Revenir au volet précédent',
    searchGroup: 'Chercher un groupe',
    searchEmail: 'Chercher un email',
    searchActivity: 'Chercher une activité',
    searchAddress: 'Ville, lieu, pays',
    pageReport: '{first} à {last} sur {totalRecords} résultats',
    groupName: 'Nom du groupe',
    groupLead: 'Responsable',
    sportType: 'Sport',
    totalSubgroups: 'Sous-groupes',
    totalMembers: 'Membres',
    totalActivities: 'Activités',
    total: 'Total',
    subgroups: 'Sous-groupes',
    member: 'Membres',
    activitiesHeader: 'Activités',
    userName: 'Nom',
    email: 'Email',
    userType: 'Type de compte',
    lastLogin: 'Dernière connexion',
    pushNotif: 'Notif. push',
    status: 'Statut',
    createdOn: 'Créé le',
    deletedBy: 'Supprimé par',
    activity: 'Activité',
    group: 'Groupe',
    participantsHeader: 'Participants',
    organizer: 'Organisateur',
    participants: 'Participants',
    deletedItem: 'Élément supprimé',
    deletedByLabel: 'Supprimé par',
    deletedRange: 'Période de suppression',
    addressContain: "L'adresse contient",
    coverPhoto: 'Photo de couverture',
    description: 'Description',
    sport: 'Sport',
    sportTypeLabel: 'Type de sport',
    dateTime: 'Date et heure',
    duration: 'Durée',
    level: 'Niveau',
    visibility: 'Visibilité',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    weekly: 'Hebdomadaire',
    close: 'Fermer',
    showLess: 'Voir moins',
    loadingGroups: 'Chargement des groupes…',
    loadingUsers: 'Chargement des utilisateurs…',
    loading: 'Chargement…',
    noGroups: 'Aucun groupe supprimé',
    noUsers: 'Aucun utilisateur supprimé',
    noActivities: 'Aucune activité supprimée',
    noGroupsMatch: 'Aucun groupe ne correspond à la recherche ou aux filtres',
    noUsersMatch: 'Aucun utilisateur ne correspond à la recherche ou aux filtres',
    noActivitiesMatch: 'Aucune activité ne correspond à la recherche ou aux filtres',
    noParticipants: 'Pas encore de participant sur cette activité',
  },
  report: {
    reliability: 'Construit sur les {{share}} % de profils dont ce champ est renseign\u00e9',
    crossLink: 'Croiser le sport avec la ville et la dur\u00e9e',
    title: 'Analyse des utilisateurs',
    trendsTitle: 'Tendances de participation par sport',
    trendsNote: 'Participants par mois et par sport',
    ageTitle: 'Répartition par âge',
    ageNote: "Utilisateurs par tranche d'âge",
    genderTitle: 'Répartition par genre',
    genderNote: 'Utilisateurs par genre',
    levelTitle: 'Répartition par niveau',
    levelNote: 'Participants par niveau de pratique',
    sportTitle: 'Répartition par sport',
    sportNote: 'Part des utilisateurs par sport',
    activityTitle: "Statut d'activité des utilisateurs",
    activityNote: 'Selon la création ou la participation à une activité',
    totalUsers: 'Utilisateurs au total',
    allAccounts: 'Tous les comptes inscrits',
    averageAge: 'Âge moyen',
    yearsOld: 'ans',
    eventsInPeriod: 'Activités sur la période',
    noSports: 'Aucun sport ne correspond',
    period: 'Période',
    sports: 'Sports',
    total: 'Total',
    filters: 'Filtres',
    allSports: 'Tous les sports',
    apply: 'Appliquer',
    resetAll: 'Tout réinitialiser',
    mostPopular: 'Sport le plus populaire',
    searchSports: 'Chercher un sport',
    gender: 'Genre',
    level: 'Niveau',
    levelAll: 'Tous niveaux',
    level1: 'Niveau 1 · débutant',
    level2: 'Niveau 2 · intermédiaire',
    level3: 'Niveau 3 · avancé',
    levelNoteScale: "Chaque cran vaut un tiers de l'échelle propre du sport (6, 10 ou 12 niveaux).",
    activeUsers: 'Utilisateurs actifs',
    inactiveUsers: 'Utilisateurs inactifs',
    participantsWord: 'Participants',
    participantsWordLower: 'participants',
    usersWord: 'Utilisateurs',
    eventsWord: 'activités',
    tooltipNoUsers: 'aucun utilisateur',
    eventsCreatedLegend: 'Activités créées • Participants ayant rejoint',
    sportsWord: 'sports',
    city: 'Ville',
    cityPlaceholder: 'Ville, orthographes repliées — Entrée pour appliquer',
    cityClear: 'Effacer',
    genderAll: 'Tout le monde',
    genderMale: 'Hommes',
    genderFemale: 'Femmes',
    genderNote2: "Filtre les répartitions démographiques ; les comptes d'activités restent tous genres.",
    preset30d: '30 derniers jours',
    preset90d: '90 derniers jours',
    preset6m: '6 derniers mois',
    preset12m: '12 derniers mois',
    vsLastYear: 'vs la même période un an plus tôt',
    participantsVsLastYear: 'de participants vs un an plus tôt',
    notFilled: '{{count}} non renseignés ({{share}} % de la base globale), sortis du graphique',
  },
  journeyFunnel: {
    title: 'Parcours',
    subtitle: 'Du profil complété à la première participation, et où les nouveaux membres décrochent',
    cityFilterLabel: 'Parcours par ville',
    cityFilterAll: 'Tous',
    cityFilterNote: 'Segmente le parcours selon la ville du profil du membre.',
    loadFailed: 'Impossible de charger le tunnel du parcours',
    verdict: '{{joined}} des {{base}} membres ayant complété leur profil ont rejoint une activité ({{share}} %).',
    storyTitle: 'Le parcours, marche par marche',
    keptFromPrevious: "{{share}} % de l'étape précédente",
    lostHere: 'ici',
    note:
      "Chaque marche compte des membres distincts et s'emboîte dans la précédente, donc un taux ne dépasse jamais " +
      "100 %. Les membres inscrits avant la sortie de l'émission mobile sont hors de ce tunnel par conception : il " +
      "affiche zéro tant que les événements ne s'accumulent pas.",
    stage: {
      profile_completed: 'Profil complété',
      search_performed: 'A fait une recherche',
      activity_viewed: 'A vu une activité',
      joined: 'A rejoint une activité',
    },
  },
  matching: {
    title: 'Appariement',
    subtitle: "De l'inscription \u00e0 la vraie rencontre, et o\u00f9 les gens se perdent",
    cityFilterLabel: 'Funnel par ville',
    cityFilterAll: 'Tous',
    cityFilterNote: "Segmente le funnel par la ville du membre. Les cartes d'action ci-dessous restent globales.",
    stage: {
      signedUp: 'Inscrits',
      matchable: 'Profil appariable',
      involved: 'Impliqu\u00e9s dans une activit\u00e9',
      joinedSomeoneElse: "Ont rejoint quelqu'un d'autre",
      paid: 'Ont pay\u00e9',
    },
    verdict:
      "La plus grosse perte est de {{lost}} personnes, \u00e0 l'\u00e9tape \u00ab {{stage}} \u00bb. C'est l\u00e0 qu'agir rapporte.",
    keptFromPrevious: "{{share}}% de l'\u00e9tape pr\u00e9c\u00e9dente",
    note:
      'Chaque \u00e9tape compte des personnes distinctes : la chute entre deux \u00e9tapes est une vraie perte, pas un ' +
      'double comptage. Appariable signifie un sport d\u00e9clar\u00e9 et une position, les deux choses dont ' +
      "l'appariement a besoin. \u00ab Ont rejoint quelqu'un d'autre \u00bb exclut les organisateurs qui se comptent " +
      'eux-m\u00eames, ce que font les donn\u00e9es brutes.',
    seeActivities: "Voir les activit\u00e9s o\u00f9 l'organisateur est encore seul",
    loadFailed: "Impossible de charger l'entonnoir",
    verdictJoined: "{{joined}} personnes ont réellement rejoint quelqu'un, sur {{base}} inscrits.",
    verdictWaiting:
      "{{waiting}} personnes sont prêtes à jouer et n'ont rien à rejoindre : c'est là que chaque euro marketing rapporte le plus.",
    actionsTitle: 'Les 3 actions qui débloquent le plus',
    action1Title: "Relancer les {{count}} profils à qui il ne manque QU'UN sport",
    action1Body: "Ils ont tout rempli sauf le sport. Un seul champ les sépare d'être appariables.",
    action1Push:
      'Environ {{share}} % de la base est joignable en push : une campagne « choisis ton sport » à coût zéro.',
    action1Gain: 'Gain potentiel : +{{gain}} % de profils appariables',
    action1Cta: 'Voir la qualité des profils',
    action2Title: "Créer de l'offre là où {{waiting}} appariables attendent",
    action2Body: 'La marche la plus coûteuse du funnel : des gens prêts, rien à rejoindre près de chez eux.',
    action2Candidates:
      "{{candidates}} chefs de groupe n'ont jamais rien organisé ; mener un groupe multiplie par 19,7 la probabilité d'organiser, ce sont tes meilleurs candidats.",
    action2Cta: 'Voir les villes à activer',
    action2Cta2: 'Les organisateurs potentiels',
    action3Title: "Sauver les {{count}} activités où l'organisateur est encore seul",
    action3Body:
      "Chacune sauvée fait franchir la marche à plusieurs personnes d'un coup : le levier le plus rapide du funnel.",
    action3Cta: 'Voir les activités en difficulté',
    storyTitle: 'Le parcours, marche par marche',
    lossHere: 'ici',
    lossLink: '→ traiter',
    loss: {
      gap1: 'profils incomplets, surtout le sport et la position',
      gap2: 'prêts, mais rien à rejoindre près de chez eux',
      gap3: "ont créé, personne n'est venu",
      gap4: 'le paiement ne concerne que les activités payantes',
    },
  },
  partners: {
    loading: 'Chargement…',
    failed: "Les marques n'ont pas pu être chargées.",
    premium: 'PREMIUM',
    save: 'Enregistrer',
    cancel: 'Annuler',
    close: 'Fermer',
    uploading: "Envoi de l'image…",
    uploadFailed: "L'image n'a pas pu être envoyée.",
    brands: {
      title: 'Marques partenaires',
      subtitle: 'Les marques qui peuvent porter un événement partenaire. Réservé aux admins.',
      none: "Aucune marque pour l'instant.",
      add: '+ Ajouter une marque',
      newEvent: 'Nouvel événement partenaire',
      brand: 'Marque',
      color: 'Couleur',
      link: 'Lien',
      events: 'Événements',
      next: 'Prochain',
      status: 'Statut',
      active: 'Active',
      archived: 'Archivée',
      edit: 'Modifier',
      addTitle: 'Nouvelle marque',
      editTitle: 'Modifier la marque',
      name: 'Nom',
      logo: 'Logo',
      logoHint: 'Carré, 512 × 512 px minimum, PNG ou SVG',
      choose: 'Choisir',
      replace: 'Remplacer',
      removeLogo: 'Retirer',
      colorHint: "Sert de fond au logo quand il n'a pas d'image.",
      siteLink: 'Lien vers le site',
      account: 'Compte organisateur de la marque (email)',
      accountHint:
        "Seulement pour les événements que la marque organise seule. Le compte doit déjà exister dans l'app.",
      preview: "Aperçu dans l'app",
      archive: 'Archiver',
      unarchive: 'Réactiver',
    },
    event: {
      title: 'Nouvel événement partenaire',
      subtitle: 'Visible dans le feed de tout le monde, ouvert aux seuls abonnés Premium. Toujours gratuit.',
      toBrands: 'Marques partenaires',
      created: "L'événement est publié. Il apparaît dans le feed de l'app.",
      who: "Qui l'organise",
      brand: 'Marque',
      chooseBrand: 'Choisir une marque',
      organiser: 'Organisateur affiché',
      bySmatchy: 'Smatchy, avec la marque',
      bySmatchyHint: 'Le compte Smatchy organise. Les inscrits écrivent à Smatchy.',
      byBrand: 'La marque seule',
      byBrandHint: 'La marque a son propre compte organisateur, rattaché à cette fiche.',
      outing: 'La sortie',
      name: 'Titre',
      nameHint: "Peut être le nom d'un athlète : il reste visible pour les non-abonnés.",
      sport: 'Sport',
      chooseSport: 'Choisir un sport',
      kind: 'Type',
      chooseKind: 'Choisir un type',
      day: 'Date',
      hour: 'Heure (Paris)',
      hiddenHour: 'Masquée aux non-abonnés',
      place: 'Adresse',
      placeHint: "Tape une adresse, un lieu ou une ville, en France ou à l'étranger",
      placeNoTown: 'Google ne donne pas la ville de ce lieu : choisis une adresse proche.',
      city: 'Ville montrée aux non-abonnés : {{city}}',
      exactPlace: 'Lieu exact',
      hiddenPlace: 'Masqué aux non-abonnés',
      duration: 'Durée',
      places: 'Places',
      photo: 'Photo',
      description: 'Description',
      hiddenDescription: 'Masquée aux non-abonnés',
      publish: 'Publier',
      preview: 'Aperçu dans le feed',
      asSubscriber: 'Abonné Premium',
      asOther: 'Pas abonné',
      free: 'Gratuit avec Premium',
      discover: 'Découvrir Premium',
      previewHint:
        'Vu par un non-abonné : titre, marque, date, ville et type visibles, heure et lieu exact masqués, bouton « Découvrir Premium ».',
    },
    events: {
      title: 'Événements partenaires',
      subtitle: 'Les événements partenaires écrits au back-office. Réservé aux admins.',
      upcoming: 'À venir',
      past: 'Passés et annulés',
      allBrands: 'Toutes les marques',
      failed: "Les événements partenaires n'ont pas pu être chargés.",
      none: 'Aucun événement partenaire ici.',
      event: 'Événement',
      when: 'Date',
      where: 'Ville',
      registered: 'Inscrits',
      status: { upcoming: 'À venir', past: 'Passé', cancelled: 'Annulé' },
      remove: 'Retirer',
      editTitle: "Modifier l'événement partenaire",
      fixed: 'La marque, le sport et la ville ne changent pas : pour en changer, écris un nouvel événement.',
      belowRegistered: '{{n}} personnes sont déjà inscrites : il ne peut pas y avoir moins de places.',
      toldOfChange:
        "Les {{n}} inscrits sont prévenus si la date, l'heure ou la durée change. Un nouveau lieu exact seul ne leur est pas envoyé.",
      removeTitle: 'Annuler « {{title}} » ?',
      cancel: 'Annuler la sortie',
      cancelHint: "Elle n'aura plus lieu. Les {{n}} inscrits sont prévenus.",
    },
    refused: {
      brand_account_not_found: "Aucun compte de l'app n'a cet email.",
      brand_archived: "Cette marque est archivée : réactive-la d'abord.",
      brand_has_no_account:
        "Cette marque n'a pas de compte organisateur : ajoute son email sur la fiche, ou choisis Smatchy.",
      brand_not_found: "Cette marque n'existe plus.",
      partner_event_duration_invalid: "Cette durée n'est pas possible.",
      partner_event_in_the_past: 'Cette date est déjà passée.',
      session_kind_not_in_sport: "Ce type n'appartient pas à ce sport.",
      smatchy_account_missing: "Le compte Smatchy n'existe pas encore sur ce serveur.",
      event_not_found: "Cette sortie n'existe plus, ou elle est déjà annulée.",
      partner_event_cancel_failed: "La sortie n'a pas pu être annulée. Réessaie, ou regarde-la dans les activités.",
      partner_event_not_found: "Cette sortie n'est pas un événement partenaire.",
      partner_event_places_below_registered: "Il ne peut pas y avoir moins de places que d'inscrits.",
      unknown: "L'enregistrement n'a pas abouti.",
    },
  },
  gamificationSettings: {
    title: 'Réglages de la gamification',
    subtitle: 'Pris en compte sans nouvelle version de l’API.',
    loading: 'Chargement…',
    failed: 'Les réglages n’ont pas pu être chargés.',
    presence: {
      title: 'Présence à une sortie',
      what: 'La distance maximale entre la position d’un participant et le lieu de la sortie, départ ou arrivée, pour que sa présence soit vérifiée.',
      radius: 'Rayon',
      walk: 'Environ {{minutes}} min à pied.',
      bounds: 'Entre {{min}} et {{max}} m. Par défaut : {{fallback}} m.',
      counts: 'Seul le parrainage exige une présence vérifiée. La série, les badges et l’XP comptent sans elle.',
      when: 'Un changement s’applique aux présences décidées après l’enregistrement. Une présence déjà vérifiée le reste.',
    },
    save: 'Enregistrer',
    saved: 'Enregistré.',
    refused: 'Le réglage n’a pas pu être enregistré.',
    changed: 'Modifié le {{day}} par {{who}}.',
    default: 'Valeur par défaut, jamais modifiée.',
  },
  finals: {
    title: 'Compétitions',
    subtitle:
      'Une finale saisie ici devient un badge dans l’app : une sortie de son sport pendant les sept jours qui finissent le jour de la finale. Noms génériques, jamais de marque.',
    loading: 'Chargement…',
    failed: 'Impossible de charger les finales.',
    none: 'Aucune finale saisie pour l’instant.',
    name: 'Nom affiché',
    sport: 'Sport',
    week: 'Semaine comptée',
    state: 'État',
    holders: 'Gagné par',
    states: { ahead: 'À venir', running: 'Cette semaine', past: 'Passée' },
    edit: 'Modifier',
    delete: 'Supprimer',
    locked: 'Verrouillée',
    lockedHint: 'Quelqu’un a gagné ce badge : la finale ne se modifie plus et ne se supprime plus.',
    areYouSure: 'Supprimer « {{name}} » ?',
    formAdd: 'Ajouter une finale',
    formEdit: 'Modifier la finale',
    nameFr: 'Nom en français',
    nameEn: 'Nom en anglais',
    chooseSport: 'Choisir un sport',
    finalDay: 'Jour de la finale',
    rule: 'La semaine comptée, ce sont les sept jours qui finissent le jour de la finale.',
    weekHint: 'Semaine comptée : du {{from}} au {{to}}, à l’heure de chaque membre.',
    save: 'Enregistrer',
    cancel: 'Annuler',
    refused: {
      final_day_past: 'Cette finale est déjà passée : il n’y a pas de rattrapage.',
      final_already_earned: 'Quelqu’un a gagné ce badge : la finale ne se modifie plus et ne se supprime plus.',
      sport_not_found: 'Ce sport n’existe plus.',
      unknown: 'Impossible d’enregistrer la finale.',
    },
  },
  collusionCases: {
    title: 'Duos mis de côté',
    subtitle:
      'Deux membres dont les sorties ressemblent à de l’XP gagné l’un par l’autre. Tant qu’un cas attend, l’XP des sorties qu’ils ont partagées ne compte pour aucune ligue.',
    loading: 'Chargement des cas...',
    failed: 'Impossible de charger les cas. Réessayez dans un instant.',
    none: 'Rien ici.',
    people: 'Membres',
    signalsTitle: 'Pourquoi',
    outings: 'Sorties mises de côté',
    when: 'Repéré le',
    decision: 'Décision',
    dismiss: 'Écarter',
    cancelXp: 'Annuler l’XP',
    confirm: 'Confirmer',
    dismissHelp: 'Écarter : tout allait bien. L’XP revient et le duo n’est plus examiné.',
    cancelXpHelp:
      'Annuler l’XP : l’XP de ces sorties est mise de côté pour de bon, avec tous les bonus qu’elles ont rapportés : le +20 de la semaine, le +25 de fiabilité et le bonus duo, les semaines duo suivantes étant recalculées. La série et les badges déjà gagnés ne bougent pas.',
    confirmHelp:
      'Confirmer : l’XP est mise de côté pour de bon avec tous les bonus des sorties (le +20 de la semaine, le +25 de fiabilité, le bonus duo, les semaines duo suivantes recalculées), et les sorties sont retirées de la série et des compteurs, semaines closes comprises. Un badge qu’elles étaient seules à avoir fait gagner est retiré avec son XP.',
    nobodyIsTold: 'Aucun des deux membres n’est prévenu de ce que vous décidez ici.',
    areYouSure: {
      cancel_xp: 'Mettre de côté pour de bon l’XP et les bonus de ces sorties ? Impossible de revenir en arrière.',
      confirm:
        'Retirer ces sorties aux deux membres : XP, bonus, série, compteurs et badges qu’elles étaient seules à avoir fait gagner ? Impossible de revenir en arrière.',
    },
    filters: {
      open: 'En attente',
      dismissed: 'Écartés',
      confirmed: 'Confirmés',
      cancelled: 'XP annulée',
      all: 'Tout',
    },
    statuses: {
      open: 'En attente',
      dismissed: 'Écarté',
      confirmed: 'Confirmé',
      cancelled: 'XP annulée',
    },
    signals: {
      always_together: 'Presque toutes les sorties de chacun incluent l’autre',
      tiny_and_frequent: 'Petites sorties, très souvent, sans position vérifiée',
      recent_accounts: 'Les deux comptes sont récents',
      same_place_same_time: 'Même lieu, même jour et même heure, semaine après semaine',
    },
  },
  groupReview: {
    title: 'Validation des groupes',
    subtitle:
      'Sécurité de marque : chaque nouveau groupe arrive ici. Il reste en ligne pendant que tu regardes ; le refuser le retire.',
    waiting: '{{count}} groupe(s) en attente de décision',
    filters: {
      pending: 'En attente',
      info_requested: 'Infos demandées',
      approved: 'Approuvés',
      refused: 'Refusés',
      all: 'Tous',
    },
    statuses: {
      pending: 'En attente',
      approved: 'Approuvé',
      info_requested: 'Infos demandées',
      refused: 'Refusé',
    },
    flags: {
      coarse: 'Mot grossier',
      brand: 'Nom de marque',
    },
    by: 'par',
    sportUnknown: 'Sport non renseigné',
    descriptionEmpty: 'Aucune description',
    reasonGiven: 'Motif indiqué :',
    reasonLabel: "Motif (montré au créateur en cas de refus ou de demande d'infos)",
    reasonPlaceholder: "Pourquoi c'est refusé, ou ce qui manque…",
    approve: 'Approuver',
    requestInfo: 'Demander des infos',
    refuse: 'Refuser',
    loading: 'Chargement…',
    failed: 'Impossible de charger la file de validation.',
    none: 'Rien à valider ici.',
    footnote:
      'Un groupe reste visible et rejoignable pendant la revue. Seul un refus le retire, et le créateur voit le motif. Approuver efface le motif précédent.',
  },

  memberReports: {
    messageQuoted: 'Message signalé, tel qu’il était',
    reviewQuoted: 'Avis signalé, tel qu’il était',
    reviewHandledEffect:
      'Marquer « traité » retire le texte de cet avis du profil, pour tout le monde. Ses étoiles restent dans la note.',
    title: 'Modération',
    subtitle: "Les signalements de membres, groupes et activités, en attente d'un regard.",
    waiting: '{{count}} en attente',
    loading: 'Chargement des signalements...',
    failed: "Les signalements n'ont pas pu être chargés. Réessaie dans un instant.",
    none: 'Rien ici.',
    type: 'Type',
    reported: 'Signalé',
    by: 'Par',
    reason: 'Motif',
    when: 'Reçu le',
    decision: 'Décision',
    markHandled: 'Traité',
    markDismissed: 'Écarté',
    nobodyIsTold:
      "Personne n'est informé de ta décision. Ce qui a été signalé, membre, groupe ou activité, ne l'apprend jamais, et la personne qui a signalé sait seulement que son signalement est arrivé.",
    types: {
      member: 'Membre',
      group: 'Groupe',
      activity: 'Activité',
    },
    typeFilters: {
      all: 'Tous types',
      member: 'Membres',
      group: 'Groupes',
      activity: 'Activités',
    },
    filters: {
      pending: 'En attente',
      handled: 'Traités',
      dismissed: 'Écartés',
      all: 'Tout',
    },
    statuses: {
      pending: 'En attente',
      handled: 'Traité',
      dismissed: 'Écarté',
    },
    reasons: {
      hate: 'Propos haineux ou discriminatoires',
      harassment: 'Harcèlement ou intimidation',
      sexual: 'Contenu à caractère sexuel',
      impersonation: "Usurpation d'identité, faux profil",
      scam: 'Arnaque, spam ou publicité',
      danger: 'Comportement dangereux pendant une sortie',
      minor: 'Compte qui semble appartenir à un mineur',
      other: 'Autre',
    },
  },
  blockedPayouts: {
    title: 'Argent qui ne peut pas être versé',
    subtitle:
      "Organisateurs dont les gains ne peuvent pas être virés par Stripe, séquestre compris. Jusqu'à cet écran, la seule trace était une erreur nocturne.",
    loading: 'Chargement…',
    failed: "La liste n'a pas pu être chargée.",
    none: "Personne n'est bloqué. Tous ceux qui détiennent de l'argent peuvent le recevoir.",
    total: '{{amount}} € détenus et non versables',
    unfinished: 'Compte créé, jamais terminé',
    unfinishedHelp:
      'Ces personnes peuvent le terminer elles-mêmes, et elles ont été prévenues une fois depuis le 20 septembre 2026.',
    neverStarted: 'Aucun compte Stripe',
    neverStartedHelp: "Ces personnes n'ont jamais commencé. Une conversation plutôt qu'un rappel.",
    organizer: 'Organisateur',
    amount: 'Détenu',
    account: 'Compte Stripe',
    told: 'Prévenu le',
    notTold: 'Jamais prévenu',
  },
  needingHelp: {
    yourMove: 'Ton action :',
    openManagement: 'ouvrir la gestion des activités',
    openCities: 'ouvrir les villes à activer',
    daysShort: 'J−{{days}}',
    recurring: 'récurrente',
    nearby: 'À {{radius}} km',
    inSport: 'Dans ce sport',
    empty: "Toutes les activités à venir ont au moins un participant. Rien à sauver aujourd'hui.",
    case: {
      audienceReady: {
        name: 'Public présent, pas prévenu',
        why: "Des sportifs du bon sport vivent à portée. L'activité n'a simplement pas trouvé son monde : le sauvetage le moins cher de la page.",
        action:
          "Demander à l'organisateur de la partager, et envisager une relance ciblée aux sportifs concernés à proximité.",
        reading:
          "{{inSport}} sportifs de ce sport à portée n'ont pas rejoint, et l'activité démarre dans {{days}} jour(s). Les prévenir est le geste le plus rentable de cette page.",
      },
      fewInSport: {
        name: 'Public rare',
        why: 'Une poignée de personnes déclarent ce sport à proximité. Élargir la cible ou reclasser le sport se défendent tous les deux.',
        action: "Vérifier que le titre et le sport disent ce qui s'y passe vraiment, et élargir la formulation si oui.",
        reading:
          '{{nearby}} personnes autour, seulement {{inSport}} dans ce sport. Un titre plus large ou un sport voisin pourrait multiplier le public avant le jour J.',
      },
      invisible: {
        name: 'Invisible pour son public',
        why: 'Il y a du monde autour, personne ne déclare le sport sous lequel elle est classée. Un problème de fiche, pas de demande.',
        action: "Corriger le sport ou le titre avec l'organisateur pour que les bonnes personnes la voient.",
        reading:
          "{{nearby}} personnes autour et 0 dans ce sport : l'activité est probablement classée sous le mauvais sport, ou son sport n'existe pas ici. Corriger la fiche.",
      },
      desert: {
        name: 'Zone déserte',
        why: "Personne à portée, quel que soit le sport. Un problème d'acquisition, pas de mise en relation.",
        action: "Noter la zone comme cible d'acquisition et prévenir l'organisateur du contexte.",
        reading:
          "Seulement {{nearby}} personnes à portée : l'app ne peut pas remplir celle-ci. Elle vivra par le bouche-à-oreille de l'organisateur ; la zone est une cible d'acquisition.",
      },
      unknownLocation: {
        name: 'Sans position exploitable',
        why: "L'activité n'a pas de coordonnées, rien ne peut être mesuré autour d'elle.",
        action: 'Faire corriger le lieu ; tout diagnostic en découle.',
        reading: "Pas de position sur cette activité : corriger le lieu d'abord, tout le reste en découle.",
      },
    },
    title: 'Activités en difficulté',
    subtitle: "Les activités à venir où l'organisateur est encore seul, et pourquoi",
    total: 'Activités',
    distinctOwners: 'Organisateurs à contacter',
    activity: 'Activité',
    place: 'Lieu',
    daysUntilStart: 'Jours restants',
    nearbyUsers: 'Personnes à proximité',
    nearbySameSport: 'Dans ce sport',
    diagnosisColumn: 'Lecture',
    recurringHint: "Une occurrence d'une série répétée. Plusieurs lignes peuvent être le même problème.",
    diagnosis: {
      unknownLocation: 'Sans lieu',
      noOneAround: 'Personne à proximité',
      wrongSport: 'Personne dans ce sport',
      audienceExists: 'Le public est là',
    },
    note:
      'Deux comptes par activité, tous deux dans un rayon de {{radius}} km : tout le monde, et ceux qui ont ' +
      'déclaré le sport sous lequel elle est classée. Du monde autour mais personne dans le sport signifie que ' +
      "l'activité est invisible pour ceux qui la voudraient, le plus souvent parce que le sport sous lequel " +
      "elle est classée n'est pas vraiment le sien. Personne autour du tout est une question de nombre " +
      "d'inscrits sur place, pas de mise en relation.",
    none: 'Aucune activité à venir ne manque de monde',
    loadFailed: 'Impossible de charger les activités',
  },
  fillRate: {
    title: 'Remplissage des activités',
    subtitle: 'Combien de places proposées aux autres sont réellement prises',
    activities: 'Activités',
    neverJoined: 'Personne n’a rejoint',
    averageAttendees: 'Personnes par activité',
    adjustedFillRate: 'Places pourvues',
    sport: 'Sport',
    reference: 'Capacité du sport',
    declared: 'Capacité saisie',
    note:
      'Le taux compte les places proposées aux autres : une activité où l’organisateur est resté seul vaut zéro, ' +
      'quel que soit le sport. La capacité vient de la définition de chaque sport et non de ce que les organisateurs ' +
      'ont saisi, car la moitié des activités porte la valeur par défaut du formulaire, 21.',
    noReference: 'Pas de définition de sport',
    scope: 'Quelles activités',
    vsLastMonth: 'vs le mois précédent, mois pleins',
    boardTitle: 'Évolution mois par mois',
    boardNote:
      'Douze mois pleins, chaque taux recalculé sur les comptes bruts du mois. Chaque courbe vit sur sa propre échelle ; le point marque le dernier mois plein.',
    board: {
      activities: 'Activités tenues',
      neverJoined: 'Part jamais rejointe',
      partners: 'Partenaires par activité',
      declared: 'Remplissage déclaré',
    },
    scopeAll: 'Toutes',
    scopePast: 'Passées',
    scopeUpcoming: 'À venir',
    upcomingNote:
      "Les activités à venir n'ont pas encore eu lieu, donc une part élevée de « personne n'a rejoint » est " +
      "ici un délai normal, pas un échec. C'est sur les activités passées que le chiffre veut dire quelque chose.",
    loadFailed: 'Impossible de charger le remplissage',
  },
  userActivity: {
    title: 'Activité des utilisateurs',
    subtitle: 'Qui fait réellement quelque chose, au-delà de qui existe simplement',
    loadFailed: "Les compteurs n'ont pas pu être chargés. Le reste de la page reste affiché.",
    registered: 'Profils inscrits',
    engaged: 'Actifs sur les {{days}} derniers jours',
    engagedShare: 'de la base',
    inactiveShare: '{{share}}% sans action sur la période',
    averagePerActive: '≈ {{count}} activités par actif',
    shareOfAll: '{{share}}% de toutes les activités',
    perGroup: '≈ {{count}} activités par groupe',
    activesGrowth: 'vs la période précédente',
    creationsGrowth: 'de créations vs le mois précédent, mois pleins',
    heldGrowth: "d'activités tenues vs le mois précédent, mois pleins",
    groupsGrowth: 'de groupes créés vs le mois précédent, mois pleins',
    signupsGrowth: "d'inscriptions, dernier mois plein vs le précédent",
    upcoming: 'Activités à venir',
    past: 'Activités passées',
    groups: 'Groupes',
    engagedTitle: 'Ceux qui agissent',
    engagedNote:
      'Les membres ayant créé ou rejoint au moins une activité sur la période, les plus actifs en premier. Une ligne ouvre la fiche du membre.',
    period: 'Période',
    periodDays: '{{days}} jours',
    member: 'Membre',
    activityCount: 'Activités',
    lastActivity: 'Dernière activité',
    empty: "Personne n'a agi sur cette période.",
  },
  synthesis: {
    paymentAlerts: {
      title: 'Alertes paiements',
      allQuiet:
        "Rien n'appelle d'action : aucun retrait en attente, chaque solde positif a un compte bancaire v\u00e9rifi\u00e9, aucun ne dort.",
      neverUsed:
        "\u00c0 noter : aucune demande de retrait n'a jamais \u00e9t\u00e9 faite, donc ce z\u00e9ro signifie \u00ab jamais test\u00e9 \u00bb, pas \u00ab en bonne sant\u00e9 \u00bb.",
      pendingPayouts: '{{count}} demande(s) de retrait en attente, la plus ancienne depuis {{days}} jour(s)',
      withoutBank:
        '{{count}} organisateur(s) d\u00e9tiennent {{euros}} \u20ac sans compte bancaire v\u00e9rifi\u00e9 : impossible \u00e0 verser',
      stale: '{{count}} solde(s) positif(s) sans mouvement depuis plus de quatre-vingt-dix jours',
    },
    title: 'Synthèse',
    subtitle: 'Est-ce que ça marche, où ça casse, à qui parler, quoi faire',
    vsLastMonth: 'vs le mois précédent, mois pleins',
    users: 'Profils inscrits',
    activities: 'Activités créées',
    neverJoined: 'des activités sans participants',
    dormant: 'des profils sans activité',
    funnelTitle: 'De l’inscription à la rencontre réelle',
    funnelNote:
      'Chaque étape compte des personnes distinctes, elles sont donc comparables. Le chiffre de droite est la ' +
      'part de l’étape précédente, et c’est là que la perte se voit : une étape peut paraître faible ' +
      'simplement parce que tout ce qui la précède l’est.',
    worstStep: 'L’étape qui perd le plus de monde :',
    stage: {
      signedUp: 'Inscrits',
      matchable: 'Profil appariable',
      involved: 'Ont créé ou rejoint',
      joinedSomeoneElse: 'Ont rejoint quelqu’un',
      paid: 'Ont payé',
    },
    sportsTitle: 'Où l’appariement échoue le plus',
    sportsNote: 'Sports classés par part d’activités que personne n’a jamais rejointes, les pires en premier.',
    seeFillRate: 'Voir tous les sports',
    leverTitle: 'Le levier le plus court',
    leverNote:
      'Des personnes qui mènent un groupe sans avoir jamais créé une seule activité. Mener un groupe multiplie ' +
      'par 19,7 la probabilité d’organiser, un profil complet ne la multiplie que par 1,5.',
    leverCount: 'responsables de groupe n’ont jamais créé d’activité',
    seeSegments: 'Voir la liste',
    seeTodo: 'Ce qu’il faut en faire',
    loadFailed: 'Certains chiffres n’ont pas pu être chargés',
  },
  segments: {
    export: 'Exporter',
    exporting: 'Export\u2026',
    title: 'Segments utilisateurs',
    subtitle: 'La base répartie selon ce que les gens ont réellement fait',
    breakdown: 'Répartition',
    note:
      'L’ancienneté se mesure sur la création ou la participation à une activité, jamais sur la connexion : ' +
      'ce champ n’est enregistré que depuis juillet 2026, son absence ne dit donc rien. Ceux qui n’ont jamais ' +
      'agi sont séparés de ceux qui se sont arrêtés, car faire revenir et faire démarrer ne se disent pas pareil.',
    exportFailed: "Le téléchargement de l'export a échoué. L'environnement ne le sert peut-être pas encore.",
    totalShort: 'profils comptés',
    total: 'Profils comptés :',
    candidatesTitle: 'Les plus susceptibles de se mettre à organiser',
    candidatesNote:
      'Des personnes qui mènent un groupe sans avoir jamais créé une seule activité. Mener un groupe multiplie ' +
      'par 19,7 la probabilité d’organiser, un profil complet ne la multiplie que par 1,5.',
    outOf: 'sur',
    groupLeaders: 'responsables de groupe n’ont jamais créé d’activité',
    minGroups: 'Groupes menés, au moins',
    groupsLed: 'Groupes menés',
    name: 'Nom',
    searchPlaceholder: 'Prénom ou nom',
    profileGone: 'Profil indisponible',
    truncated: 'Il existe plus de candidats que ceux chargés : les filtres ci-dessous ne portent que sur les chargés.',
    loadFailed: 'Impossible de charger les segments',
    key: {
      hot: 'Chauds, moins d’un mois',
      warm: 'Tièdes, 1 à 3 mois',
      cool: 'Frais, 3 à 12 mois',
      frozen: 'Gelés, plus d’un an',
      groupOnly: 'Dans un groupe seulement',
      untouched: 'N’ont jamais agi',
    },
  },
  common: {
    all: 'Tous',
    search: 'Rechercher',
    reset: 'Réinitialiser',
    filters: 'Filtres',
    noData: 'Aucune donnée',
    loading: 'Chargement',
    export: 'Exporter',
    sport: 'Sport',
    city: 'Ville',
    gender: 'Genre',
    period: 'Période',
  },
} as const;

export type BackofficeLanguage = 'en' | 'fr';

export const DICTIONARIES: Record<BackofficeLanguage, unknown> = { en: EN, fr: FR };
